import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

let cleanupFestivalLiveMap = null;
let festivalLiveMapGeneration = 0;

const initializeFestivalLiveMap = () => {
    const generation = ++festivalLiveMapGeneration;

    cleanupFestivalLiveMap?.();
    cleanupFestivalLiveMap = null;

    const eventController = new AbortController();
    const addWindowListener = (type, listener, options = {}) => {
        window.addEventListener(type, listener, {
            ...options,
            signal: eventController.signal,
        });
    };

    const requestedMeetupId =
        new URLSearchParams(
            window.location.search
        ).get('meetup');
    const requestedLandmarkId =
        new URLSearchParams(
            window.location.search
        ).get('landmark');
    const requestedCrowdSignalKey = new URLSearchParams(window.location.search).get('crowd_signal');
    const requestedLandmarkNavigation =
        new URLSearchParams(
            window.location.search
        ).get('navigate') === '1';
    const mapElement = document.querySelector(
        '[data-festival-live-map]'
    );

    if (! mapElement) {
        return;
    }

    const canvas = mapElement.querySelector(
        '[data-festival-live-map-canvas]'
    );

    const illustratedCanvas =
        mapElement.querySelector(
            '[data-festival-illustrated-map]'
        );

    const illustratedTerrain =
        mapElement.querySelector(
            '[data-festival-illustrated-map-terrain]'
        );

    const illustratedAreas =
        mapElement.querySelector(
            '[data-festival-illustrated-map-areas]'
        );

    const illustratedPaths =
        mapElement.querySelector(
            '[data-festival-illustrated-map-paths]'
        );

    const illustratedElements =
        mapElement.querySelector(
            '[data-festival-illustrated-map-elements]'
        );

    const illustratedMembers =
        mapElement.querySelector(
            '[data-festival-illustrated-map-members]'
        );

    const loadingElement = mapElement.querySelector(
        '[data-festival-live-map-loading]'
    );

    const errorElement = mapElement.querySelector(
        '[data-festival-live-map-error]'
    );

    const section = mapElement.closest(
        '[data-festival-live-map-section]'
    );

    const liveLocationMaxAccuracyMeters =
        Number.parseFloat(
            mapElement.dataset
                .liveLocationMaxAccuracyMeters
        ) || 75;

    const mapViewControls =
        section?.querySelector(
            '[data-festival-map-view-controls]'
        );

    const mapViewButtons =
        section?.querySelectorAll(
            '[data-festival-map-view]'
        ) ?? [];

    const illustratedGuideToggle =
        section?.querySelector(
            '[data-festival-illustrated-map-guide-toggle]'
        );

    const illustratedGuide =
        section?.querySelector(
            '[data-festival-illustrated-map-guide]'
        );

    const illustratedGuideClose =
        section?.querySelector(
            '[data-festival-illustrated-map-guide-close]'
        );

    const illustratedGuideItems =
        section?.querySelector(
            '[data-festival-illustrated-map-guide-items]'
        );

    const countText = section?.querySelector(
        '[data-festival-live-map-count-text]'
    );

    const radiusBadge = section?.querySelector(
        '[data-festival-live-map-radius]'
    );

    const radiusText = section?.querySelector(
        '[data-festival-live-map-radius-text]'
    );

    let liveMapGeofenceActive = false;
    let liveMapRadiusMiles = null;
    let liveMapGeofenceCircle = null;
    let viewerLiveLocationState = null;

    const showEveryoneButton = section?.querySelector(
        '[data-festival-live-map-show-everyone]'
    );

    const centerMeButton = section?.querySelector(
        '[data-festival-live-map-center-me]'
    );

    const refreshButton = section?.querySelector(
        '[data-festival-live-map-refresh]'
    );

    const startSharingButton = section?.querySelector(
        '[data-festival-live-location-start]'
    );

    const stopSharingButton = section?.querySelector(
        '[data-festival-live-location-stop]'
    );

    const sharingStatus = section?.querySelector(
        '[data-festival-live-location-status]'
    );

    const navigationSummary =
        section?.querySelector(
            '[data-festival-navigation-summary]'
        );

    const navigationTitle =
        section?.querySelector(
            '[data-festival-navigation-title]'
        );

    const navigationMeta =
        section?.querySelector(
            '[data-festival-navigation-meta]'
        );

    const navigationConfidence =
        section?.querySelector(
            '[data-festival-navigation-confidence]'
        );

    const navigationConfidenceToggle =
        section?.querySelector(
            '[data-festival-navigation-confidence-toggle]'
        );

    const navigationConfidenceDetails =
        section?.querySelector(
            '[data-festival-navigation-confidence-details]'
        );

    const clearNavigationButton =
        section?.querySelector(
            '[data-festival-navigation-clear]'
        );

    const navigationNextStep =
        section?.querySelector(
            '[data-festival-navigation-next-step]'
        );

    const navigationNextMeta =
        section?.querySelector(
            '[data-festival-navigation-next-meta]'
        );

    const navigationStepsToggle =
        section?.querySelector(
            '[data-festival-navigation-steps-toggle]'
        );

    const navigationSteps =
        section?.querySelector(
            '[data-festival-navigation-steps]'
        );

    const commandLocationButtons =
        document.querySelectorAll(
            '[data-command-location]'
        );

    const meetupMapButtons =
        document.querySelectorAll(
            '[data-festival-meetup-map-button]'
        );

    const layerToggleButtons =
        section?.querySelectorAll(
            '[data-festival-map-layer-toggle]'
        ) ?? [];

    const resetLayerPreferencesButton =
        section?.querySelector(
            '[data-festival-map-layers-reset]'
        );

    const advancedMapToggle =
        section?.querySelector(
            '[data-festival-advanced-map-toggle]'
        );

    const advancedMapLayers =
        section?.querySelector(
            '[data-festival-advanced-map-layers]'
        );

    const meetupDataElement = mapElement.querySelector(
        '[data-festival-live-map-meetups]'
    );

    let meetups = [];

    if (meetupDataElement) {
        try {
            const parsedMeetups = JSON.parse(
                meetupDataElement.textContent || '[]'
            );

            meetups = Array.isArray(parsedMeetups)
                ? parsedMeetups
                : [];
        } catch (error) {
            console.error(
                'Festival meetup map data could not be parsed.',
                error
            );
        }
    }

    const landmarkDataElement = mapElement.querySelector(
        '[data-festival-live-map-landmarks]'
    );

    let landmarks = [];

    if (landmarkDataElement) {
        try {
            const parsedLandmarks = JSON.parse(
                landmarkDataElement.textContent || '[]'
            );

            landmarks = Array.isArray(parsedLandmarks)
                ? parsedLandmarks
                : [];
        } catch (error) {
            console.error(
                'Festival venue landmark map data could not be parsed.',
                error
            );
        }
    }

    const communitySuggestionDataElement = mapElement.querySelector(
        '[data-festival-live-map-community-suggestions]'
    );

    let communitySuggestions = [];

    if (communitySuggestionDataElement) {
        try {
            const parsedCommunitySuggestions = JSON.parse(
                communitySuggestionDataElement.textContent || '[]'
            );

            communitySuggestions = Array.isArray(parsedCommunitySuggestions)
                ? parsedCommunitySuggestions
                : [];
        } catch (error) {
            console.error(
                'Festival community map suggestion data could not be parsed.',
                error
            );
        }
    }

    const crowdSignalDataElement = mapElement.querySelector(
        '[data-festival-live-map-crowd-signals]'
    );

    let crowdSignals = [];

    if (crowdSignalDataElement) {
        try {
            const parsedCrowdSignals = JSON.parse(
                crowdSignalDataElement.textContent || '[]'
            );

            crowdSignals = Array.isArray(parsedCrowdSignals)
                ? parsedCrowdSignals
                : [];
        } catch (error) {
            console.error(
                'Festival crowd map signal data could not be parsed.',
                error
            );
        }
    }

    const walkthroughDataElement = mapElement.querySelector(
        '[data-festival-live-map-walkthrough]'
    );

    let venueMapWalkthrough = null;

    if (walkthroughDataElement) {
        try {
            const parsedWalkthrough = JSON.parse(
                walkthroughDataElement.textContent || '{}'
            );

            venueMapWalkthrough = parsedWalkthrough
                && typeof parsedWalkthrough === 'object'
                ? parsedWalkthrough
                : null;
        } catch (error) {
            console.error(
                'Festival venue walk-through data could not be parsed.',
                error
            );
        }
    }

    const personalPinDataElement =
        mapElement.querySelector(
            '[data-festival-live-map-personal-pins]'
        );

    let personalPins = [];

    if (personalPinDataElement) {
        try {
            const parsedPersonalPins=JSON.parse(
                personalPinDataElement.textContent || '[]'
            );

            personalPins=Array.isArray(parsedPersonalPins)
                ? parsedPersonalPins
                : [];
        } catch (error) {
            console.error(
                'Festival private map pin data could not be parsed.',
                error
            );
        }
    }

    const zoneDataElement = mapElement.querySelector(
        '[data-festival-live-map-zones]'
    );

    let zones = [];

    if (zoneDataElement) {
        try {
            const parsedZones = JSON.parse(
                zoneDataElement.textContent || '[]'
            );

            zones = Array.isArray(parsedZones)
                ? parsedZones
                : [];
        } catch (error) {
            console.error(
                'Festival venue zone map data could not be parsed.',
                error
            );
        }
    }

    const pathDataElement =
        mapElement.querySelector(
            '[data-festival-live-map-paths]'
        );

    let paths = [];

    if (pathDataElement) {
        try {
            const parsedPaths = JSON.parse(
                pathDataElement.textContent
                || '[]'
            );

            paths = Array.isArray(
                parsedPaths
            )
                ? parsedPaths
                : [];
        } catch (error) {
            console.error(
                'Festival venue path map data could not be parsed.',
                error
            );
        }
    }

    const junctionDataElement =
        mapElement.querySelector(
            '[data-festival-live-map-path-junctions]'
        );

    let pathJunctions = [];

    if (junctionDataElement) {
        try {
            const parsedJunctions =
                JSON.parse(
                    junctionDataElement.textContent
                    || '[]'
                );

            pathJunctions =
                Array.isArray(
                    parsedJunctions
                )
                    ? parsedJunctions
                    : [];
        } catch (error) {
            console.error(
                'Festival path junction data could not be parsed.',
                error
            );
        }
    }

    const locationUpdateUrl =
        mapElement.dataset.liveLocationUpdateUrl;

    const locationClearUrl =
        mapElement.dataset.liveLocationClearUrl;

    const viewerUserId =
        mapElement.dataset.viewerUserId
        || null;

    const pendingLocationClearStorageKey =
        viewerUserId
        && locationClearUrl
            ? `festfriends:live-location-clear:${viewerUserId}:${locationClearUrl}`
            : null;

    const readPendingLocationClear = () => {
        if (! pendingLocationClearStorageKey) {
            return false;
        }

        try {
            return window.localStorage.getItem(
                pendingLocationClearStorageKey
            ) === '1';
        } catch {
            return false;
        }
    };

    const persistPendingLocationClear = (
        pending
    ) => {
        if (! pendingLocationClearStorageKey) {
            return;
        }

        try {
            if (pending) {
                window.localStorage.setItem(
                    pendingLocationClearStorageKey,
                    '1'
                );

                return;
            }

            window.localStorage.removeItem(
                pendingLocationClearStorageKey
            );
        } catch {
            // Storage can be unavailable in privacy-restricted browsers.
        }
    };

    const venueMapConfirmUrl =
        mapElement.dataset.venueMapConfirmUrl;

    const personalPinStoreUrl =
        mapElement.dataset.personalPinStoreUrl
        || null;

    const personalPinDestroyAllUrl =
        mapElement.dataset.personalPinDestroyAllUrl
        || null;

    const personalPinDuplicateCheckUrl =
        mapElement.dataset.personalPinDuplicateCheckUrl
        || null;

    const venueMapReadinessUrl =
        mapElement.dataset.venueMapReadinessUrl
        || null;

    const venueMapReadinessPanel =
        section?.querySelector(
            '[data-venue-map-readiness-panel]'
        );

    const venueMapReadinessStateLabel =
        venueMapReadinessPanel?.querySelector(
            '[data-venue-map-readiness-state-label]'
        );

    const venueMapReadinessHeadline =
        venueMapReadinessPanel?.querySelector(
            '[data-venue-map-readiness-headline]'
        );

    const venueMapReadinessDescription =
        venueMapReadinessPanel?.querySelector(
            '[data-venue-map-readiness-description]'
        );

    const venueMapReadinessOfficialTotal =
        venueMapReadinessPanel?.querySelector(
            '[data-venue-map-readiness-official-total]'
        );

    const venueMapReadinessCommunityGroups =
        venueMapReadinessPanel?.querySelector(
            '[data-venue-map-readiness-community-groups]'
        );

    const venueMapReadinessPrivateCount =
        venueMapReadinessPanel?.querySelector(
            '[data-venue-map-readiness-private-count]'
        );

    const personalPinComposer =
        mapElement.querySelector(
            '[data-personal-pin-composer]'
        );

    const personalPinDropToggle =
        section?.querySelector(
            '[data-personal-pin-drop-toggle]'
        );

    const personalPinComposerClose =
        personalPinComposer?.querySelector(
            '[data-personal-pin-composer-close]'
        );

    const personalPinTypeInput =
        personalPinComposer?.querySelector(
            '[data-personal-pin-type]'
        );

    const personalPinLabelInput =
        personalPinComposer?.querySelector(
            '[data-personal-pin-label]'
        );

    const personalPinNoteInput =
        personalPinComposer?.querySelector(
            '[data-personal-pin-note]'
        );

    const personalPinPlacementStart =
        personalPinComposer?.querySelector(
            '[data-personal-pin-placement-start]'
        );

    const personalPinCurrentLocation =
        personalPinComposer?.querySelector(
            '[data-personal-pin-current-location]'
        );

    const personalPinPlacementStatus =
        personalPinComposer?.querySelector(
            '[data-personal-pin-placement-status]'
        );

    const personalPinError =
        personalPinComposer?.querySelector(
            '[data-personal-pin-error]'
        );

    const personalPinCount =
        personalPinComposer?.querySelector(
            '[data-personal-pin-count]'
        );

    const personalPinRemaining =
        personalPinComposer?.querySelector(
            '[data-personal-pin-remaining]'
        );

    const personalPinClearAll =
        personalPinComposer?.querySelector(
            '[data-personal-pin-clear-all]'
        );

    const personalPinPresetButtons =
        personalPinComposer?.querySelectorAll(
            '[data-personal-pin-preset]'
        ) ?? [];

    const personalPinManagerFilter =
        personalPinComposer?.querySelector(
            '[data-personal-pin-manager-filter]'
        );

    const personalPinManagerList =
        personalPinComposer?.querySelector(
            '[data-personal-pin-manager-list]'
        );

    const personalPinManagerEmpty =
        personalPinComposer?.querySelector(
            '[data-personal-pin-manager-empty]'
        );

    const personalPinPromotionPanel =
        mapElement.querySelector(
            '[data-personal-pin-promotion-panel]'
        );

    const personalPinPromotionClose =
        personalPinPromotionPanel?.querySelector(
            '[data-personal-pin-promotion-close]'
        );

    const personalPinPromotionCancel =
        personalPinPromotionPanel?.querySelector(
            '[data-personal-pin-promotion-cancel]'
        );

    const personalPinPromotionTitle =
        personalPinPromotionPanel?.querySelector(
            '[data-personal-pin-promotion-title]'
        );

    const personalPinPromotionGuidance =
        personalPinPromotionPanel?.querySelector(
            '[data-personal-pin-promotion-guidance]'
        );

    const personalPinPromotionMatch =
        personalPinPromotionPanel?.querySelector(
            '[data-personal-pin-promotion-match]'
        );

    const personalPinPromotionMatchTitle =
        personalPinPromotionPanel?.querySelector(
            '[data-personal-pin-promotion-match-title]'
        );

    const personalPinPromotionMatchCopy =
        personalPinPromotionPanel?.querySelector(
            '[data-personal-pin-promotion-match-copy]'
        );

    const personalPinPromotionMatchLink =
        personalPinPromotionPanel?.querySelector(
            '[data-personal-pin-promotion-match-link]'
        );

    const personalPinPromotionPhoto =
        personalPinPromotionPanel?.querySelector(
            '[data-personal-pin-promotion-photo]'
        );

    const personalPinPromotionPhotoGuidance =
        personalPinPromotionPanel?.querySelector(
            '[data-personal-pin-promotion-photo-guidance]'
        );

    const personalPinPromotionDuplicateWrap =
        personalPinPromotionPanel?.querySelector(
            '[data-personal-pin-promotion-duplicate-wrap]'
        );

    const personalPinPromotionDuplicateAck =
        personalPinPromotionPanel?.querySelector(
            '[data-personal-pin-promotion-duplicate-ack]'
        );

    const personalPinPromotionSubmit =
        personalPinPromotionPanel?.querySelector(
            '[data-personal-pin-promotion-submit]'
        );

    const personalPinPromotionError =
        personalPinPromotionPanel?.querySelector(
            '[data-personal-pin-promotion-error]'
        );

    const personalPinPromotionResult =
        personalPinPromotionPanel?.querySelector(
            '[data-personal-pin-promotion-result]'
        );

    const personalPinPromotionResultTitle =
        personalPinPromotionPanel?.querySelector(
            '[data-personal-pin-promotion-result-title]'
        );

    const personalPinPromotionResultCopy =
        personalPinPromotionPanel?.querySelector(
            '[data-personal-pin-promotion-result-copy]'
        );

    const personalPinPromotionResultLink =
        personalPinPromotionPanel?.querySelector(
            '[data-personal-pin-promotion-result-link]'
        );

    const personalPinPromotionActions =
        personalPinPromotionPanel?.querySelector(
            '[data-personal-pin-promotion-actions]'
        );

    let pendingPersonalPinPromotion = null;

    let viewerCheckedIn =
        mapElement.dataset.viewerCheckedIn === 'true';

    let geolocationWatchId = null;
    let locationUpdateInProgress = false;
    let lastLocationSentAt = 0;
    let locationsLoadController = null;
    let locationsLoadSequence = 0;
    let pendingLiveLocationPosition = null;
    let liveLocationNetworkOffline = false;
    let pendingLiveLocationClear =
        readPendingLocationClear();
    let liveLocationClearPromise = null;
    let pageWasPersistedInHistory = false;
    let sharingWasActiveBeforeHistoryFreeze = false;

    const locationsUrl =
        mapElement.dataset.liveLocationsUrl;

    const festivalMapKey =
        mapElement.dataset.festivalMapKey
        || mapElement.dataset.festivalName
        || 'festival';

    const layerPreferenceStorageKey =
        `festfriends:map-layers:${festivalMapKey}`;

    const readLayerPreferences = () => {
        try {
            const raw=window.localStorage.getItem(
                layerPreferenceStorageKey
            );

            if (!raw) {
                return {};
            }

            const parsed=JSON.parse(raw);

            return parsed
                && typeof parsed === 'object'
                && !Array.isArray(parsed)
                    ? parsed
                    : {};
        } catch {
            return {};
        }
    };

    const persistLayerPreferences = (
        visibility
    ) => {
        try {
            window.localStorage.setItem(
                layerPreferenceStorageKey,
                JSON.stringify(visibility)
            );
        } catch {
            // Storage can be unavailable in privacy-restricted browsers.
        }
    };

    const clearLayerPreferences = () => {
        try {
            window.localStorage.removeItem(
                layerPreferenceStorageKey
            );
        } catch {
            // Storage can be unavailable in privacy-restricted browsers.
        }
    };

    const festivalName =
        mapElement.dataset.festivalName
        || 'Festival';

    const festivalLatitude = Number.parseFloat(
        mapElement.dataset.festivalLatitude
    );

    const festivalLongitude = Number.parseFloat(
        mapElement.dataset.festivalLongitude
    );

    if (
        ! locationsUrl
        || ! Number.isFinite(festivalLatitude)
        || ! Number.isFinite(festivalLongitude)
    ) {
        return;
    }

    const map = L.map(canvas, {
        zoomControl: true,
        scrollWheelZoom: false,
    }).setView(
        [
            festivalLatitude,
            festivalLongitude,
        ],
        16
    );

    L.tileLayer(
        'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
        {
            maxZoom: 20,
            attribution:
                '&copy; OpenStreetMap contributors',
        }
    ).addTo(map);

    const peopleLayer = L.layerGroup().addTo(map);
    const meetupLayer = L.layerGroup().addTo(map);
    const landmarkLayer = L.layerGroup().addTo(map);
    const communityLayer = L.layerGroup().addTo(map);
    const personalPinLayer = L.layerGroup().addTo(map);
    const zoneLayer = L.layerGroup().addTo(map);

    // Path/Junction routing data remains available as an
    // optional advanced layer, but it no longer clutters the
    // default attendee map.
    const pathLayer = L.layerGroup();
    const junctionLayer = L.layerGroup();

    const navigationLayer = L.layerGroup().addTo(map);

    const layerVisibility = {
        people: true,
        meetups: true,
        landmarks: true,
        community: true,
        personal: true,
        zones: true,
        paths: false,
        junctions: false,
    };

    const savedLayerPreferences=
        readLayerPreferences();

    Object.keys(layerVisibility).forEach(
        (layerName) => {
            if (
                typeof savedLayerPreferences[layerName]
                === 'boolean'
            ) {
                layerVisibility[layerName]=
                    savedLayerPreferences[layerName];
            }
        }
    );

    let activeMapView = 'geographic';
    let illustratedMapAvailable = false;
    const memberMotions = new Map();
    const memberFixes = new Map();
    const memberMotionEligibility = new Map();
    const illustratedMemberPins = new Map();
    const motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');

    const cancelMemberMotion = (key, finish = false) => {
        const motion = memberMotions.get(key);
        if (!motion) return;
        window.cancelAnimationFrame(motion.frame);
        memberMotions.delete(key);
        if (finish) motion.apply(motion.target);
    };

    const stopMemberMotions = (finish = true) => {
        [...memberMotions.keys()].forEach(key => cancelMemberMotion(key, finish));
    };

    const clearIllustratedMemberPins = () => {
        illustratedMemberPins.forEach((pin, id) => {
            cancelMemberMotion(`illustrated:${id}`);
            pin.remove();
        });
        illustratedMemberPins.clear();
    };

    const moveMemberPin = (key, from, target, apply, eligible, view) => {
        cancelMemberMotion(key);
        const surface = view === 'illustrated' ? illustratedCanvas : canvas;
        const bounds = surface?.getBoundingClientRect();
        const onScreen = bounds && bounds.width > 0 && bounds.height > 0 && bounds.bottom > 0 && bounds.top < window.innerHeight
            && bounds.right > 0 && bounds.left < window.innerWidth;
        if (!eligible || motionPreference.matches || document.hidden || activeMapView !== view || !onScreen) {
            apply(target);
            return;
        }
        const started = performance.now();
        const motion = { target, apply, frame: null };
        const step = timestamp => {
            if (memberMotions.get(key) !== motion) return;
            if (!mapIsUsable()) {
                cancelMemberMotion(key);
                return;
            }
            const fraction = Math.min(1, Math.max(0, (timestamp - started) / 450));
            if (fraction === 1 || document.hidden || motionPreference.matches || activeMapView !== view) {
                memberMotions.delete(key);
                apply(target);
                return;
            }
            const progress = 1 - (1 - fraction) ** 3;
            apply(from.map((value, index) => value + (target[index] - value) * progress));
            motion.frame = window.requestAnimationFrame(step);
        };
        memberMotions.set(key, motion);
        motion.frame = window.requestAnimationFrame(step);
    };

    let illustratedGuideAvailable =
        false;

    const illustratedGuideColor = (
        element
    ) => {
        if (element.area_color) {
            return element.area_color;
        }

        return {
            main_stage: '#b91c8f',
            secondary_stage: '#6d28d9',
            medical: '#b91c1c',
            information: '#0369a1',
            water_station: '#0369a1',
            bar: '#a16207',
            food_vendors: '#a16207',
            merch: '#a16207',
            accessible: '#1e40af',
            security: '#334155',
            parking: '#64748b',
            camping: '#2563eb',
            rv_camping: '#2563eb',
            custom_area: '#8b5cf6',
            entrance: '#8b572a',
            restroom: '#0f766e',
        }[element.type]
        ?? '#059669';
    };

    let illustratedGuideHighlightTimer =
        null;

    const clearIllustratedGuideHighlights =
        () => {
            window.clearTimeout(
                illustratedGuideHighlightTimer
            );

            illustratedGuideHighlightTimer =
                null;

            illustratedCanvas
                ?.querySelectorAll(
                    '[data-illustrated-guide-highlight]'
                )
                .forEach(
                    (highlight) =>
                        highlight.remove()
                );
        };

    const highlightIllustratedFeatures = (
        type,
        label
    ) => {
        if (
            !illustratedCanvas
            || !illustratedElements
        ) {
            return;
        }

        clearIllustratedGuideHighlights();

        const matches =
            Array.from(
                illustratedElements.children
            ).filter(
                (wrapper) =>
                    wrapper.dataset
                        .illustratedElementType
                        === String(type)
                    && wrapper.dataset
                        .illustratedElementLabel
                        === String(label)
            );

        matches.forEach((wrapper) => {
            const ring =
                document.createElement(
                    'div'
                );

            ring.dataset
                .illustratedGuideHighlight =
                    'true';

            ring.className =
                'pointer-events-none absolute z-[280] rounded-2xl border-4 border-fuchsia-500 bg-fuchsia-300/15 shadow-[0_0_0_4px_rgba(255,255,255,.95),0_0_30px_rgba(217,70,239,.75)]';

            ring.style.left =
                wrapper.style.left;

            ring.style.top =
                wrapper.style.top;

            ring.style.width =
                `max(${
                    wrapper.style.width
                }, 42px)`;

            ring.style.height =
                `max(${
                    wrapper.style.height
                }, 42px)`;

            ring.style.transform =
                'translate(-50%,-50%)';

            illustratedCanvas.append(
                ring
            );

            ring.animate(
                [
                    {
                        opacity: 0,
                        transform:
                            'translate(-50%,-50%) scale(.7)',
                    },
                    {
                        opacity: 1,
                        transform:
                            'translate(-50%,-50%) scale(1.12)',
                    },
                    {
                        opacity: .9,
                        transform:
                            'translate(-50%,-50%) scale(1)',
                    },
                    {
                        opacity: 1,
                        transform:
                            'translate(-50%,-50%) scale(1.08)',
                    },
                    {
                        opacity: .9,
                        transform:
                            'translate(-50%,-50%) scale(1)',
                    },
                ],
                {
                    duration: 1600,
                    easing:
                        'ease-out',
                    fill: 'forwards',
                }
            );
        });

        if (matches.length > 0) {
            setIllustratedGuideOpen(
                false
            );

            illustratedCanvas
                .scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                });

            illustratedGuideHighlightTimer =
                window.setTimeout(
                    clearIllustratedGuideHighlights,
                    4200
                );
        }
    };

    const renderIllustratedGuide = (
        elements
    ) => {
        if (
            !illustratedGuideItems
            || !illustratedGuideToggle
        ) {
            return;
        }

        const excludedTypes =
            new Set([
                'tree',
                'forest',
                'water',
                'path',
                'map_text',
            ]);

        const seen =
            new Set();

        const items =
            elements
                .filter(
                    (element) =>
                        element.show_label
                        && element.group
                            !== 'Decorations'
                        && !excludedTypes.has(
                            element.type
                        )
                )
                .filter(
                    (element) => {
                        const key =
                            `${
                                element.type
                            }|${
                                element.label
                            }`;

                        if (seen.has(key)) {
                            return false;
                        }

                        seen.add(key);
                        return true;
                    }
                );

        illustratedGuideItems
            .replaceChildren();

        items.forEach((element) => {
            const item =
                document.createElement(
                    'button'
                );

            item.type =
                'button';

            item.className =
                'flex min-w-0 items-center gap-2 rounded-xl border border-violet-100 bg-white/90 px-3 py-2 text-left shadow-sm transition hover:border-fuchsia-300 hover:bg-fuchsia-50 focus:outline-none focus:ring-2 focus:ring-fuchsia-400 dark:border-violet-400/10 dark:bg-zinc-900/80 dark:hover:border-fuchsia-400/30 dark:hover:bg-fuchsia-400/10';

            const icon =
                document.createElement(
                    'span'
                );

            icon.className =
                'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border-2 border-white text-sm shadow-sm';

            icon.style.background =
                illustratedGuideColor(
                    element
                );

            icon.textContent =
                element.icon
                || '•';

            const label =
                document.createElement(
                    'span'
                );

            label.className =
                'min-w-0 truncate text-xs font-black text-zinc-800 dark:text-zinc-100';

            label.textContent =
                element.label
                || 'Map item';

            const locate =
                document.createElement(
                    'span'
                );

            locate.className =
                'ml-auto shrink-0 text-[10px] font-black uppercase tracking-wide text-fuchsia-600 dark:text-fuchsia-300';

            locate.textContent =
                'Locate';

            item.append(
                icon,
                label,
                locate
            );

            item.addEventListener(
                'click',
                () => {
                    highlightIllustratedFeatures(
                        element.type,
                        element.label
                    );
                },
                {
                    signal:
                        eventController.signal,
                }
            );

            illustratedGuideItems.append(
                item
            );
        });

        illustratedGuideAvailable =
            items.length > 0;

        illustratedGuideToggle
            .classList.toggle(
                'hidden',
                !illustratedGuideAvailable
                || activeMapView
                    !== 'illustrated'
            );

        if (
            !illustratedGuideAvailable
        ) {
            illustratedGuide
                ?.classList.add(
                    'hidden'
                );

            illustratedGuideToggle
                .setAttribute(
                    'aria-expanded',
                    'false'
                );
        }
    };

    const setIllustratedGuideOpen = (
        open
    ) => {
        const shouldOpen =
            Boolean(
                open
                && illustratedGuideAvailable
                && activeMapView
                    === 'illustrated'
            );

        illustratedGuide
            ?.classList.toggle(
                'hidden',
                !shouldOpen
            );

        illustratedGuideToggle
            ?.setAttribute(
                'aria-expanded',
                shouldOpen
                    ? 'true'
                    : 'false'
            );
    };

    const setMapView = (view) => {
        const nextView =
            view === 'illustrated'
            && illustratedMapAvailable
                ? 'illustrated'
                : 'geographic';

        if (nextView !== activeMapView) stopMemberMotions();
        activeMapView = nextView;

        canvas.classList.toggle(
            'hidden',
            nextView === 'illustrated'
        );

        illustratedCanvas?.classList.toggle(
            'hidden',
            nextView !== 'illustrated'
        );

        mapViewButtons.forEach((button) => {
            button.setAttribute(
                'aria-pressed',
                button.dataset.festivalMapView
                    === nextView
                    ? 'true'
                    : 'false'
            );
        });

        illustratedGuideToggle
            ?.classList.toggle(
                'hidden',
                nextView
                    !== 'illustrated'
                || !illustratedGuideAvailable
            );

        if (nextView !== 'illustrated') {
            setIllustratedGuideOpen(
                false
            );

            clearIllustratedGuideHighlights();
        }

        if (nextView === 'geographic') {
            window.setTimeout(() => {
                map.invalidateSize();
            }, 50);
        }
    };

    mapViewButtons.forEach((button) => {
        button.addEventListener(
            'click',
            () => {
                setMapView(
                    button.dataset.festivalMapView
                );
            },
            {
                signal: eventController.signal,
            }
        );
    });

    illustratedGuideToggle
        ?.addEventListener(
            'click',
            () => {
                setIllustratedGuideOpen(
                    illustratedGuideToggle
                        .getAttribute(
                            'aria-expanded'
                        )
                    !== 'true'
                );
            },
            {
                signal:
                    eventController.signal,
            }
        );

    illustratedGuideClose
        ?.addEventListener(
            'click',
            () => {
                setIllustratedGuideOpen(
                    false
                );
            },
            {
                signal:
                    eventController.signal,
            }
        );

    const festivalMarkerIcon = L.divIcon({
        className: '',
        html: `
            <div
                style="
                    display:flex;
                    align-items:center;
                    justify-content:center;
                    width:42px;
                    height:42px;
                    border-radius:9999px;
                    border:3px solid white;
                    background:#0891b2;
                    color:white;
                    font-size:20px;
                    box-shadow:0 8px 24px rgba(0,0,0,.45);
                "
                aria-hidden="true"
            >
                🎪
            </div>
        `,
        iconSize: [42, 42],
        iconAnchor: [21, 21],
        popupAnchor: [0, -24],
    });

    L.marker(
        [
            festivalLatitude,
            festivalLongitude,
        ],
        {
            icon: festivalMarkerIcon,
            zIndexOffset: 100,
        }
    )
        .addTo(map)
        .bindPopup(
            `
                <div style="min-width:180px">
                    <strong>${escapeHtml(festivalName)}</strong>
                    <div style="margin-top:4px">
                        Festival location
                    </div>
                </div>
            `
        );

    const memberMarkers = new Map();

    let currentUserMemberId = null;

    const memberPresence =
        new Map();

    const navigableMemberIds =
        new Set();

    const publishMemberMarkerState = () => {
        window.dispatchEvent(
            new CustomEvent(
                'festival:member-markers-changed',
                {
                    detail: {
                        memberIds:
                            Array.from(
                                memberMarkers.keys()
                            ).map(
                                (memberId) =>
                                    String(
                                        memberId
                                    )
                            ),

                        navigableMemberIds:
                            viewerCheckedIn
                                ? Array.from(
                                    navigableMemberIds
                                ).map(
                                    (memberId) =>
                                        String(
                                            memberId
                                        )
                                )
                                : [],

                        members:
                            Array.from(
                                memberPresence
                                    .values()
                            ),
                    },
                }
            )
        );
    };

    const meetupMarkers = new Map();

    const landmarkMarkers = new Map();
    const personalPinMarkers = new Map();

    const zonePolygons = new Map();

    const pathPolylines = new Map();

    const junctionMarkers = new Map();

    let navigationPolyline = null;
    let navigationStartMarker = null;
    let navigationEndMarker = null;
    let activeNavigationRoute = null;
    let activeNavigationLandmark = null;
    let activeNavigationMemberId = null;
    let navigationWatchId = null;
    let navigationOffRouteFixCount = 0;
    let navigationRerouteInProgress = false;
    let lastNavigationRerouteAt = 0;
    let navigationArrivalFixCount = 0;
    let navigationArrived = false;
    let directGuidancePreviousDistance = null;

    const navigationOffRouteRequiredFixes = 3;
    const navigationRerouteCooldownMs = 20000;
    const navigationArrivalRequiredFixes = 2;

    let highlightedCircle = null;

    const highlightMarker = (marker) => {
        if (!marker) {
            return;
        }

        if (highlightedCircle) {
            map.removeLayer(highlightedCircle);
            highlightedCircle = null;
        }

        const center = marker.getLatLng();

        highlightedCircle = L.circleMarker(
            center,
            {
                radius: 24,
                color: '#ec4899',
                weight: 5,
                fillColor: '#ec4899',
                fillOpacity: 0.12,
                opacity: 1,
            }
        ).addTo(map);

        let radius = 24;

        highlightedCircle.setRadius(radius);

        const animation = window.setInterval(() => {
            radius += 1.5;

            if (!highlightedCircle) {
                window.clearInterval(animation);
                return;
            }

            highlightedCircle.setRadius(radius);

            highlightedCircle.setStyle({
                opacity: Math.max(
                    0,
                    1 - (radius - 24) / 36
                ),
            });

            if (radius >= 60) {
                window.clearInterval(animation);

                map.removeLayer(
                    highlightedCircle
                );

                highlightedCircle = null;
            }
        }, 50);
    };

    const showError = (message) => {
        if (! errorElement) {
            return;
        }

        errorElement.textContent = message;
        errorElement.classList.remove('hidden');
    };

    const hideError = () => {
        if (! errorElement) {
            return;
        }

        errorElement.textContent = '';
        errorElement.classList.add('hidden');
    };

    const showSharingStatus = (
        message,
        type = 'success',
        source = 'general'
    ) => {
        if (! sharingStatus) {
            return;
        }

        sharingStatus.textContent = message;
        sharingStatus.dataset.liveMapStatusSource =
            source;

        sharingStatus.classList.remove(
            'hidden',
            'bg-emerald-400/10',
            'text-emerald-200',
            'bg-rose-400/10',
            'text-rose-700', 'dark:text-rose-200',
            'bg-cyan-400/10',
            'text-cyan-700', 'dark:text-cyan-200'
        );

        if (type === 'error') {
            sharingStatus.classList.add(
                'bg-rose-400/10',
                'text-rose-700', 'dark:text-rose-200'
            );

            return;
        }

        if (type === 'info') {
            sharingStatus.classList.add(
                'bg-cyan-400/10',
                'text-cyan-700', 'dark:text-cyan-200'
            );

            return;
        }

        sharingStatus.classList.add(
            'bg-emerald-400/10',
            'text-emerald-200'
        );
    };

    const clearLiveMapLocationStateStatus = () => {
        if (! sharingStatus) {
            return;
        }

        const source =
            sharingStatus.dataset
                .liveMapStatusSource;

        if (
            source !== 'geofence'
            && source !== 'freshness'
        ) {
            return;
        }

        sharingStatus.textContent = '';
        delete sharingStatus.dataset
            .liveMapStatusSource;
        sharingStatus.classList.add(
            'hidden'
        );
    };

    const updateCommandLocationAction = (
        checkedIn,
        sharingActive
    ) => {
        commandLocationButtons.forEach(
            (button) => {
                const label =
                    button.querySelector(
                        '[data-command-location-label]'
                    );

                const icon =
                    button.querySelector(
                        'span[aria-hidden="true"]'
                    );

                if (! checkedIn) {
                    if (label) {
                        label.textContent =
                            'Live Map';
                    }

                    if (icon) {
                        icon.textContent = '🗺️';
                    }

                    button.setAttribute(
                        'aria-label',
                        'View the festival live map'
                    );

                    return;
                }

                if (sharingActive) {
                    if (label) {
                        label.textContent =
                            'Stop Sharing';
                    }

                    if (icon) {
                        icon.textContent = '⏹️';
                    }

                    button.setAttribute(
                        'aria-label',
                        'Stop sharing your live location'
                    );

                    return;
                }

                if (label) {
                    label.textContent =
                        'Share Location';
                }

                if (icon) {
                    icon.textContent = '📡';
                }

                button.setAttribute(
                    'aria-label',
                    'Share your live location'
                );
            }
        );
    };

    const setSharingActive = (active) => {
        startSharingButton?.classList.toggle(
            'hidden',
            active
        );

        stopSharingButton?.classList.toggle(
            'hidden',
            ! active
        );

        updateCommandLocationAction(
            viewerCheckedIn,
            active
        );
    };

    const setViewerCheckedIn = (
        checkedIn
    ) => {
        viewerCheckedIn = checkedIn;

        mapElement.dataset.viewerCheckedIn =
            checkedIn ? 'true' : 'false';

        publishMemberMarkerState();

        if (! checkedIn) {
            if (
                geolocationWatchId !== null
            ) {
                navigator.geolocation.clearWatch(
                    geolocationWatchId
                );

                geolocationWatchId = null;
            }

            setSharingActive(false);

            startSharingButton?.classList.add(
                'hidden'
            );

            sharingStatus?.classList.add(
                'hidden'
            );

            return;
        }

        if (geolocationWatchId === null) {
            startSharingButton?.classList.remove(
                'hidden'
            );

            stopSharingButton?.classList.add(
                'hidden'
            );
        }

        updateCommandLocationAction(
            checkedIn,
            geolocationWatchId !== null
        );
    };

    const formatMiles = (value) => {
        const numericValue = Number(value);

        if (! Number.isFinite(numericValue)) {
            return null;
        }

        return Number.isInteger(numericValue)
            ? numericValue.toFixed(0)
            : numericValue
                .toFixed(2)
                .replace(/0+$/, '')
                .replace(/\.$/, '');
    };

    const showOutsideFestivalAreaStatus = (
        liveMapState,
        { persisted = false } = {}
    ) => {
        const outsideFestivalArea =
            liveMapState?.geofence_active === true
            && liveMapState?.within_festival_area === false;

        if (! outsideFestivalArea) {
            return false;
        }

        const formattedDistance = formatMiles(
            liveMapState.distance_miles
        );

        const formattedRadius = formatMiles(
            liveMapState.radius_miles
        );

        const distanceSummary = formattedDistance !== null
            ? `You are approximately ${formattedDistance} miles from the festival location`
            : 'You are currently outside the festival location area';

        const radiusSummary = formattedRadius !== null
            ? `outside the ${formattedRadius}-mile Live Map area`
            : 'outside the configured Live Map area';

        const message = persisted
            ? `Your most recent location was received successfully. ${distanceSummary} and ${radiusSummary}, so your pin is intentionally hidden. If you have moved closer, use Share My Location to update your position; otherwise nothing is broken and you do not need to keep pressing it.`
            : `Location sharing is working. ${distanceSummary} and ${radiusSummary}, so your pin is intentionally hidden. It will appear automatically once a GPS update is inside the festival area.`;

        showSharingStatus(
            message,
            'info',
            'geofence'
        );

        return true;
    };



    const updateViewerLiveLocationStatus = (
        previousState,
        currentState
    ) => {
        const wasFresh =
            previousState?.is_fresh === true;

        const isFresh =
            currentState?.is_fresh === true;

        const hasSharedLocation =
            currentState
                ?.has_shared_location === true;

        const hasValidCoordinates =
            currentState
                ?.has_valid_coordinates !== false;

        const hasAcceptableAccuracy =
            currentState
                ?.has_acceptable_accuracy !== false;

        const hasValidTimestamp =
            currentState
                ?.has_valid_timestamp !== false;

        if (
            hasSharedLocation
            && ! hasValidCoordinates
        ) {
            const message =
                geolocationWatchId !== null
                    ? 'Your last stored location contained invalid GPS coordinates, so FestFriends is keeping that pin hidden. Location sharing is still on and the next valid reading will replace it automatically.'
                    : 'Your last stored location contained invalid GPS coordinates, so FestFriends is keeping that pin hidden. Choose Share My Location to send a new valid position.';

            showSharingStatus(
                message,
                'info',
                'coordinates'
            );

            return;
        }

        if (
            hasSharedLocation
            && ! hasAcceptableAccuracy
        ) {
            const message =
                geolocationWatchId !== null
                    ? 'Your last stored location does not include a reliable enough GPS accuracy measurement, so FestFriends is keeping that pin hidden. Location sharing is still on and the next reliable reading will replace it automatically.'
                    : 'Your last stored location does not include a reliable enough GPS accuracy measurement, so FestFriends is keeping that pin hidden. Choose Share My Location to send a new reliable position.';

            showSharingStatus(
                message,
                'info',
                'accuracy'
            );

            return;
        }

        if (
            hasSharedLocation
            && ! hasValidTimestamp
        ) {
            const message =
                geolocationWatchId !== null
                    ? 'Your last stored location has an invalid timestamp, so FestFriends is keeping that pin hidden. Location sharing is still on and the next valid reading will replace it automatically.'
                    : 'Your last stored location has an invalid timestamp, so FestFriends is keeping that pin hidden. Choose Share My Location to send a new valid position.';

            showSharingStatus(
                message,
                'info',
                'timestamp'
            );

            return;
        }

        if (
            hasSharedLocation
            && ! isFresh
        ) {
            const message =
                geolocationWatchId !== null
                    ? 'Your last reliable GPS location is no longer recent enough to appear on the Live Map. Location sharing is still on and FestFriends is waiting for the next reliable reading before showing your pin again.'
                    : 'Your last shared location is no longer recent enough to appear on the Live Map. If you want to appear again, choose Share My Location to send a fresh position.';

            showSharingStatus(
                message,
                'info',
                'freshness'
            );

            return;
        }

        const wasOutside =
            wasFresh
            && previousState
                ?.geofence_active === true
            && previousState
                ?.within_festival_area === false;

        const isOutside =
            isFresh
            && currentState
                ?.geofence_active === true
            && currentState
                ?.within_festival_area === false;

        if (isOutside) {
            showOutsideFestivalAreaStatus(
                currentState,
                { persisted: true }
            );

            return;
        }

        if (
            wasOutside
            && isFresh
            && currentState
                ?.within_festival_area === true
        ) {
            showSharingStatus(
                'You are now inside the Festival Live Map area. Your pin can appear normally while your shared location remains current.',
                'success',
                'geofence'
            );

            return;
        }

        if (
            ! wasFresh
            && isFresh
            && previousState
                ?.has_shared_location === true
        ) {
            showSharingStatus(
                'FestFriends received a fresh reliable location again. Your Live Map pin can appear normally now.',
                'success',
                'freshness'
            );

            return;
        }

        clearLiveMapLocationStateStatus();
    };

    const updateCenterMeControl = () => {
        if (! centerMeButton) {
            return;
        }

        const hasVisibleViewerMarker =
            currentUserMemberId !== null
            && memberMarkers.has(
                currentUserMemberId
            );

        centerMeButton.disabled =
            ! hasVisibleViewerMarker;

        if (hasVisibleViewerMarker) {
            centerMeButton.title =
                'Center the map on your current shared location.';
            centerMeButton.setAttribute(
                'aria-label',
                'Center map on my current shared location'
            );

            return;
        }

        let reason =
            'Share your location to place your pin on the Live Map.';

        if (! viewerCheckedIn) {
            reason =
                'Check in to this Festival before sharing and centering on your location.';
        } else if (
            viewerLiveLocationState
                ?.has_shared_location === true
            && viewerLiveLocationState
                ?.has_valid_coordinates === false
        ) {
            reason =
                'Your last stored location contained invalid GPS coordinates. Use Share My Location to replace it with a valid reading.';
        } else if (
            viewerLiveLocationState
                ?.has_shared_location === true
            && viewerLiveLocationState
                ?.is_fresh !== true
        ) {
            reason =
                'Your last shared location is no longer recent enough to appear on the Live Map. Use Share My Location to update it.';
        } else if (
            viewerLiveLocationState
                ?.is_fresh === true
            && viewerLiveLocationState
                ?.geofence_active === true
            && viewerLiveLocationState
                ?.within_festival_area === false
        ) {
            const radius =
                formatMiles(
                    viewerLiveLocationState
                        ?.radius_miles
                );

            reason = radius !== null
                ? `Your last shared location is outside the ${radius}-mile Festival Live Map area, so your pin is hidden.`
                : 'Your last shared location is outside the Festival Live Map area, so your pin is hidden.';
        }

        centerMeButton.title = reason;
        centerMeButton.setAttribute(
            'aria-label',
            `Center on Me unavailable. ${reason}`
        );
    };

    const updateGeofenceMeta = (festivalPayload) => {
        liveMapGeofenceActive = Boolean(
            festivalPayload?.live_map_geofence_active
        );

        const parsedRadius = Number(
            festivalPayload?.live_map_radius_miles
        );

        liveMapRadiusMiles = Number.isFinite(parsedRadius)
            ? parsedRadius
            : null;

        const shouldShowGeofence =
            liveMapGeofenceActive
            && liveMapRadiusMiles !== null
            && liveMapRadiusMiles > 0;

        if (shouldShowGeofence) {
            const radiusMeters =
                liveMapRadiusMiles * 1609.344;

            if (! liveMapGeofenceCircle) {
                liveMapGeofenceCircle = L.circle(
                    [
                        festivalLatitude,
                        festivalLongitude,
                    ],
                    {
                        radius: radiusMeters,
                        color: '#9333ea',
                        weight: 2,
                        opacity: 0.7,
                        fillColor: '#a855f7',
                        fillOpacity: 0.045,
                        dashArray: '8 8',
                        interactive: false,
                    }
                ).addTo(map);
            } else {
                liveMapGeofenceCircle
                    .setLatLng([
                        festivalLatitude,
                        festivalLongitude,
                    ])
                    .setRadius(radiusMeters);

                if (! map.hasLayer(liveMapGeofenceCircle)) {
                    liveMapGeofenceCircle.addTo(map);
                }
            }
        } else if (liveMapGeofenceCircle) {
            map.removeLayer(liveMapGeofenceCircle);
        }

        if (! radiusBadge || ! radiusText) {
            return;
        }

        if (! shouldShowGeofence) {
            radiusBadge.classList.add('hidden');
            radiusBadge.classList.remove('inline-flex');
            radiusText.textContent = '';
            return;
        }

        const formattedRadius = formatMiles(liveMapRadiusMiles);

        radiusText.textContent = `${formattedRadius}-mile festival area`;
        radiusBadge.classList.remove('hidden');
        radiusBadge.classList.add('inline-flex');
    };

    const updateCount = (count) => {
        if (! countText) {
            return;
        }

        if (count === 0 && liveMapGeofenceActive) {
            countText.textContent = 'No nearby live locations';
            return;
        }

        countText.textContent =
            count === 1
                ? '1 live location'
                : `${count} live locations`;
    };

    const removeMissingMarkers = (memberIds) => {
        memberMarkers.forEach(
            (marker, memberId) => {
                if (memberIds.has(memberId)) {
                    return;
                }

                cancelMemberMotion(`geographic:${memberId}`);
                peopleLayer.removeLayer(marker);
                memberMarkers.delete(memberId);
            }
        );
        const visibleIds = new Set([...memberIds].map(String));
        illustratedMemberPins.forEach((pin, id) => {
            if (visibleIds.has(id)) return;
            cancelMemberMotion(`illustrated:${id}`);
            pin.remove();
            illustratedMemberPins.delete(id);
        });
        memberFixes.forEach((fix, id) => {
            if (visibleIds.has(id)) return;
            memberFixes.delete(id);
            memberMotionEligibility.delete(id);
        });
    };

    const createMemberIcon = (member) => {
        const label =
            member.profile_photo_url
                ? `
                    <img
                        src="${escapeAttribute(
                    member.profile_photo_url
                )}"
                        alt=""
                        style="
                            width:100%;
                            height:100%;
                            object-fit:cover;
                            border-radius:9999px;
                        "
                    >
                `
                : `
                    <span
                        style="
                            display:flex;
                            width:100%;
                            height:100%;
                            align-items:center;
                            justify-content:center;
                            font-size:13px;
                            font-weight:900;
                            color:white;
                        "
                    >
                        ${escapeHtml(
                    member.initials || '?'
                )}
                    </span>
                `;

        const background =
            member.is_current_user
                ? '#059669'
                : member.is_friend
                    ? '#c026d3'
                    : '#2563eb';

        return L.divIcon({
            className: '',
            html: `
                <div
                    style="
                        width:46px;
                        height:46px;
                        padding:3px;
                        border-radius:9999px;
                        border:3px solid white;
                        background:${background};
                        box-shadow:0 8px 24px rgba(0,0,0,.5);
                    "
                >
                    ${label}
                </div>
            `,
            iconSize: [46, 46],
            iconAnchor: [23, 23],
            popupAnchor: [0, -26],
        });
    };

    const createMemberPopup = (member) => {
        const name = escapeHtml(
            member.name || 'FestFriends member'
        );

        const avatar = member.profile_photo_url
            ? `
                <img
                    src="${escapeAttribute(member.profile_photo_url)}"
                    alt=""
                    style="
                        width:46px;
                        height:46px;
                        border-radius:9999px;
                        object-fit:cover;
                        border:2px solid white;
                        box-shadow:0 2px 8px rgba(15,23,42,.18);
                    "
                >
            `
            : `
                <div
                    style="
                        display:flex;
                        width:46px;
                        height:46px;
                        align-items:center;
                        justify-content:center;
                        border-radius:9999px;
                        background:#2563eb;
                        color:white;
                        font-size:14px;
                        font-weight:900;
                        border:2px solid white;
                        box-shadow:0 2px 8px rgba(15,23,42,.18);
                    "
                >
                    ${escapeHtml(member.initials || '?')}
                </div>
            `;

        const relationshipBadge = member.is_current_user
            ? 'You'
            : member.is_friend
                ? 'Friend'
                : 'Festival member';

        const locationParts = [];

        if (member.current_zone?.name) {
            locationParts.push(
                `◫ In ${escapeHtml(member.current_zone.name)}`
            );
        } else if (member.nearest_landmark?.name) {
            locationParts.push(
                `📍 Near ${escapeHtml(member.nearest_landmark.name)}`
            );
        }

        if (member.current_location) {
            locationParts.push(
                escapeHtml(formatLabel(member.current_location))
            );
        }

        const location = locationParts.length
            ? `
                <div style="margin-top:10px;font-size:12px;font-weight:700;color:#0f766e">
                    ${locationParts.join(' · ')}
                </div>
            `
            : '';

        const status = member.current_status
            ? `
                <div style="margin-top:7px;font-size:12px;color:#334155">
                    <strong>Status:</strong>
                    ${escapeHtml(formatLabel(member.current_status))}
                </div>
            `
            : '';

        const note = member.current_status_note
            ? `
                <div
                    style="
                        margin-top:7px;
                        padding:8px 10px;
                        border-radius:9px;
                        background:#f8fafc;
                        color:#475569;
                        font-size:12px;
                        line-height:1.4;
                    "
                >
                    ${escapeHtml(member.current_status_note)}
                </div>
            `
            : '';

        const updated = member.location_updated_at_human
            ? `
                <div style="margin-top:8px;font-size:11px;color:#64748b">
                    Location updated ${escapeHtml(member.location_updated_at_human)}
                </div>
            `
            : '';

        const findFriendAction = (
            viewerCheckedIn
            && member.is_friend
            && ! member.is_current_user
            && Number.isFinite(Number.parseFloat(member.latitude))
            && Number.isFinite(Number.parseFloat(member.longitude))
        )
            ? `
                <button
                    type="button"
                    data-member-direct-guide
                    data-member-id="${escapeAttribute(String(member.id))}"
                    style="
                        border:0;
                        border-radius:9px;
                        background:#c026d3;
                        color:white;
                        padding:9px 11px;
                        font-size:12px;
                        font-weight:800;
                        cursor:pointer;
                    "
                >
                    🧭 Find Friend
                </button>
            `
            : '';

        const profileLink = member.profile_url
            ? `
                <a
                    href="${escapeAttribute(member.profile_url)}"
                    style="
                        display:inline-flex;
                        align-items:center;
                        justify-content:center;
                        border:1px solid #cbd5e1;
                        border-radius:9px;
                        background:white;
                        color:#334155;
                        padding:8px 11px;
                        font-size:12px;
                        font-weight:800;
                        text-decoration:none;
                    "
                >
                    View Profile
                </a>
            `
            : '';

        const quickHello = (
            member.is_friend
            && ! member.is_current_user
            && member.quick_message_url
        )
            ? `
                <div
                    style="
                        margin-top:12px;
                        padding-top:11px;
                        border-top:1px solid #e2e8f0;
                    "
                    data-member-quick-message
                    data-quick-message-url="${escapeAttribute(member.quick_message_url)}"
                >
                    <div style="font-size:11px;font-weight:900;text-transform:uppercase;letter-spacing:.08em;color:#64748b">
                        Quick Hello
                    </div>

                    <div style="display:flex;gap:6px;margin-top:6px">
                        <select
                            data-member-quick-message-select
                            style="
                                min-width:0;
                                flex:1;
                                border:1px solid #cbd5e1;
                                border-radius:9px;
                                background:white;
                                color:#334155;
                                padding:8px;
                                font-size:12px;
                            "
                        >
                            <option value="Hi! 👋">Hi! 👋</option>
                            <option value="How are you doing?">How are you doing?</option>
                            <option value="Are you having fun?">Are you having fun?</option>
                            <option value="Want to meet up?">Want to meet up?</option>
                            <option value="I’m nearby! 👋">I’m nearby! 👋</option>
                        </select>

                        <button
                            type="button"
                            data-member-quick-message-send
                            style="
                                border:0;
                                border-radius:9px;
                                background:#0891b2;
                                color:white;
                                padding:8px 11px;
                                font-size:12px;
                                font-weight:900;
                                cursor:pointer;
                            "
                        >
                            Send
                        </button>
                    </div>

                    <div
                        data-member-quick-message-status
                        style="display:none;margin-top:6px;font-size:11px;font-weight:700"
                        role="status"
                        aria-live="polite"
                    ></div>
                </div>
            `
            : '';

        return `
            <div
                style="
                    min-width:260px;
                    max-width:300px;
                    color:#0f172a;
                    font-family:inherit;
                "
            >
                <div style="display:flex;align-items:center;gap:10px">
                    ${avatar}

                    <div style="min-width:0;flex:1">
                        <div style="font-size:15px;font-weight:900;line-height:1.2">
                            ${name}
                        </div>

                        <span
                            style="
                                display:inline-block;
                                margin-top:4px;
                                border-radius:9999px;
                                background:${member.is_friend ? '#fae8ff' : '#e0f2fe'};
                                color:${member.is_friend ? '#a21caf' : '#0369a1'};
                                padding:3px 7px;
                                font-size:10px;
                                font-weight:900;
                            "
                        >
                            ${relationshipBadge}
                        </span>
                    </div>
                </div>

                ${location}
                ${status}
                ${note}
                ${updated}

                ${(findFriendAction || profileLink)
                    ? `
                        <div style="display:flex;flex-wrap:wrap;gap:6px;margin-top:11px">
                            ${findFriendAction}
                            ${profileLink}
                        </div>
                    `
                    : ''}

                ${quickHello}
            </div>
        `;
    };

    const createMeetupIcon = () => {
        return L.divIcon({
            className: '',
            html: `
            <div
                style="
                    display:flex;
                    align-items:center;
                    justify-content:center;
                    width:42px;
                    height:42px;
                    border-radius:9999px;
                    border:3px solid white;
                    background:#f59e0b;
                    color:white;
                    font-size:20px;
                    box-shadow:0 8px 24px rgba(0,0,0,.5);
                "
                aria-hidden="true"
            >
                ⭐
            </div>
        `,
            iconSize: [42, 42],
            iconAnchor: [21, 21],
            popupAnchor: [0, -24],
        });
    };

    const createMeetupPopup = (meetup) => {
        const title = escapeHtml(
            meetup.title || 'Festival meetup'
        );

        const category = meetup.category_label
            ? `
            <div style="margin-top:5px;color:#92400e">
                ${escapeHtml(meetup.category_label)}
            </div>
        `
            : '';

        const location = meetup.location_label
            ? `
            <div style="margin-top:7px">
                📍 ${escapeHtml(meetup.location_label)}
            </div>
        `
            : '';

        const startTime = meetup.starts_at_label
            ? `
            <div style="margin-top:5px">
                🕒 ${escapeHtml(meetup.starts_at_label)}
            </div>
        `
            : '';

        const memberCount = Number.parseInt(
            meetup.members_count,
            10
        ) || 0;

        const attendees = `
        <div style="margin-top:5px">
            👥 ${memberCount}
            ${memberCount === 1
            ? 'attendee'
            : 'attendees'}
        </div>
    `;

        const locationNotice =
            meetup.has_exact_coordinates
                ? ''
                : `
                <div
                    style="
                        margin-top:8px;
                        font-size:12px;
                        color:#64748b;
                    "
                >
                    Approximate festival-area location
                </div>
            `;

        const directGuidanceAction = (
            viewerCheckedIn
            && meetup.has_exact_coordinates
            && Number.isFinite(
                Number.parseFloat(
                    meetup.latitude
                )
            )
            && Number.isFinite(
                Number.parseFloat(
                    meetup.longitude
                )
            )
        )
            ? `
                <button
                    type="button"
                    data-meetup-direct-guide
                    data-meetup-id="${escapeAttribute(
                        String(meetup.id)
                    )}"
                    style="
                        display:block;
                        width:100%;
                        margin-top:10px;
                        border:0;
                        border-radius:8px;
                        background:#d97706;
                        color:white;
                        padding:8px 12px;
                        cursor:pointer;
                        font-weight:800;
                    "
                >
                    🧭 Go Here
                </button>

                <div
                    style="
                        margin-top:5px;
                        font-size:11px;
                        line-height:1.4;
                        color:#64748b;
                    "
                >
                    Direct GPS guidance to this Meetup location.
                </div>
            `
            : '';

        const rememberPrivateAction=(
            viewerCheckedIn
            && meetup.can_remember_private_pin
            && meetup.remember_private_pin_url
        )
            ? `
                <button
                    type="button"
                    data-meetup-remember-private
                    data-meetup-id="${escapeAttribute(String(meetup.id))}"
                    data-meetup-private-pin-preview-url="${escapeAttribute(meetup.private_pin_preview_url || '')}"
                    data-meetup-remember-private-url="${escapeAttribute(meetup.remember_private_pin_url)}"
                    style="
                        display:block;
                        width:100%;
                        margin-top:8px;
                        border:1px solid #d946ef;
                        border-radius:8px;
                        background:#fdf4ff;
                        color:#a21caf;
                        padding:8px 12px;
                        cursor:pointer;
                        font-weight:800;
                    "
                >
                    📌 Remember this meetup privately
                </button>

                <div
                    style="
                        margin-top:5px;
                        font-size:11px;
                        line-height:1.4;
                        color:#64748b;
                    "
                >
                    Saves one owner-only private pin. It does not share the meetup location more broadly.
                </div>
            `
            : '';

        return `
    <div style="min-width:210px">
        <strong>${title}</strong>

        ${category}

        ${location}

        ${startTime}

        ${attendees}

        ${locationNotice}

        ${directGuidanceAction}

        ${rememberPrivateAction}

        <a
    href="${escapeAttribute(
            meetup.details_url || '#'
        )}"
    style="
        display:inline-block;
        margin-top:10px;
        padding:7px 12px;
        border:none;
        border-radius:8px;
        background:#0891b2;
        color:white;
        cursor:pointer;
        font-weight:700;
        text-decoration:none;
    "
>
    View Meetup
</a>
    </div>
`;
    };

    const landmarkEmoji = (type) => {
        const icons = {
            stage: '🎵',
            entrance: '🚪',
            parking: '🅿️',
            camping: '⛺',
            restroom: '🚻',
            vendor: '🛍️',
            water: '💧',
            medical: '🩹',
            information: 'ℹ️',
            security: '🛡️',
            other: '📍',
        };

        return icons[type] || '📍';
    };

    const createLandmarkIcon = (landmark) => {
        const emoji = landmarkEmoji(
            landmark.type
        );

        return L.divIcon({
            className: '',
            html: `
                <div
                    style="
                        display:flex;
                        align-items:center;
                        justify-content:center;
                        width:36px;
                        height:36px;
                        border-radius:12px;
                        border:2px solid white;
                        background:#0f766e;
                        color:white;
                        font-size:18px;
                        box-shadow:0 6px 18px rgba(0,0,0,.4);
                    "
                    aria-hidden="true"
                >
                    ${emoji}
                </div>
            `,
            iconSize: [36, 36],
            iconAnchor: [18, 18],
            popupAnchor: [0, -20],
        });
    };

    const confirmationMarkup = (
        targetType,
        item
    ) => {
        const count = Number.parseInt(
            item.confirmation_count,
            10
        ) || 0;

        const countLabel =
            `${count} ${count === 1 ? 'confirmation' : 'confirmations'}`;

        const historicalCount = Number.parseInt(
            item.historical_confirmation_count,
            10
        ) || 0;

        const editionYear =
            item.edition_year
            || 'this edition';

        const freshnessMarkup = (() => {
            if (count > 0) {
                return '';
            }

            if (historicalCount > 0) {
                const historicalLabel =
                    `${historicalCount} previous ${
                        historicalCount === 1
                            ? 'confirmation'
                            : 'confirmations'
                    }`;

                return `
                    <div
                        style="
                            margin-top:8px;
                            padding:7px 8px;
                            border-radius:8px;
                            background:#fff7ed;
                            color:#9a3412;
                            font-size:11px;
                            line-height:1.4;
                            font-weight:700;
                        "
                    >
                        Needs verification for ${escapeHtml(
                            String(editionYear)
                        )} · ${historicalLabel}
                    </div>
                `;
            }

            return `
                <div
                    style="
                        margin-top:8px;
                        padding:7px 8px;
                        border-radius:8px;
                        background:#f4f4f5;
                        color:#52525b;
                        font-size:11px;
                        line-height:1.4;
                        font-weight:700;
                    "
                >
                    Not yet verified for ${escapeHtml(
                        String(editionYear)
                    )}
                </div>
            `;
        })();

        if (item.viewer_confirmed) {
            return `
                <div
                    style="
                        margin-top:10px;
                        font-size:12px;
                        font-weight:700;
                        color:#047857;
                    "
                >
                    ✓ Confirmed accurate · ${countLabel}
                </div>
                ${freshnessMarkup}
            `;
        }

        if (
            !viewerCheckedIn
            || !venueMapConfirmUrl
        ) {
            return count > 0
                ? `
                    <div
                        style="
                            margin-top:10px;
                            font-size:12px;
                            color:#64748b;
                        "
                    >
                        ${countLabel}
                    </div>
                    ${freshnessMarkup}
                `
                : freshnessMarkup;
        }

        return `
            <div style="margin-top:10px">
                <button
                    type="button"
                    data-venue-map-confirm
                    data-target-type="${escapeAttribute(
                        targetType
                    )}"
                    data-target-id="${escapeAttribute(
                        item.id
                    )}"
                    style="
                        padding:7px 10px;
                        border:0;
                        border-radius:8px;
                        background:#0f766e;
                        color:white;
                        cursor:pointer;
                        font-weight:700;
                    "
                >
                    Confirm Accurate
                </button>

                <div
                    style="
                        margin-top:5px;
                        font-size:12px;
                        color:#64748b;
                    "
                >
                    ${countLabel}
                </div>
                ${freshnessMarkup}
            </div>
        `;
    };

    section?.addEventListener(
        'change',
        (event) => {
            const input = event.target;

            if (
                ! (
                    input
                    instanceof HTMLInputElement
                )
                || ! input.matches(
                    '[data-landmark-photo-input]'
                )
            ) {
                return;
            }

            const filenameElement =
                input
                    .closest('form')
                    ?.querySelector(
                        '[data-landmark-photo-filename]'
                    );

            if (! filenameElement) {
                return;
            }

            filenameElement.textContent =
                input.files?.[0]?.name
                || 'No photo selected';
        }
    );

    const createLandmarkPopup = (landmark) => {
        const name = escapeHtml(
            landmark.name || 'Venue landmark'
        );
        const typeLabel = escapeHtml(
            landmark.type_label || 'Venue landmark'
        );
        const description = landmark.description
            ? `
                <div
                    style="
                        margin-top:6px;
                        color:#475569;
                    "
                >
                    ${escapeHtml(landmark.description)}
                </div>
            `
            : '';

        const landmarkPhoto = landmark.photo_url
            ? `
                <a
                    href="${escapeHtml(landmark.photo_url)}"
                    target="_blank"
                    rel="noopener noreferrer"
                    style="display:block;margin-top:8px"
                >
                    <img
                        src="${escapeHtml(
                            landmark.photo_thumbnail_url
                            || landmark.photo_url
                        )}"
                        alt="${name}"
                        style="
                            display:block;
                            width:100%;
                            max-height:150px;
                            object-fit:cover;
                            border-radius:10px;
                        "
                    >
                </a>
            `
            : '';

        const photoReportAction = (
            viewerCheckedIn
            && landmark.photo_url
            && landmark.photo_report_url
        )
            ? `
                <a
                    href="${escapeAttribute(
                        landmark.photo_report_url
                    )}"
                    style="
                        display:inline-block;
                        margin-top:6px;
                        font-size:11px;
                        font-weight:700;
                        color:#a21caf;
                        text-decoration:none;
                    "
                >
                    Report outdated or incorrect photo
                </a>
            `
            : '';

        const navigationAction =
            viewerCheckedIn
            && Number.isFinite(
                Number.parseFloat(
                    landmark.latitude
                )
            )
            && Number.isFinite(
                Number.parseFloat(
                    landmark.longitude
                )
            )
                ? `
                    <button
                        type="button"
                        data-landmark-navigate
                        data-landmark-id="${escapeHtml(
                            String(landmark.id)
                        )}"
                        style="
                            display:block;
                            width:100%;
                            margin-top:10px;
                            border:0;
                            border-radius:9px;
                            background:#2563eb;
                            color:white;
                            padding:9px 12px;
                            font-size:12px;
                            font-weight:800;
                            cursor:pointer;
                        "
                    >
                        🧭 Go Here
                    </button>

                    <div
                        style="
                            margin-top:5px;
                            font-size:11px;
                            line-height:1.4;
                            color:#64748b;
                        "
                    >
                        Direct GPS guidance. Follow marked festival paths and venue rules.
                    </div>

                    ${
                        landmark.navigation_url
                            ? `
                                <button
                                    type="button"
                                    data-landmark-mapped-route
                                    data-landmark-id="${escapeHtml(
                                        String(landmark.id)
                                    )}"
                                    style="
                                        display:inline-block;
                                        margin-top:7px;
                                        border:0;
                                        background:transparent;
                                        color:#475569;
                                        padding:0;
                                        font-size:11px;
                                        font-weight:700;
                                        cursor:pointer;
                                        text-decoration:underline;
                                    "
                                >
                                    Mapped Route (Advanced)
                                </button>
                            `
                            : ''
                    }
                `
                : '';

        const photoUpload = (
            viewerCheckedIn
            && landmark.photo_upload_url
        )
            ? `
                <form
                    method="POST"
                    action="${escapeHtml(
                        landmark.photo_upload_url
                    )}"
                    enctype="multipart/form-data"
                    style="
                        margin-top:10px;
                        padding-top:10px;
                        border-top:1px solid #e4e4e7;
                    "
                >
                    <input
                        type="hidden"
                        name="_token"
                        value="${escapeHtml(
                            document.querySelector(
                                'meta[name="csrf-token"]'
                            )?.content || ''
                        )}"
                    >
                    <label
                        style="
                            display:block;
                            font-size:12px;
                            font-weight:700;
                            color:#475569;
                        "
                    >
                        Help identify this landmark
                    </label>
                    <input
                        id="landmark-photo-${escapeHtml(
                            String(landmark.id)
                        )}"
                        type="file"
                        name="photo"
                        accept="image/jpeg,image/png,image/webp"
                        required
                        data-landmark-photo-input
                        style="
                            position:absolute;
                            width:1px;
                            height:1px;
                            opacity:0;
                            overflow:hidden;
                            pointer-events:none;
                        "
                    >
                    <label
                        for="landmark-photo-${escapeHtml(
                            String(landmark.id)
                        )}"
                        style="
                            display:inline-flex;
                            align-items:center;
                            justify-content:center;
                            margin-top:7px;
                            border:1px solid #0891b2;
                            border-radius:8px;
                            background:white;
                            color:#0e7490;
                            padding:7px 10px;
                            font-size:12px;
                            font-weight:700;
                            cursor:pointer;
                        "
                    >
                        Choose Photo
                    </label>
                    <div
                        data-landmark-photo-filename
                        style="
                            margin-top:5px;
                            font-size:11px;
                            color:#64748b;
                            word-break:break-word;
                        "
                    >
                        No photo selected
                    </div>
                    <button
                        type="submit"
                        style="
                            display:block;
                            margin-top:7px;
                            border:0;
                            border-radius:8px;
                            background:#0e7490;
                            color:white;
                            padding:7px 10px;
                            font-size:12px;
                            font-weight:700;
                            cursor:pointer;
                        "
                    >
                        Submit Photo
                    </button>
                    <div
                        style="
                            margin-top:5px;
                            font-size:11px;
                            color:#64748b;
                        "
                    >
                        Organizer approval is required before it appears publicly.
                    </div>
                </form>
            `
            : '';

        return `
            <div style="min-width:220px;max-width:280px">
                <strong>${name}</strong>
                <div
                    style="
                        margin-top:4px;
                        font-size:12px;
                        font-weight:700;
                        color:#0f766e;
                    "
                >
                    ${typeLabel}
                </div>
                ${description}
                ${landmarkPhoto}
                ${photoReportAction}
                ${navigationAction}
                ${confirmationMarkup(
                    'landmark',
                    landmark
                )}
                ${photoUpload}
            </div>
        `;
    };

    const renderLandmarks = (
        venueLandmarks
    ) => {
        venueLandmarks.forEach(
            (landmark) => {
                const latitude = Number.parseFloat(
                    landmark.latitude
                );
                const longitude = Number.parseFloat(
                    landmark.longitude
                );

                if (
                    !Number.isFinite(latitude)
                    || !Number.isFinite(longitude)
                ) {
                    return;
                }

                const marker = L.marker(
                    [latitude, longitude],
                    {
                        icon: createLandmarkIcon(
                            landmark
                        ),
                        zIndexOffset: 50,
                    }
                )
                    .addTo(landmarkLayer)
                    .bindPopup(
                        createLandmarkPopup(
                            landmark
                        )
                    );

                landmarkMarkers.set(
                    String(landmark.id),
                    marker
                );
            }
        );
    };

    const crowdSignalMarkers = new Map();

    const renderCrowdSignals = (signals) => {
        signals.forEach((signal) => {
            const latitude = Number.parseFloat(signal.latitude);
            const longitude = Number.parseFloat(signal.longitude);

            if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
                return;
            }

            const contributors = Number.parseInt(signal.contributor_count, 10) || 0;
            const support = Number.parseInt(signal.participation?.support_count, 10) || 0;
            const dispute = Number.parseInt(signal.participation?.dispute_count, 10) || 0;
            const stateLabel = escapeHtml(signal.participation?.state?.label || 'No crowd vote yet');
            const strengthLabel = escapeHtml(signal.signal_strength?.label || 'Crowd signal');
            const landmarkType = escapeHtml(signal.landmark_type || 'landmark');
            const photoCount = Number.parseInt(signal.evidence?.photo_count, 10) || 0;
            const qualityLabel = escapeHtml(signal.evidence_quality?.label || 'Evidence developing');
            const qualityScore = Number.parseInt(signal.evidence_quality?.score, 10) || 0;
            const nextEvidenceLabel = escapeHtml(signal.evidence_quality?.next_contribution?.label || 'Add stronger evidence');
            const recognitionLabel = escapeHtml(signal.evidence_quality?.recognition?.label || 'Crowd evidence');
            const activityLabel = escapeHtml(signal.activity?.label || 'Established crowd signal');
            const recentContributorCount = Number.parseInt(signal.activity?.recent_contributor_count, 10) || 0;
            const mission = signal.mission || null;
            const missionTitle = mission ? escapeHtml(mission.title || 'Mapping mission') : '';
            const missionEmoji = mission ? escapeHtml(mission.emoji || '📍') : '';
            const missionPriority = mission ? escapeHtml(mission.priority_label || 'Useful next step') : '';
            const communityUrl = escapeHtml(signal.links?.community_web || '#');
            const contributeUrl = escapeHtml(signal.links?.contribute_web || '#');
            const net = support - dispute;
            const momentum = net > 0 ? `+${net}` : String(net);

            const icon = L.divIcon({
                className: '',
                html: `
                    <div style="position:relative;display:flex;align-items:center;justify-content:center;width:40px;height:40px;border-radius:50%;border:3px solid #fff;background:#0891b2;color:#fff;font-size:14px;font-weight:900;box-shadow:0 7px 20px rgba(8,145,178,.38)" aria-hidden="true">
                        ${contributors}
                        <span style="position:absolute;right:-7px;bottom:-5px;min-width:22px;border-radius:999px;background:${net < 0 ? '#b45309' : '#047857'};padding:2px 5px;font-size:9px;line-height:14px">${momentum}</span>
                    </div>
                `,
                iconSize: [40, 40],
                iconAnchor: [20, 20],
                popupAnchor: [0, -21],
            });

            const marker = L.marker([latitude, longitude], { icon, zIndexOffset: 45 })
                .addTo(communityLayer)
                .bindPopup(`
                    <div style="min-width:230px;max-width:300px">
                        <div style="font-size:11px;font-weight:900;text-transform:uppercase;letter-spacing:.08em;color:#0e7490">${strengthLabel} · approximate area</div>
                        <strong style="display:block;margin-top:5px">${contributors} people marked something here</strong>
                        <div style="margin-top:4px;font-size:12px;color:#475569">Likely type: ${landmarkType}</div>
                        <div style="margin-top:9px;border-radius:9px;background:#ecfeff;padding:8px;font-size:12px;color:#164e63"><strong>${stateLabel}</strong><br>${support} support · ${dispute} dispute · ${photoCount} evidence photos</div>
                        <div style="margin-top:7px;border-radius:9px;background:#ecfdf5;padding:8px;font-size:11px;color:#065f46"><strong>${activityLabel}</strong>${recentContributorCount >= 2 ? `<br>${recentContributorCount} independent contributors active in this coarse recent window` : ''}</div>
                        <div style="margin-top:7px;border-radius:9px;background:#f5f3ff;padding:8px;font-size:11px;color:#5b21b6"><strong>${qualityLabel} · ${qualityScore}/100</strong><br>${recognitionLabel}<br>Best next evidence: ${nextEvidenceLabel}</div>
                        ${mission ? `<div style="margin-top:7px;border-radius:9px;background:#faf5ff;padding:8px;font-size:11px;color:#6b21a8"><strong>${missionEmoji} ${missionTitle}</strong><br>${missionPriority}</div>` : ''}
                        <div style="display:flex;flex-wrap:wrap;gap:6px;margin-top:10px">
                            <a href="${communityUrl}" style="border-radius:8px;background:#0e7490;padding:7px 9px;color:#fff;font-size:11px;font-weight:900;text-decoration:none">Open crowd evidence</a>
                            <a href="${contributeUrl}" style="border-radius:8px;border:1px solid #67e8f9;padding:7px 9px;color:#155e75;font-size:11px;font-weight:900;text-decoration:none">Add photo-first evidence</a>
                        </div>
                        <div style="margin-top:8px;font-size:10px;line-height:1.45;color:#64748b">Approximate aggregate marker only. Evidence quality describes completeness, not correctness. Use your own fresh GPS observation and photo; organizer approval is still required.</div>
                    </div>
                `);

            crowdSignalMarkers.set(String(signal.signal_key || ''), marker);
        });
    };

    const renderCommunitySuggestions = (groups) => {
        groups.forEach((group) => {
            const latitude = Number.parseFloat(group.latitude);
            const longitude = Number.parseFloat(group.longitude);

            if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
                return;
            }

            const signals = Number.parseInt(group.positive_signals, 10) || 0;
            const disputes = Number.parseInt(group.dispute_count, 10) || 0;
            const label = escapeHtml(group.name || 'Community landmark suggestion');
            const type = escapeHtml(group.landmark_type_label || 'Landmark');

            const icon = L.divIcon({
                className: '',
                html: `
                    <div style="display:flex;align-items:center;justify-content:center;width:34px;height:34px;border-radius:50%;border:2px dashed #fff;background:#d97706;color:#fff;font-size:13px;font-weight:900;box-shadow:0 6px 18px rgba(0,0,0,.35)" aria-hidden="true">
                        ${signals}
                    </div>
                `,
                iconSize: [34, 34],
                iconAnchor: [17, 17],
                popupAnchor: [0, -18],
            });

            L.marker([latitude, longitude], { icon, zIndexOffset: 40 })
                .addTo(communityLayer)
                .bindPopup(`
                    <div style="min-width:210px;max-width:270px">
                        <div style="font-size:11px;font-weight:900;text-transform:uppercase;letter-spacing:.08em;color:#b45309">Community suggestion · pending review</div>
                        <strong style="display:block;margin-top:4px">${label}</strong>
                        <div style="margin-top:3px;font-size:12px;color:#475569">${type}</div>
                        <div style="margin-top:8px;font-size:12px"><strong>${signals}</strong> positive signals · <strong>${disputes}</strong> disputes</div>
                        <div style="margin-top:7px;font-size:11px;color:#64748b">Coordinates are clustered and attendee identities are not shown.</div>
                    </div>
                `);
        });
    };

    const personalPinPopup = (pin) => {
        const label=escapeHtml(
            pin.label || 'Private map pin'
        );
        const note=pin.note
            ? `<div style="margin-top:6px;color:#475569">${escapeHtml(pin.note)}</div>`
            : '';
        const promoted=Boolean(pin.promoted);
        const placementWarning=
            pin.placement?.warning
                ? `
                    <div style="margin-top:8px;border-radius:8px;background:#fff7ed;padding:7px 8px;font-size:11px;font-weight:800;color:#9a3412">
                        ${escapeHtml(pin.placement.warning)}
                    </div>
                `
                : '';
        const retentionContext=
            pin.expires_on
                ? `
                    <div style="margin-top:7px;font-size:11px;color:#71717a">
                        Private coordinate cleanup: ${escapeHtml(pin.expires_on)}
                    </div>
                `
                : '';
        const sharing=pin.community_sharing || {};
        const sharingGuidance=sharing.guidance
            ? `<div style="margin-top:8px;border-radius:8px;background:${sharing.recommended ? '#ecfdf5' : '#faf5ff'};padding:7px 8px;font-size:11px;font-weight:800;color:${sharing.recommended ? '#047857' : '#86198f'}">${escapeHtml(sharing.guidance)}</div>`
            : '';
        const promoteAction=pin.links?.promote
            ? `
                <button
                    type="button"
                    data-personal-pin-promote="${escapeHtml(String(pin.id))}"
                    data-personal-pin-promote-url="${escapeHtml(pin.links.promote)}"
                    data-personal-pin-promotion-preview-url="${escapeHtml(pin.links.promotion_preview || '')}"
                    style="border:1px solid #c026d3;border-radius:8px;background:#fff;color:#a21caf;padding:6px 8px;font-size:11px;font-weight:900"
                    ${promoted ? 'disabled' : ''}
                >
                    ${promoted ? 'Already Shared' : 'Suggest to Community'}
                </button>
            `
            : '';
        const editAction=pin.links?.update
            ? `
                <button
                    type="button"
                    data-personal-pin-edit="${escapeHtml(String(pin.id))}"
                    data-personal-pin-update-url="${escapeHtml(pin.links.update)}"
                    style="border:1px solid #d4d4d8;border-radius:8px;background:#fff;color:#3f3f46;padding:6px 8px;font-size:11px;font-weight:900"
                >
                    Edit
                </button>
                <button
                    type="button"
                    data-personal-pin-move="${escapeHtml(String(pin.id))}"
                    data-personal-pin-update-url="${escapeHtml(pin.links.update)}"
                    style="border:1px solid #d4d4d8;border-radius:8px;background:#fff;color:#3f3f46;padding:6px 8px;font-size:11px;font-weight:900"
                >
                    Move
                </button>
            `
            : '';
        const deleteAction=pin.links?.delete
            ? `
                <button
                    type="button"
                    data-personal-pin-delete="${escapeHtml(String(pin.id))}"
                    data-personal-pin-delete-url="${escapeHtml(pin.links.delete)}"
                    style="border:1px solid #d4d4d8;border-radius:8px;background:#fff;color:#52525b;padding:6px 8px;font-size:11px;font-weight:900"
                >
                    Delete
                </button>
            `
            : '';

        return `
            <div style="min-width:210px;max-width:280px">
                <div style="font-size:11px;font-weight:900;text-transform:uppercase;letter-spacing:.08em;color:#a21caf">
                    ${escapeHtml(pin.emoji || '📌')} Private to you
                </div>
                <strong style="display:block;margin-top:4px">${label}</strong>
                <div style="margin-top:3px;font-size:12px;color:#64748b">${escapeHtml(pin.pin_type_label || 'Private spot')}</div>
                ${note}
                <div style="margin-top:8px;font-size:11px;color:#64748b">This exact location is not included in community aggregates unless you explicitly submit it for review.</div>
                ${placementWarning}
                ${retentionContext}
                ${sharingGuidance}
                <div style="display:flex;gap:6px;flex-wrap:wrap;margin-top:10px">
                    ${promoteAction}
                    ${editAction}
                    ${deleteAction}
                </div>
            </div>
        `;
    };

    const renderPersonalPin = (pin) => {
        const latitude=Number.parseFloat(pin.latitude);
        const longitude=Number.parseFloat(pin.longitude);

        if (
            !Number.isFinite(latitude)
            || !Number.isFinite(longitude)
        ) {
            return null;
        }

        const icon=L.divIcon({
            className: '',
            html: `
                <div style="display:flex;align-items:center;justify-content:center;width:34px;height:34px;border-radius:50%;border:2px solid #fff;background:#a21caf;color:#fff;font-size:17px;box-shadow:0 6px 18px rgba(0,0,0,.32)" aria-hidden="true">
                    ${escapeHtml(pin.emoji || '📌')}
                </div>
            `,
            iconSize: [34,34],
            iconAnchor: [17,17],
            popupAnchor: [0,-18],
        });

        const marker=L.marker(
            [latitude,longitude],
            {
                icon,
                zIndexOffset: 120,
                draggable: false,
            }
        )
            .addTo(personalPinLayer)
            .bindPopup(
                personalPinPopup(pin)
            );

        marker.on(
            'dragend',
            async () => {
                marker.dragging?.disable();

                const url=pin.links?.update;
                const position=marker.getLatLng();

                if (!url) {
                    return;
                }

                try {
                    const response=await fetch(
                        url,
                        {
                            method:'PATCH',
                            headers:{
                                'Accept':'application/json',
                                'Content-Type':'application/json',
                                'X-CSRF-TOKEN':csrfToken(),
                            },
                            body:JSON.stringify({
                                latitude:position.lat,
                                longitude:position.lng,
                            }),
                        }
                    );

                    const payload=await response.json();

                    if (!response.ok) {
                        throw new Error(
                            payload?.message
                            || 'The private pin could not be moved.'
                        );
                    }

                    const index=personalPins.findIndex(
                        (item) =>
                            String(item.id)
                            === String(pin.id)
                    );

                    if (index >= 0) {
                        personalPins[index]={
                            ...personalPins[index],
                            ...payload.data,
                        };
                    }

                    marker.setPopupContent(
                        personalPinPopup(
                            payload.data || pin
                        )
                    );
                    marker.openPopup();
                } catch (error) {
                    marker.setLatLng(
                        [pin.latitude,pin.longitude]
                    );
                    window.alert(
                        error?.message
                        || 'The private pin could not be moved.'
                    );
                }
            }
        );

        personalPinMarkers.set(
            String(pin.id),
            marker
        );

        return marker;
    };

    const renderPersonalPins = (pins) => {
        personalPinLayer.clearLayers();
        personalPinMarkers.clear();

        pins.forEach((pin) => {
            renderPersonalPin(pin);
        });
    };


    const personalPinMatchesManagerFilter = (
        pin,
        filter
    ) => {
        if (!filter || filter === 'all') {
            return true;
        }

        if (filter === 'promoted') {
            return Boolean(pin.promoted);
        }

        if (filter === 'private_only') {
            return !pin.promoted;
        }

        return pin.pin_type === filter;
    };

    const renderPersonalPinManager = () => {
        if (!personalPinManagerList) {
            return;
        }

        const filter=
            personalPinManagerFilter?.value
            || 'all';

        const visible=personalPins.filter(
            (pin) =>
                personalPinMatchesManagerFilter(
                    pin,
                    filter
                )
        );

        personalPinManagerList.innerHTML=
            visible
                .map((pin) => {
                    const outside=
                        pin.placement?.inside_live_map_radius
                        === false;

                    return `
                        <button
                            type="button"
                            data-personal-pin-manager-focus="${escapeHtml(String(pin.id))}"
                            style="display:flex;width:100%;align-items:center;justify-content:space-between;gap:10px;border:1px solid #e4e4e7;border-radius:12px;background:#fff;padding:9px 10px;text-align:left"
                        >
                            <span style="min-width:0">
                                <span style="display:block;font-size:12px;font-weight:900;color:#18181b;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">
                                    ${escapeHtml(pin.emoji || '📌')} ${escapeHtml(pin.label || 'Private pin')}
                                </span>
                                <span style="display:block;margin-top:2px;font-size:10px;color:#71717a">
                                    ${escapeHtml(pin.pin_type_label || 'Private spot')}
                                    ${pin.promoted ? ' · promoted' : ''}
                                    ${outside ? ' · outside normal map radius' : ''}
                                </span>
                            </span>
                            <span style="font-size:11px;font-weight:900;color:#a21caf;white-space:nowrap">
                                Show on map
                            </span>
                        </button>
                    `;
                })
                .join('');

        personalPinManagerEmpty?.classList.toggle(
            'hidden',
            visible.length > 0
        );
    };

    const resetPersonalPinPromotionPanel = () => {
        pendingPersonalPinPromotion = null;
        personalPinPromotionPanel?.classList.add('hidden');
        personalPinPromotionMatch?.classList.add('hidden');
        personalPinPromotionDuplicateWrap?.classList.add('hidden');
        personalPinPromotionResult?.classList.add('hidden');
        personalPinPromotionActions?.classList.remove('hidden');

        if (personalPinPromotionPhoto) {
            personalPinPromotionPhoto.value = '';
        }

        if (personalPinPromotionDuplicateAck) {
            personalPinPromotionDuplicateAck.checked = false;
        }

        if (personalPinPromotionError) {
            personalPinPromotionError.textContent = '';
            personalPinPromotionError.classList.add('hidden');
        }
    };

    const showPersonalPinPromotionError = (message = '') => {
        if (!personalPinPromotionError) {
            return;
        }

        personalPinPromotionError.textContent = message;
        personalPinPromotionError.classList.toggle(
            'hidden',
            !message
        );
    };

    const openPersonalPinPromotionPanel = (
        pin,
        url,
        preview
    ) => {
        if (!personalPinPromotionPanel) {
            return false;
        }

        const guide = preview?.promotion_guide || {};
        const recommendation = guide.recommendation || {};
        const matchingSignal = guide.matching_signal || null;
        const duplicate = preview?.duplicate_check || {};

        pendingPersonalPinPromotion = {
            pin,
            url,
            preview,
        };

        personalPinPromotionTitle.textContent =
            recommendation.label
            || 'Share this place carefully';

        personalPinPromotionGuidance.textContent =
            recommendation.guidance
            || 'FestFriends checked nearby pending evidence before anything is shared.';

        if (matchingSignal) {
            personalPinPromotionMatch?.classList.remove('hidden');

            if (personalPinPromotionMatchTitle) {
                personalPinPromotionMatchTitle.textContent =
                    matchingSignal.viewer_already_contributed
                        ? 'You already contributed in this crowd area'
                        : 'Nearby crowd evidence found';
            }

            if (personalPinPromotionMatchCopy) {
                const distance = Number.isFinite(
                    Number(matchingSignal.approximate_distance_meters)
                )
                    ? ` · about ${Number(matchingSignal.approximate_distance_meters)} m away`
                    : '';
                const quality = matchingSignal.evidence_quality?.label
                    ? ` · ${matchingSignal.evidence_quality.label}`
                    : '';

                personalPinPromotionMatchCopy.textContent =
                    `${matchingSignal.label || 'Crowd map signal'}${distance}${quality}.`;
            }

            if (personalPinPromotionMatchLink) {
                personalPinPromotionMatchLink.href =
                    matchingSignal.links?.community_web
                    || '#';
            }
        } else {
            personalPinPromotionMatch?.classList.add('hidden');
        }

        if (personalPinPromotionPhotoGuidance) {
            personalPinPromotionPhotoGuidance.textContent =
                guide.photo_evidence?.guidance
                || 'A current photo can make the observation easier to validate. It is attached to the pending suggestion—not saved onto your private pin.';
        }

        const needsDuplicateAcknowledgement =
            Boolean(duplicate?.has_similar)
            || Boolean(matchingSignal);

        personalPinPromotionDuplicateWrap?.classList.toggle(
            'hidden',
            !needsDuplicateAcknowledgement
        );

        if (personalPinPromotionDuplicateAck) {
            personalPinPromotionDuplicateAck.checked = false;
        }

        personalPinPromotionResult?.classList.add('hidden');
        personalPinPromotionActions?.classList.remove('hidden');
        showPersonalPinPromotionError('');
        personalPinPromotionPanel.classList.remove('hidden');
        personalPinPromotionPanel.scrollIntoView({
            behavior:'smooth',
            block:'nearest',
        });

        return true;
    };

    const focusPersonalPin = (id) => {
        const marker=
            personalPinMarkers.get(String(id));

        if (!marker) {
            return false;
        }

        setLayerVisibility(
            'personal',
            true,
            true
        );

        const position=marker.getLatLng();

        map.setView(
            position,
            Math.max(
                map.getZoom(),
                17
            ),
            {
                animate:true,
            }
        );

        marker.openPopup();

        return true;
    };

    const createZonePopup = (zone) => {
        const name = escapeHtml(
            zone.name || 'Venue zone'
        );
        const typeLabel = escapeHtml(
            zone.type_label || 'Venue zone'
        );
        const description = zone.description
            ? `
                <div
                    style="
                        margin-top:6px;
                        color:#475569;
                    "
                >
                    ${escapeHtml(zone.description)}
                </div>
            `
            : '';

        return `
            <div style="min-width:190px">
                <strong>${name}</strong>
                <div
                    style="
                        margin-top:4px;
                        font-size:12px;
                        font-weight:700;
                        color:#0f766e;
                    "
                >
                    ${typeLabel}
                </div>
                ${description}
                ${confirmationMarkup(
                    'zone',
                    zone
                )}
            </div>
        `;
    };

    const renderZones = (
        venueZones
    ) => {
        venueZones.forEach(
            (zone) => {
                const points = Array.isArray(
                    zone.boundary_points
                )
                    ? zone.boundary_points
                    : [];

                const latLngs = points
                    .map((point) => {
                        const latitude =
                            Number.parseFloat(
                                point.latitude
                            );
                        const longitude =
                            Number.parseFloat(
                                point.longitude
                            );

                        if (
                            !Number.isFinite(latitude)
                            || !Number.isFinite(longitude)
                        ) {
                            return null;
                        }

                        return [
                            latitude,
                            longitude,
                        ];
                    })
                    .filter(Boolean);

                if (latLngs.length < 3) {
                    return;
                }

                const polygon = L.polygon(
                    latLngs,
                    {
                        color: '#0f766e',
                        weight: 2,
                        fillColor: '#14b8a6',
                        fillOpacity: 0.16,
                    }
                )
                    .addTo(zoneLayer)
                    .bindPopup(
                        createZonePopup(zone)
                    )
                    .bindTooltip(
                        escapeHtml(
                            zone.name || 'Venue zone'
                        ),
                        {
                            sticky: true,
                        }
                    );

                zonePolygons.set(
                    String(zone.id),
                    polygon
                );
            }
        );
    };

    const createPathPopup = (
        path
    ) => {
        const name = escapeHtml(
            path.name || 'Venue path'
        );

        const typeLabel = escapeHtml(
            path.type_label || 'Venue path'
        );

        const description = path.description
            ? `
                <div
                    style="
                        margin-top:6px;
                        color:#475569;
                    "
                >
                    ${escapeHtml(path.description)}
                </div>
            `
            : '';

        return `
            <div style="min-width:190px">
                <strong>${name}</strong>
                <div
                    style="
                        margin-top:4px;
                        font-size:12px;
                        font-weight:700;
                        color:#1d4ed8;
                    "
                >
                    ${typeLabel}
                </div>
                <div
                    style="
                        margin-top:4px;
                        font-size:11px;
                        color:#64748b;
                    "
                >
                    ${path.is_bidirectional
                        ? 'Two-way route'
                        : 'One-way route'}
                </div>
                ${description}
                ${confirmationMarkup(
                    'path',
                    path
                )}
            </div>
        `;
    };

    const renderPaths = (
        venuePaths
    ) => {
        venuePaths.forEach(
            (path) => {
                const points = Array.isArray(
                    path.path_points
                )
                    ? path.path_points
                    : [];

                const latLngs = points
                    .map((point) => {
                        const latitude =
                            Number.parseFloat(
                                point.latitude
                            );

                        const longitude =
                            Number.parseFloat(
                                point.longitude
                            );

                        if (
                            !Number.isFinite(latitude)
                            || !Number.isFinite(longitude)
                        ) {
                            return null;
                        }

                        return [
                            latitude,
                            longitude,
                        ];
                    })
                    .filter(Boolean);

                if (latLngs.length < 2) {
                    return;
                }

                const line = L.polyline(
                    latLngs,
                    {
                        color: '#2563eb',
                        weight: 5,
                        opacity: 0.82,
                        dashArray:
                            path.type === 'restricted'
                                ? '8 8'
                                : null,
                    }
                )
                    .addTo(pathLayer)
                    .bindPopup(
                        createPathPopup(path)
                    )
                    .bindTooltip(
                        escapeHtml(
                            path.name || 'Venue path'
                        ),
                        {
                            sticky: true,
                        }
                    );

                pathPolylines.set(
                    String(path.id),
                    line
                );
            }
        );
    };

    const createJunctionIcon = (
        junction
    ) => {
        const symbol = junction.type
            === 'bridge'
                ? '🌉'
                : junction.type === 'gate'
                    ? '🚪'
                    : junction.type === 'shuttle_stop'
                        ? '🚌'
                        : junction.type === 'hazard'
                            ? '⚠'
                            : '◆';

        return L.divIcon({
            className: '',
            html: `
                <div
                    style="
                        display:flex;
                        align-items:center;
                        justify-content:center;
                        width:32px;
                        height:32px;
                        border-radius:9999px;
                        border:3px solid white;
                        background:#4f46e5;
                        color:white;
                        font-size:15px;
                        box-shadow:0 6px 18px rgba(0,0,0,.35);
                    "
                >
                    ${symbol}
                </div>
            `,
            iconSize: [32, 32],
            iconAnchor: [16, 16],
            popupAnchor: [0, -18],
        });
    };

    const createJunctionPopup = (
        junction
    ) => {
        const name = escapeHtml(
            junction.name
            || 'Path junction'
        );

        const typeLabel = escapeHtml(
            junction.type_label
            || 'Navigation point'
        );

        const description =
            junction.description
                ? `
                    <div
                        style="
                            margin-top:6px;
                            color:#475569;
                        "
                    >
                        ${escapeHtml(
                            junction.description
                        )}
                    </div>
                `
                : '';

        const connectedPaths =
            Array.isArray(
                junction.connected_paths
            )
            && junction.connected_paths.length > 0
                ? `
                    <div
                        style="
                            margin-top:8px;
                            padding-top:8px;
                            border-top:1px solid #e4e4e7;
                            font-size:11px;
                            color:#52525b;
                        "
                    >
                        <strong>
                            Connected paths:
                        </strong>

                        ${junction.connected_paths
                            .map(
                                (path) =>
                                    escapeHtml(
                                        path.name
                                        || 'Venue path'
                                    )
                            )
                            .join(', ')}
                    </div>
                `
                : `
                    <div
                        style="
                            margin-top:8px;
                            font-size:11px;
                            color:#a16207;
                        "
                    >
                        Not connected to a Venue path yet
                    </div>
                `;

        return `
            <div style="min-width:190px">
                <strong>${name}</strong>
                <div
                    style="
                        margin-top:4px;
                        font-size:12px;
                        font-weight:700;
                        color:#4338ca;
                    "
                >
                    ${typeLabel}
                </div>
                ${description}
                ${connectedPaths}
                ${confirmationMarkup(
                    'junction',
                    junction
                )}
            </div>
        `;
    };

    const renderPathJunctions = (
        junctions
    ) => {
        junctions.forEach(
            (junction) => {
                const latitude =
                    Number.parseFloat(
                        junction.latitude
                    );

                const longitude =
                    Number.parseFloat(
                        junction.longitude
                    );

                if (
                    !Number.isFinite(latitude)
                    || !Number.isFinite(longitude)
                ) {
                    return;
                }

                const marker = L.marker(
                    [
                        latitude,
                        longitude,
                    ],
                    {
                        icon:
                            createJunctionIcon(
                                junction
                            ),
                        zIndexOffset: 50,
                    }
                )
                    .addTo(junctionLayer)
                    .bindPopup(
                        createJunctionPopup(
                            junction
                        )
                    )
                    .bindTooltip(
                        escapeHtml(
                            junction.name
                            || 'Path junction'
                        ),
                        {
                            sticky: true,
                        }
                    );

                junctionMarkers.set(
                    String(junction.id),
                    marker
                );
            }
        );
    };

    const meetupCoordinates = (
        meetup,
        index
    ) => {
        const latitude = Number.parseFloat(
            meetup.latitude
        );

        const longitude = Number.parseFloat(
            meetup.longitude
        );

        if (
            ! Number.isFinite(latitude)
            || ! Number.isFinite(longitude)
        ) {
            return null;
        }

        if (meetup.has_exact_coordinates) {
            return [
                latitude,
                longitude,
            ];
        }

        /*
         * Meetups without exact GPS coordinates currently use
         * the festival location. Spread them around the festival
         * marker so the markers remain separately clickable.
         */
        const angle =
            (
                index * 137.508
            ) * Math.PI / 180;

        const ring =
            Math.floor(index / 8);

        const distance =
            0.00022
            + ring * 0.00008;

        const longitudeScale = Math.max(
            Math.cos(
                festivalLatitude
                * Math.PI
                / 180
            ),
            0.2
        );

        return [
            latitude
            + Math.cos(angle) * distance,

            longitude
            + (
                Math.sin(angle)
                * distance
                / longitudeScale
            ),
        ];
    };

    const renderMeetups = (
        festivalMeetups
    ) => {
        festivalMeetups.forEach(
            (
                meetup,
                index
            ) => {
                const coordinates =
                    meetupCoordinates(
                        meetup,
                        index
                    );

                if (! coordinates) {
                    return;
                }

                const meetupId = String(
                    meetup.id
                );

                const marker = L.marker(
                    coordinates,
                    {
                        icon: createMeetupIcon(),
                        zIndexOffset: 75,
                    }
                )
                    .addTo(meetupLayer)
                    .bindPopup(
                        createMeetupPopup(meetup)
                    );


                meetupMarkers.set(
                    meetupId,
                    marker
                );
            }
        );
    };

    const mapIsUsable = () => (
        generation === festivalLiveMapGeneration
        && canvas.isConnected
        && map.getContainer()?.isConnected
        && map.getPane('mapPane')
    );

    const fitMapToVisibleMarkers = () => {
        if (! mapIsUsable()) {
            return;
        }

        if (navigationPolyline) {
            return;
        }

        const visiblePoints = [
            L.latLng(
                festivalLatitude,
                festivalLongitude
            ),
        ];

        if (layerVisibility.people) {
            memberMarkers.forEach((marker) => {
                visiblePoints.push(
                    marker.getLatLng()
                );
            });
        }

        if (layerVisibility.meetups) {
            meetupMarkers.forEach((marker) => {
                visiblePoints.push(
                    marker.getLatLng()
                );
            });
        }

        if (layerVisibility.landmarks) {
            landmarkMarkers.forEach((marker) => {
                visiblePoints.push(
                    marker.getLatLng()
                );
            });
        }

        if (layerVisibility.personal) {
            personalPinMarkers.forEach(
                (marker) => {
                    visiblePoints.push(
                        marker.getLatLng()
                    );
                }
            );
        }

        if (layerVisibility.zones) {
            zonePolygons.forEach((polygon) => {
                polygon
                    .getLatLngs()
                    .flat(Infinity)
                    .forEach((point) => {
                        if (
                            point
                            && Number.isFinite(point.lat)
                            && Number.isFinite(point.lng)
                        ) {
                            visiblePoints.push(point);
                        }
                    });
            });
        }

        if (layerVisibility.paths) {
            pathPolylines.forEach(
                (line) => {
                    line
                        .getLatLngs()
                        .flat(Infinity)
                        .forEach((point) => {
                            if (
                                point
                                && Number.isFinite(point.lat)
                                && Number.isFinite(point.lng)
                            ) {
                                visiblePoints.push(
                                    point
                                );
                            }
                        });
                }
            );
        }

        if (layerVisibility.junctions) {
            junctionMarkers.forEach(
                (marker) => {
                    visiblePoints.push(
                        marker.getLatLng()
                    );
                }
            );
        }

        if (visiblePoints.length === 1) {
            return;
        }

        map.fitBounds(
            L.latLngBounds(visiblePoints),
            {
                padding: [70, 70],
                maxZoom: 16,
                animate: false,
            }
        );
    };

    const setLayerVisibility = (
        layerName,
        visible,
        persist = false
    ) => {
        const layers = {
            people: peopleLayer,
            meetups: meetupLayer,
            landmarks: landmarkLayer,
            community: communityLayer,
            personal: personalPinLayer,
            zones: zoneLayer,
            paths: pathLayer,
            junctions: junctionLayer,
        };

        const layer = layers[layerName];

        if (!layer) {
            return;
        }

        layerVisibility[layerName] = visible;

        if (persist) {
            persistLayerPreferences(
                layerVisibility
            );
        }

        if (visible) {
            if (!map.hasLayer(layer)) {
                layer.addTo(map);
            }
        } else if (map.hasLayer(layer)) {
            map.removeLayer(layer);
        }

        layerToggleButtons.forEach((button) => {
            if (
                button.dataset.festivalMapLayerToggle
                !== layerName
            ) {
                return;
            }

            button.setAttribute(
                'aria-pressed',
                visible ? 'true' : 'false'
            );
        });

        fitMapToVisibleMarkers();
    };

    Object.entries(layerVisibility).forEach(
        ([layerName, visible]) => {
            setLayerVisibility(
                layerName,
                visible
            );
        }
    );

    resetLayerPreferencesButton?.addEventListener(
        'click',
        () => {
            const defaults={
                people: true,
                meetups: true,
                landmarks: true,
                community: true,
                personal: true,
                zones: true,
                paths: false,
                junctions: false,
            };

            clearLayerPreferences();

            Object.entries(defaults).forEach(
                ([layerName, visible]) => {
                    setLayerVisibility(
                        layerName,
                        visible
                    );
                }
            );
        },
        {
            signal:
                eventController.signal,
        }
    );

    advancedMapToggle?.addEventListener(
        'click',
        () => {
            if (!advancedMapLayers) {
                return;
            }

            const willShow =
                advancedMapLayers.classList
                    .contains('hidden');

            advancedMapLayers.classList
                .toggle(
                    'hidden',
                    !willShow
                );

            advancedMapToggle.setAttribute(
                'aria-expanded',
                willShow
                    ? 'true'
                    : 'false'
            );

            advancedMapToggle.textContent =
                willShow
                    ? '⚙ Hide Advanced Map'
                    : '⚙ Advanced Map';
        },
        {
            signal:
                eventController.signal,
        }
    );

    layerToggleButtons.forEach((button) => {
        button.addEventListener(
            'click',
            () => {
                const layerName =
                    button.dataset
                        .festivalMapLayerToggle;

                if (
                    !Object.prototype.hasOwnProperty.call(
                        layerVisibility,
                        layerName
                    )
                ) {
                    return;
                }

                setLayerVisibility(
                    layerName,
                    !layerVisibility[layerName],
                    true
                );
            },
            {
                signal: eventController.signal,
            }
        );
    });

    const clearNavigationDisplay = () => {
        navigationLayer.clearLayers();
        navigationPolyline = null;
        navigationStartMarker = null;
        navigationEndMarker = null;

        navigationSummary?.classList.add(
            'hidden'
        );

        if (navigationTitle) {
            navigationTitle.textContent = '';
        }

        if (navigationMeta) {
            navigationMeta.textContent = '';
        }

        if (navigationConfidence) {
            navigationConfidence.textContent = '';
            navigationConfidence.classList.add(
                'hidden'
            );
        }

        if (navigationConfidenceDetails) {
            navigationConfidenceDetails.innerHTML = '';
            navigationConfidenceDetails.classList.add(
                'hidden'
            );
        }

        if (navigationConfidenceToggle) {
            navigationConfidenceToggle.classList.add(
                'hidden'
            );
            navigationConfidenceToggle.textContent =
                'Show confidence details';
        }

        if (navigationNextStep) {
            navigationNextStep.textContent = '';
        }

        if (navigationNextMeta) {
            navigationNextMeta.textContent = '';
        }

        if (navigationSteps) {
            navigationSteps.innerHTML = '';
            navigationSteps.classList.add(
                'hidden'
            );
        }

        if (navigationStepsToggle) {
            navigationStepsToggle.classList.add(
                'hidden'
            );
            navigationStepsToggle.textContent =
                'Show route steps';
        }
    };

    const clearNavigation = () => {
        if (
            navigationWatchId !== null
            && navigator.geolocation
        ) {
            navigator.geolocation.clearWatch(
                navigationWatchId
            );
            navigationWatchId = null;
        }

        activeNavigationRoute = null;
        activeNavigationLandmark = null;
        navigationOffRouteFixCount = 0;
        navigationRerouteInProgress = false;
        lastNavigationRerouteAt = 0;
        navigationArrivalFixCount = 0;
        navigationArrived = false;

        clearNavigationDisplay();
    };

    const formatNavigationDistance = (
        meters
    ) => {
        const value =
            Number.parseFloat(meters);

        if (!Number.isFinite(value)) {
            return '';
        }

        if (value >= 1609.344) {
            return `${
                (value / 1609.344)
                    .toFixed(2)
            } mi`;
        }

        return `${
            Math.round(
                value * 3.28084
            )
        } ft`;
    };

    const renderNavigationInstructions = (
        route
    ) => {
        const instructions =
            Array.isArray(
                route.instructions
            )
                ? route.instructions
                : [];

        const actionable =
            instructions.filter(
                (instruction) =>
                    instruction.type
                    !== 'arrival'
            );

        const next =
            actionable[0]
            || instructions[0]
            || null;

        if (navigationNextStep) {
            navigationNextStep.textContent =
                next?.title || '';
        }

        if (navigationNextMeta) {
            const nextDistance =
                next
                ? formatNavigationDistance(
                    next.distance_meters
                )
                : '';

            navigationNextMeta.textContent =
                [
                    nextDistance,
                    next?.detail || '',
                ]
                    .filter(Boolean)
                    .join(' · ');
        }

        if (
            !navigationSteps
            || !navigationStepsToggle
            || instructions.length === 0
        ) {
            return;
        }

        navigationSteps.innerHTML =
            instructions
                .map(
                    (
                        instruction,
                        index
                    ) => {
                        const distance =
                            formatNavigationDistance(
                                instruction.distance_meters
                            );

                        return `
                            <div
                                style="
                                    display:flex;
                                    gap:10px;
                                    padding:8px 0;
                                    border-bottom:
                                        ${index === instructions.length - 1
                                            ? '0'
                                            : '1px solid rgba(148,163,184,.25)'};
                                "
                            >
                                <div
                                    style="
                                        flex:0 0 auto;
                                        width:24px;
                                        height:24px;
                                        border-radius:9999px;
                                        display:flex;
                                        align-items:center;
                                        justify-content:center;
                                        background:#dbeafe;
                                        color:#1d4ed8;
                                        font-size:11px;
                                        font-weight:800;
                                    "
                                >
                                    ${index + 1}
                                </div>

                                <div style="min-width:0;flex:1">
                                    <div
                                        style="
                                            font-size:12px;
                                            font-weight:800;
                                        "
                                    >
                                        ${escapeHtml(
                                            instruction.title
                                            || 'Continue'
                                        )}
                                    </div>

                                    <div
                                        style="
                                            margin-top:2px;
                                            font-size:11px;
                                            color:#64748b;
                                        "
                                    >
                                        ${escapeHtml(
                                            [
                                                distance,
                                                instruction.detail
                                                || '',
                                            ]
                                                .filter(Boolean)
                                                .join(' · ')
                                        )}
                                    </div>
                                </div>
                            </div>
                        `;
                    }
                )
                .join('');

        navigationStepsToggle.classList.remove(
            'hidden'
        );
    };

    const navigationDistanceMeters = (
        fromLatitude,
        fromLongitude,
        toLatitude,
        toLongitude
    ) => {
        const earthRadius = 6371000;
        const toRadians = (degrees) =>
            degrees * Math.PI / 180;

        const latitudeDelta =
            toRadians(
                toLatitude - fromLatitude
            );
        const longitudeDelta =
            toRadians(
                toLongitude - fromLongitude
            );

        const fromLatitudeRadians =
            toRadians(fromLatitude);
        const toLatitudeRadians =
            toRadians(toLatitude);

        const a =
            Math.sin(latitudeDelta / 2) ** 2
            + Math.cos(fromLatitudeRadians)
            * Math.cos(toLatitudeRadians)
            * Math.sin(longitudeDelta / 2) ** 2;

        return earthRadius * 2 * Math.atan2(
            Math.sqrt(a),
            Math.sqrt(1 - a)
        );
    };

    const navigationBearing = (
        fromLatitude,
        fromLongitude,
        toLatitude,
        toLongitude
    ) => {
        const toRadians = (degrees) =>
            degrees * Math.PI / 180;

        const toDegrees = (radians) =>
            radians * 180 / Math.PI;

        const fromLatitudeRadians =
            toRadians(fromLatitude);

        const toLatitudeRadians =
            toRadians(toLatitude);

        const longitudeDelta =
            toRadians(
                toLongitude
                - fromLongitude
            );

        const y =
            Math.sin(longitudeDelta)
            * Math.cos(
                toLatitudeRadians
            );

        const x =
            Math.cos(
                fromLatitudeRadians
            )
            * Math.sin(
                toLatitudeRadians
            )
            - Math.sin(
                fromLatitudeRadians
            )
            * Math.cos(
                toLatitudeRadians
            )
            * Math.cos(
                longitudeDelta
            );

        return (
            toDegrees(
                Math.atan2(
                    y,
                    x
                )
            )
            + 360
        ) % 360;
    };

    const navigationCardinalDirection = (
        bearing
    ) => {
        if (!Number.isFinite(bearing)) {
            return null;
        }

        const directions = [
            'N',
            'NE',
            'E',
            'SE',
            'S',
            'SW',
            'W',
            'NW',
        ];

        return directions[
            Math.round(
                bearing / 45
            ) % 8
        ];
    };

    const navigationRouteProgress = (
        route,
        latitude,
        longitude
    ) => {
        const geometry =
            Array.isArray(route?.geometry)
                ? route.geometry
                : [];

        if (geometry.length < 2) {
            return null;
        }

        const referenceLatitude =
            latitude * Math.PI / 180;
        const metersPerLatitudeDegree =
            111320;
        const metersPerLongitudeDegree =
            Math.max(
                1,
                111320
                * Math.cos(referenceLatitude)
            );

        const pointToLocal = (point) => ({
            x:
                (Number.parseFloat(
                    point.longitude
                ) - longitude)
                * metersPerLongitudeDegree,
            y:
                (Number.parseFloat(
                    point.latitude
                ) - latitude)
                * metersPerLatitudeDegree,
        });

        let cumulativeDistance = 0;
        let best = null;

        for (
            let index = 0;
            index < geometry.length - 1;
            index++
        ) {
            const start = geometry[index];
            const end = geometry[index + 1];

            const startLatitude =
                Number.parseFloat(
                    start.latitude
                );
            const startLongitude =
                Number.parseFloat(
                    start.longitude
                );
            const endLatitude =
                Number.parseFloat(
                    end.latitude
                );
            const endLongitude =
                Number.parseFloat(
                    end.longitude
                );

            if (
                !Number.isFinite(startLatitude)
                || !Number.isFinite(startLongitude)
                || !Number.isFinite(endLatitude)
                || !Number.isFinite(endLongitude)
            ) {
                continue;
            }

            const segmentDistance =
                navigationDistanceMeters(
                    startLatitude,
                    startLongitude,
                    endLatitude,
                    endLongitude
                );

            const localStart =
                pointToLocal(start);
            const localEnd =
                pointToLocal(end);
            const dx =
                localEnd.x - localStart.x;
            const dy =
                localEnd.y - localStart.y;
            const lengthSquared =
                dx * dx + dy * dy;

            const fraction =
                lengthSquared > 0
                    ? Math.max(
                        0,
                        Math.min(
                            1,
                            -(
                                localStart.x * dx
                                + localStart.y * dy
                            ) / lengthSquared
                        )
                    )
                    : 0;

            const projectedX =
                localStart.x + dx * fraction;
            const projectedY =
                localStart.y + dy * fraction;
            const distanceFromRoute =
                Math.hypot(
                    projectedX,
                    projectedY
                );

            const distanceAlongRoute =
                cumulativeDistance
                + segmentDistance * fraction;

            if (
                !best
                || distanceFromRoute
                    < best.distanceFromRoute
            ) {
                best = {
                    distanceFromRoute,
                    distanceAlongRoute,
                };
            }

            cumulativeDistance +=
                segmentDistance;
        }

        if (!best) {
            return null;
        }

        return {
            ...best,
            routeDistance:
                cumulativeDistance,
            remainingDistance:
                Math.max(
                    0,
                    cumulativeDistance
                    - best.distanceAlongRoute
                ),
        };
    };

    const requestNavigationRoute = async (
        navigationUrl,
        latitude,
        longitude
    ) => {
        const csrfToken =
            document.querySelector(
                'meta[name="csrf-token"]'
            )?.content;

        const response = await fetch(
            navigationUrl,
            {
                method: 'POST',
                credentials: 'same-origin',
                headers: {
                    Accept: 'application/json',
                    'Content-Type':
                        'application/json',
                    'X-Requested-With':
                        'XMLHttpRequest',
                    ...(csrfToken
                        ? {
                            'X-CSRF-TOKEN':
                                csrfToken,
                        }
                        : {}),
                },
                body: JSON.stringify({
                    latitude,
                    longitude,
                }),
            }
        );

        const payload = await response.json();

        if (!response.ok) {
            throw new Error(
                payload.message
                || 'A route could not be calculated.'
            );
        }

        return payload.route;
    };

    const rerouteNavigation = async (
        latitude,
        longitude
    ) => {
        if (
            navigationRerouteInProgress
            || !activeNavigationLandmark
                ?.navigation_url
        ) {
            return;
        }

        const rerouteLandmark =
            activeNavigationLandmark;

        navigationRerouteInProgress = true;
        navigationOffRouteFixCount = 0;
        lastNavigationRerouteAt = Date.now();

        if (navigationNextStep) {
            navigationNextStep.textContent =
                'Recalculating route…';
        }

        if (navigationNextMeta) {
            navigationNextMeta.textContent =
                'You appear to be off the planned route.';
        }

        try {
            const route =
                await requestNavigationRoute(
                    rerouteLandmark
                        .navigation_url,
                    latitude,
                    longitude
                );

            if (
                activeNavigationLandmark
                !== rerouteLandmark
            ) {
                return;
            }

            activeNavigationRoute = route;
            drawNavigationRoute(route);
        } catch (error) {
            console.error(
                'Festival navigation reroute failed.',
                error
            );

            if (navigationNextMeta) {
                navigationNextMeta.textContent =
                    'Could not recalculate yet. Continuing the current route.';
            }
        } finally {
            navigationRerouteInProgress = false;
        }
    };

    const updateNavigationProgress = (
        position
    ) => {
        if (
            !activeNavigationRoute
            || navigationArrived
        ) {
            return;
        }

        const latitude =
            Number.parseFloat(
                position?.coords?.latitude
            );
        const longitude =
            Number.parseFloat(
                position?.coords?.longitude
            );
        const accuracy =
            Number.parseFloat(
                position?.coords?.accuracy
            );

        if (
            !Number.isFinite(latitude)
            || !Number.isFinite(longitude)
        ) {
            return;
        }

        // Ignore very poor fixes for navigation progress.
        // This prevents ordinary GPS drift from making the
        // guidance jump around at crowded festival sites.
        if (
            Number.isFinite(accuracy)
            && accuracy > liveLocationMaxAccuracyMeters
        ) {
            return;
        }

        if (
            activeNavigationRoute
                ?.navigation_mode
            === 'direct'
        ) {
            const destination =
                activeNavigationRoute
                    .to_endpoint;

            if (!destination) {
                return;
            }

            if (
                activeNavigationRoute
                    ?.target_type
                === 'member'
                && activeNavigationMemberId
                    !== null
            ) {
                const memberMarker =
                    memberMarkers.get(
                        activeNavigationMemberId
                    )
                    ?? memberMarkers.get(
                        String(
                            activeNavigationMemberId
                        )
                    );

                const latestPosition =
                    memberMarker?.getLatLng();

                if (
                    latestPosition
                    && Number.isFinite(
                        latestPosition.lat
                    )
                    && Number.isFinite(
                        latestPosition.lng
                    )
                ) {
                    destination.latitude =
                        latestPosition.lat;
                    destination.longitude =
                        latestPosition.lng;

                    if (navigationEndMarker) {
                        navigationEndMarker
                            .setLatLng([
                                latestPosition.lat,
                                latestPosition.lng,
                            ]);
                    }
                }
            }

            const destinationLatitude =
                Number.parseFloat(
                    destination.latitude
                );

            const destinationLongitude =
                Number.parseFloat(
                    destination.longitude
                );

            if (
                !Number.isFinite(
                    destinationLatitude
                )
                || !Number.isFinite(
                    destinationLongitude
                )
            ) {
                return;
            }

            const distance =
                navigationDistanceMeters(
                    latitude,
                    longitude,
                    destinationLatitude,
                    destinationLongitude
                );

            if (navigationStartMarker) {
                navigationStartMarker
                    .setLatLng([
                        latitude,
                        longitude,
                    ]);
            }

            if (navigationPolyline) {
                navigationPolyline
                    .setLatLngs([
                        [
                            latitude,
                            longitude,
                        ],
                        [
                            destinationLatitude,
                            destinationLongitude,
                        ],
                    ]);
            }

            const arrivalAccuracyIsGood =
                !Number.isFinite(accuracy)
                || accuracy <= 25;

            const arrivalThreshold =
                Math.max(
                    10,
                    Number.isFinite(accuracy)
                        ? Math.min(
                            20,
                            accuracy
                        )
                        : 10
                );

            const hasArrived =
                arrivalAccuracyIsGood
                && distance
                    <= arrivalThreshold;

            const bearing =
                navigationBearing(
                    latitude,
                    longitude,
                    destinationLatitude,
                    destinationLongitude
                );

            const cardinalDirection =
                navigationCardinalDirection(
                    bearing
                );

            let movementLabel = '';

            if (
                directGuidancePreviousDistance
                !== null
            ) {
                const change =
                    directGuidancePreviousDistance
                    - distance;

                if (change >= 3) {
                    movementLabel =
                        'Getting closer';
                } else if (change <= -5) {
                    movementLabel =
                        'Moving farther away';
                }
            }

            directGuidancePreviousDistance =
                distance;

            if (navigationMeta) {
                navigationMeta.textContent =
                    hasArrived
                        ? 'Arrived'
                        : [
                            `${formatNavigationDistance(
                                distance
                            )} straight-line`,
                            cardinalDirection
                                ? `Head ${cardinalDirection}`
                                : null,
                            movementLabel,
                        ]
                            .filter(Boolean)
                            .join(' · ');
            }

            const isMovingMember =
                activeNavigationRoute
                    ?.target_type
                === 'member';

            const isMeetup =
                activeNavigationRoute
                    ?.target_type
                === 'meetup';

            if (navigationNextStep) {
                navigationNextStep.textContent =
                    hasArrived
                        ? (
                            isMovingMember
                                ? `You are near ${
                                    destination.name
                                    || 'your friend'
                                }`
                                : `Arrive at ${
                                    destination.name
                                    || 'destination'
                                }`
                        )
                        : (
                            isMovingMember
                                ? `Head${
                                    cardinalDirection
                                        ? ` ${cardinalDirection}`
                                        : ''
                                } toward ${
                                    destination.name
                                    || 'your friend'
                                }`
                                : `Head${
                                    cardinalDirection
                                        ? ` ${cardinalDirection}`
                                        : ''
                                } toward ${
                                    destination.name
                                    || 'destination'
                                }`
                        );
            }

            if (navigationNextMeta) {
                navigationNextMeta.textContent =
                    hasArrived
                        ? (
                            isMovingMember
                                ? 'You have reached their latest shared location area.'
                                : 'You have reached the destination area.'
                        )
                        : (
                            isMovingMember
                                ? 'Their destination pin can move as their shared location updates.'
                                : isMeetup
                                    ? 'Direct guidance to the Meetup pin — use marked festival paths and follow venue rules.'
                                    : 'Direct guidance — follow the line and compass cue, while using marked festival paths and following venue rules.'
                        );
            }

            if (hasArrived) {
                navigationArrived = true;

                if (
                    navigationWatchId
                    !== null
                ) {
                    navigator.geolocation
                        .clearWatch(
                            navigationWatchId
                        );

                    navigationWatchId =
                        null;
                }
            }

            return;
        }

        const progress =
            navigationRouteProgress(
                activeNavigationRoute,
                latitude,
                longitude
            );

        if (!progress) {
            return;
        }

        if (navigationStartMarker) {
            navigationStartMarker.setLatLng(
                [latitude, longitude]
            );
        }

        const destination =
            activeNavigationRoute
                .to_endpoint;
        const destinationDistance =
            destination
                ? navigationDistanceMeters(
                    latitude,
                    longitude,
                    Number.parseFloat(
                        destination.latitude
                    ),
                    Number.parseFloat(
                        destination.longitude
                    )
                )
                : null;

        const arrivalAccuracyIsGood =
            !Number.isFinite(accuracy)
            || accuracy <= 25;
        const arrivalThreshold =
            Math.max(
                10,
                Number.isFinite(accuracy)
                    ? Math.min(20, accuracy)
                    : 10
            );
        const isWithinArrivalArea =
            Number.isFinite(
                destinationDistance
            )
            && destinationDistance
                <= arrivalThreshold;

        if (
            arrivalAccuracyIsGood
            && isWithinArrivalArea
        ) {
            navigationArrivalFixCount++;
        } else {
            navigationArrivalFixCount = 0;
        }

        const hasArrived =
            navigationArrivalFixCount
                >= navigationArrivalRequiredFixes;

        if (hasArrived) {
            navigationOffRouteFixCount = 0;
            navigationArrived = true;

            renderNavigationConfidenceDetails(
                activeNavigationRoute
                    ?.verification
            );

            if (navigationWatchId !== null) {
                navigator.geolocation.clearWatch(
                    navigationWatchId
                );
                navigationWatchId = null;
            }
        } else {
            const rerouteAccuracyIsGood =
                !Number.isFinite(accuracy)
                || accuracy <= 40;
            const offRouteThreshold =
                Math.max(
                    35,
                    Number.isFinite(accuracy)
                        ? accuracy * 1.5
                        : 35
                );
            const isMeaningfullyOffRoute =
                rerouteAccuracyIsGood
                && progress.distanceFromRoute
                    > offRouteThreshold;

            if (isMeaningfullyOffRoute) {
                navigationOffRouteFixCount++;
            } else {
                navigationOffRouteFixCount = 0;
            }

            const rerouteCooldownComplete =
                Date.now()
                - lastNavigationRerouteAt
                >= navigationRerouteCooldownMs;

            if (
                navigationOffRouteFixCount
                    >= navigationOffRouteRequiredFixes
                && rerouteCooldownComplete
                && !navigationRerouteInProgress
            ) {
                rerouteNavigation(
                    latitude,
                    longitude
                );

                return;
            }
        }

        const remainingDistance =
            hasArrived
                ? 0
                : progress.remainingDistance;
        const estimatedMinutes =
            remainingDistance > 0
                ? Math.max(
                    1,
                    Math.ceil(
                        remainingDistance
                        / 1.35
                        / 60
                    )
                )
                : 0;

        if (navigationMeta) {
            navigationMeta.textContent =
                hasArrived
                    ? 'Arrived'
                    : [
                        formatNavigationDistance(
                            remainingDistance
                        ),
                        `~${estimatedMinutes} min walk`,
                    ]
                        .filter(Boolean)
                        .join(' · ');
        }

        const instructions =
            Array.isArray(
                activeNavigationRoute
                    .instructions
            )
                ? activeNavigationRoute
                    .instructions
                : [];

        if (instructions.length === 0) {
            return;
        }

        if (hasArrived) {
            const arrival =
                instructions.find(
                    (instruction) =>
                        instruction.type
                        === 'arrival'
                );

            if (navigationNextStep) {
                navigationNextStep.textContent =
                    arrival?.title
                    || `Arrive at ${
                        destination?.name
                        || 'destination'
                    }`;
            }

            if (navigationNextMeta) {
                const weakRouteItems =
                    Array.isArray(
                        activeNavigationRoute
                            ?.verification
                            ?.items
                    )
                        ? activeNavigationRoute
                            .verification
                            .items
                            .filter(
                                (item) =>
                                    (
                                        Number.parseInt(
                                            item?.health_score,
                                            10
                                        ) || 0
                                    ) < 55
                            )
                        : [];

                navigationNextMeta.textContent =
                    [
                        arrival?.detail || '',
                        weakRouteItems.length > 0
                            ? 'You can help verify weak route items in confidence details.'
                            : '',
                    ]
                        .filter(Boolean)
                        .join(' · ');
            }

            return;
        }

        const actionable =
            instructions.filter(
                (instruction) =>
                    instruction.type
                    !== 'arrival'
            );

        let instructionStart = 0;
        let currentInstruction =
            actionable[0] || null;
        let currentInstructionRemaining =
            currentInstruction
                ? Number.parseFloat(
                    currentInstruction
                        .distance_meters
                )
                : 0;

        for (const instruction of actionable) {
            const instructionDistance =
                Math.max(
                    0,
                    Number.parseFloat(
                        instruction
                            .distance_meters
                    ) || 0
                );
            const instructionEnd =
                instructionStart
                + instructionDistance;

            if (
                progress.distanceAlongRoute
                <= instructionEnd + 8
            ) {
                currentInstruction =
                    instruction;
                currentInstructionRemaining =
                    Math.max(
                        0,
                        instructionEnd
                        - progress
                            .distanceAlongRoute
                    );
                break;
            }

            instructionStart =
                instructionEnd;
        }

        if (!currentInstruction) {
            return;
        }

        if (navigationNextStep) {
            navigationNextStep.textContent =
                currentInstruction.title
                || 'Continue';
        }

        if (navigationNextMeta) {
            navigationNextMeta.textContent =
                [
                    formatNavigationDistance(
                        currentInstructionRemaining
                    ),
                    currentInstruction.detail
                    || '',
                ]
                    .filter(Boolean)
                    .join(' · ');
        }
    };

    const startNavigationProgress = (
        route,
        landmark
    ) => {
        if (navigationWatchId !== null) {
            navigator.geolocation.clearWatch(
                navigationWatchId
            );
            navigationWatchId = null;
        }

        activeNavigationRoute = route;
        activeNavigationLandmark =
            route.target_type === 'landmark'
                ? landmark
                : null;
        activeNavigationMemberId =
            route.target_type === 'member'
                ? Number(route.target_id)
                : null;
        navigationOffRouteFixCount = 0;
        navigationRerouteInProgress = false;
        lastNavigationRerouteAt = 0;
        navigationArrivalFixCount = 0;
        navigationArrived = false;
        directGuidancePreviousDistance =
            null;

        navigationWatchId =
            navigator.geolocation.watchPosition(
                updateNavigationProgress,
                () => {
                    // Keep the last good navigation state if
                    // a later GPS update temporarily fails.
                },
                {
                    enableHighAccuracy: true,
                    maximumAge: 3000,
                    timeout: 15000,
                }
            );
    };

    const routeConfidenceLabel = (
        score
    ) => {
        const value =
            Number.parseInt(
                score,
                10
            );

        if (!Number.isFinite(value)) {
            return 'Unverified';
        }

        if (value >= 80) {
            return 'Excellent';
        }

        if (value >= 55) {
            return 'Good';
        }

        if (value >= 30) {
            return 'Limited';
        }

        if (value > 0) {
            return 'Weak';
        }

        return 'Unverified';
    };

    const renderNavigationConfidenceDetails = (
        verification
    ) => {
        if (
            !navigationConfidenceDetails
            || !navigationConfidenceToggle
        ) {
            return;
        }

        const items =
            Array.isArray(
                verification?.items
            )
                ? verification.items
                : [];

        navigationConfidenceDetails.innerHTML = '';
        navigationConfidenceDetails.classList.add(
            'hidden'
        );

        navigationConfidenceToggle.classList.add(
            'hidden'
        );

        navigationConfidenceToggle.textContent =
            'Show confidence details';

        if (items.length === 0) {
            return;
        }

        const orderedItems = [
            ...items,
        ].sort(
            (left, right) =>
                (
                    Number.parseInt(
                        left?.health_score,
                        10
                    ) || 0
                )
                - (
                    Number.parseInt(
                        right?.health_score,
                        10
                    ) || 0
                )
        );

        navigationConfidenceDetails.innerHTML =
            orderedItems
                .map(
                    (item, index) => {
                        const score =
                            Number.parseInt(
                                item?.health_score,
                                10
                            );

                        const confirmations =
                            Number.parseInt(
                                item?.confirmation_count,
                                10
                            ) || 0;

                        const typeLabel =
                            item?.type === 'path'
                                ? 'Path / Trail'
                                : item?.type === 'junction'
                                    ? 'Junction / Waypoint'
                                    : 'Route item';

                        const label =
                            routeConfidenceLabel(
                                score
                            );

                        const sourceItem =
                            item?.type === 'path'
                                ? paths.find(
                                    (candidate) =>
                                        Number(candidate.id)
                                        === Number(item.id)
                                )
                                : item?.type === 'junction'
                                    ? pathJunctions.find(
                                        (candidate) =>
                                            Number(candidate.id)
                                            === Number(item.id)
                                    )
                                    : null;

                        const viewerConfirmed =
                            Boolean(
                                sourceItem
                                    ?.viewer_confirmed
                            );

                        const canConfirmAfterArrival =
                            navigationArrived
                            && Number.isFinite(score)
                            && score < 55
                            && !viewerConfirmed
                            && ['path', 'junction']
                                .includes(
                                    item?.type
                                );

                        const confirmationAction =
                            canConfirmAfterArrival
                                ? `
                                    <button
                                        type="button"
                                        data-venue-map-confirm
                                        data-target-type="${escapeAttribute(
                                            item.type
                                        )}"
                                        data-target-id="${escapeAttribute(
                                            String(item.id)
                                        )}"
                                        style="
                                            margin-top:7px;
                                            border:0;
                                            border-radius:8px;
                                            padding:6px 9px;
                                            background:#0f766e;
                                            color:white;
                                            font-size:11px;
                                            font-weight:800;
                                            cursor:pointer;
                                        "
                                    >
                                        Confirm accurate after walking
                                    </button>
                                `
                                : viewerConfirmed
                                    ? `
                                        <div
                                            style="
                                                margin-top:6px;
                                                font-size:11px;
                                                font-weight:800;
                                                color:#047857;
                                            "
                                        >
                                            ✓ You confirmed this item
                                        </div>
                                    `
                                    : '';

                        return `
                            <div
                                style="
                                    padding:8px 0;
                                    border-bottom:
                                        ${index === orderedItems.length - 1
                                            ? '0'
                                            : '1px solid rgba(148,163,184,.25)'};
                                "
                            >
                                <div
                                    style="
                                        display:flex;
                                        align-items:flex-start;
                                        justify-content:space-between;
                                        gap:10px;
                                    "
                                >
                                    <div style="min-width:0">
                                        <div
                                            style="
                                                font-size:12px;
                                                font-weight:800;
                                                color:inherit;
                                            "
                                        >
                                            ${escapeHtml(
                                                item?.name
                                                || typeLabel
                                            )}
                                        </div>

                                        <div
                                            style="
                                                margin-top:2px;
                                                color:#64748b;
                                                font-size:11px;
                                            "
                                        >
                                            ${escapeHtml(
                                                `${typeLabel} · ${confirmations} ${
                                                    confirmations === 1
                                                        ? 'confirmation'
                                                        : 'confirmations'
                                                }`
                                            )}
                                        </div>

                                        ${confirmationAction}
                                    </div>

                                    <div
                                        style="
                                            flex:0 0 auto;
                                            text-align:right;
                                            font-size:11px;
                                            font-weight:800;
                                        "
                                    >
                                        ${Number.isFinite(score)
                                            ? `${score}/100`
                                            : '0/100'}
                                        <div
                                            style="
                                                margin-top:1px;
                                                font-weight:700;
                                                color:#64748b;
                                            "
                                        >
                                            ${escapeHtml(label)}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        `;
                    }
                )
                .join('');

        navigationConfidenceToggle.classList.remove(
            'hidden'
        );
    };

    const renderNavigationConfidence = (
        verification
    ) => {
        if (!navigationConfidence) {
            return;
        }

        navigationConfidence.classList.add(
            'hidden'
        );

        navigationConfidence.classList.remove(
            'border-violet-200',
            'bg-violet-50',
            'text-violet-700',
            'dark:border-violet-400/20',
            'dark:bg-violet-400/10',
            'dark:text-violet-200',
            'border-emerald-200',
            'bg-emerald-50',
            'text-emerald-700',
            'dark:border-emerald-400/20',
            'dark:bg-emerald-400/10',
            'dark:text-emerald-200',
            'border-amber-200',
            'bg-amber-50',
            'text-amber-700',
            'dark:border-amber-400/20',
            'dark:bg-amber-400/10',
            'dark:text-amber-200',
            'border-rose-200',
            'bg-rose-50',
            'text-rose-700',
            'dark:border-rose-400/20',
            'dark:bg-rose-400/10',
            'dark:text-rose-200',
            'border-zinc-200',
            'bg-zinc-50',
            'text-zinc-600',
            'dark:border-zinc-700',
            'dark:bg-zinc-800',
            'dark:text-zinc-300'
        );

        const score =
            Number.parseInt(
                verification?.score,
                10
            );

        const label =
            verification?.label
            || 'unverified';

        const unverifiedCount =
            Number.parseInt(
                verification
                    ?.unverified_count,
                10
            ) || 0;

        const limitedCount =
            Number.parseInt(
                verification
                    ?.limited_count,
                10
            ) || 0;

        const styleClasses =
            label === 'excellent'
                ? [
                    'border-violet-200',
                    'bg-violet-50',
                    'text-violet-700',
                    'dark:border-violet-400/20',
                    'dark:bg-violet-400/10',
                    'dark:text-violet-200',
                ]
                : label === 'good'
                    ? [
                        'border-emerald-200',
                        'bg-emerald-50',
                        'text-emerald-700',
                        'dark:border-emerald-400/20',
                        'dark:bg-emerald-400/10',
                        'dark:text-emerald-200',
                    ]
                    : label === 'limited'
                        ? [
                            'border-amber-200',
                            'bg-amber-50',
                            'text-amber-700',
                            'dark:border-amber-400/20',
                            'dark:bg-amber-400/10',
                            'dark:text-amber-200',
                        ]
                        : label === 'weak'
                            ? [
                                'border-rose-200',
                                'bg-rose-50',
                                'text-rose-700',
                                'dark:border-rose-400/20',
                                'dark:bg-rose-400/10',
                                'dark:text-rose-200',
                            ]
                            : [
                                'border-zinc-200',
                                'bg-zinc-50',
                                'text-zinc-600',
                                'dark:border-zinc-700',
                                'dark:bg-zinc-800',
                                'dark:text-zinc-300',
                            ];

        navigationConfidence.classList.add(
            ...styleClasses
        );

        const labelText =
            label === 'excellent'
                ? 'Excellent route confidence'
                : label === 'good'
                    ? 'Good route confidence'
                    : label === 'limited'
                        ? 'Limited route confidence'
                        : label === 'weak'
                            ? 'Weak route confidence'
                            : 'Route not yet verified';

        const details = [];

        if (unverifiedCount > 0) {
            details.push(
                `${unverifiedCount} unverified ${
                    unverifiedCount === 1
                        ? 'route item'
                        : 'route items'
                }`
            );
        } else if (limitedCount > 0) {
            details.push(
                `${limitedCount} ${
                    limitedCount === 1
                        ? 'route item has'
                        : 'route items have'
                } limited evidence`
            );
        }

        navigationConfidence.textContent =
            [
                Number.isFinite(score)
                    ? `${labelText} · ${score}/100`
                    : labelText,
                ...details,
            ].join(' · ');

        navigationConfidence.classList.remove(
            'hidden'
        );

        renderNavigationConfidenceDetails(
            verification
        );
    };

    const drawNavigationRoute = (
        route
    ) => {
        clearNavigationDisplay();

        const geometry =
            Array.isArray(
                route.geometry
            )
                ? route.geometry
                : [];

        const latLngs = geometry
            .map((point) => {
                const latitude =
                    Number.parseFloat(
                        point.latitude
                    );

                const longitude =
                    Number.parseFloat(
                        point.longitude
                    );

                if (
                    !Number.isFinite(latitude)
                    || !Number.isFinite(longitude)
                ) {
                    return null;
                }

                return [
                    latitude,
                    longitude,
                ];
            })
            .filter(Boolean);

        if (latLngs.length < 2) {
            throw new Error(
                'The route did not contain usable map geometry.'
            );
        }

        const isDirectGuidance =
            route.navigation_mode
            === 'direct';

        navigationPolyline =
            L.polyline(
                latLngs,
                {
                    color: '#2563eb',
                    weight:
                        isDirectGuidance
                            ? 5
                            : 7,
                    opacity: 0.95,
                    dashArray:
                        isDirectGuidance
                            ? '12 9'
                            : null,
                }
            ).addTo(
                navigationLayer
            );

        const start =
            route.from_endpoint;

        const destination =
            route.to_endpoint;

        if (start) {
            navigationStartMarker =
                L.circleMarker(
                    [
                        Number.parseFloat(
                            start.latitude
                        ),
                        Number.parseFloat(
                            start.longitude
                        ),
                    ],
                    {
                        radius: 9,
                        color: '#15803d',
                        weight: 3,
                        fillColor: '#22c55e',
                        fillOpacity: 1,
                    }
                )
                    .addTo(
                        navigationLayer
                    )
                    .bindTooltip(
                        'You are here',
                        {
                            permanent: true,
                            direction: 'top',
                        }
                    );
        }

        if (destination) {
            navigationEndMarker =
                L.circleMarker(
                    [
                        Number.parseFloat(
                            destination.latitude
                        ),
                        Number.parseFloat(
                            destination.longitude
                        ),
                    ],
                    {
                        radius: 9,
                        color: '#b91c1c',
                        weight: 3,
                        fillColor: '#ef4444',
                        fillOpacity: 1,
                    }
                )
                    .addTo(
                        navigationLayer
                    )
                    .bindTooltip(
                        destination.name
                        || 'Destination',
                        {
                            permanent: true,
                            direction: 'top',
                        }
                    );
        }

        const distance =
            Number.parseFloat(
                route.distance_meters
            );

        const estimatedMinutes =
            Number.isFinite(distance)
                ? Math.max(
                    1,
                    Math.ceil(
                        distance
                        / 1.35
                        / 60
                    )
                )
                : null;

        if (navigationTitle) {
            navigationTitle.textContent =
                isDirectGuidance
                    ? (
                        route.target_type
                            === 'member'
                            ? `Finding ${
                                destination?.name
                                || 'friend'
                            }`
                            : `Direct guidance to ${
                                destination?.name
                                || 'destination'
                            }`
                    )
                    : `To ${
                        destination?.name
                        || 'destination'
                    }`;
        }

        if (navigationMeta) {
            const parts =
                isDirectGuidance
                    ? [
                        Number.isFinite(
                            distance
                        )
                            ? `${formatNavigationDistance(
                                distance
                            )} straight-line`
                            : null,
                    ]
                    : [
                        formatNavigationDistance(
                            distance
                        ),
                        estimatedMinutes
                            ? `~${estimatedMinutes} min walk`
                            : null,
                    ];

            navigationMeta.textContent =
                parts
                    .filter(Boolean)
                    .join(' · ');
        }

        if (isDirectGuidance) {
            navigationConfidence
                ?.classList
                .add('hidden');

            navigationConfidenceToggle
                ?.classList
                .add('hidden');

            navigationConfidenceDetails
                ?.classList
                .add('hidden');

            navigationStepsToggle
                ?.classList
                .add('hidden');

            navigationSteps
                ?.classList
                .add('hidden');

            if (navigationNextStep) {
                navigationNextStep.textContent =
                    `Head toward ${
                        destination?.name
                        || (
                            route.target_type
                                === 'member'
                                ? 'your friend'
                                : 'destination'
                        )
                    }`;
            }

            if (navigationNextMeta) {
                navigationNextMeta.textContent =
                    route.target_type
                        === 'member'
                        ? 'Their destination pin can move as their shared location updates.'
                        : route.target_type
                            === 'meetup'
                            ? 'Direct guidance to the Meetup pin — use marked festival paths and follow venue rules.'
                            : 'Direct guidance — follow the line and compass cue, while using marked festival paths and following venue rules.';
            }
        } else {
            renderNavigationConfidence(
                route.verification
            );

            renderNavigationInstructions(
                route
            );
        }

        navigationSummary?.classList.remove(
            'hidden'
        );

        map.fitBounds(
            navigationPolyline
                .getBounds(),
            {
                padding: [60, 60],
                maxZoom: 18,
                animate: true,
            }
        );
    };

    clearNavigationButton?.addEventListener(
        'click',
        clearNavigation,
        {
            signal:
                eventController.signal,
        }
    );

    navigationConfidenceToggle?.addEventListener(
        'click',
        () => {
            if (!navigationConfidenceDetails) {
                return;
            }

            const willShow =
                navigationConfidenceDetails
                    .classList
                    .contains(
                        'hidden'
                    );

            navigationConfidenceDetails
                .classList
                .toggle(
                    'hidden',
                    !willShow
                );

            navigationConfidenceToggle.textContent =
                willShow
                    ? 'Hide confidence details'
                    : 'Show confidence details';
        },
        {
            signal:
                eventController.signal,
        }
    );

    navigationStepsToggle?.addEventListener(
        'click',
        () => {
            if (!navigationSteps) {
                return;
            }

            const willShow =
                navigationSteps
                    .classList
                    .contains(
                        'hidden'
                    );

            navigationSteps.classList.toggle(
                'hidden',
                !willShow
            );

            navigationStepsToggle.textContent =
                willShow
                    ? 'Hide route steps'
                    : 'Show route steps';
        },
        {
            signal:
                eventController.signal,
        }
    );

    mapElement.addEventListener(
        'click',
        async (event) => {
            const button = event.target.closest(
                '[data-meetup-direct-guide]'
            );

            if (
                !button
                || button.disabled
            ) {
                return;
            }

            const meetupId =
                Number.parseInt(
                    button.dataset.meetupId,
                    10
                );

            const marker =
                meetupMarkers.get(
                    String(meetupId)
                );

            const destinationPosition =
                marker?.getLatLng();

            if (
                !destinationPosition
                || !Number.isFinite(
                    destinationPosition.lat
                )
                || !Number.isFinite(
                    destinationPosition.lng
                )
            ) {
                showSharingStatus(
                    'This Meetup does not have an exact location available.',
                    'error'
                );
                return;
            }

            const title =
                button
                    .closest('div')
                    ?.querySelector('strong')
                    ?.textContent
                    ?.trim()
                || 'Meetup';

            if (!navigator.geolocation) {
                showSharingStatus(
                    'Your browser does not support location services.',
                    'error'
                );
                return;
            }

            button.disabled = true;

            const originalText =
                button.textContent;

            button.textContent =
                'Finding your location…';

            try {
                const position =
                    await new Promise(
                        (
                            resolve,
                            reject
                        ) => {
                            navigator.geolocation
                                .getCurrentPosition(
                                    resolve,
                                    reject,
                                    {
                                        enableHighAccuracy:
                                            true,
                                        timeout: 15000,
                                        maximumAge: 5000,
                                    }
                                );
                        }
                    );

                const distance =
                    navigationDistanceMeters(
                        position.coords.latitude,
                        position.coords.longitude,
                        destinationPosition.lat,
                        destinationPosition.lng
                    );

                const route = {
                    navigation_mode:
                        'direct',
                    target_type:
                        'meetup',
                    target_id:
                        meetupId,
                    distance_meters:
                        distance,
                    geometry: [
                        {
                            latitude:
                                position.coords.latitude,
                            longitude:
                                position.coords.longitude,
                        },
                        {
                            latitude:
                                destinationPosition.lat,
                            longitude:
                                destinationPosition.lng,
                        },
                    ],
                    from_endpoint: {
                        type:
                            'current_location',
                        name:
                            'Your location',
                        latitude:
                            position.coords.latitude,
                        longitude:
                            position.coords.longitude,
                    },
                    to_endpoint: {
                        type:
                            'meetup',
                        id:
                            meetupId,
                        name:
                            title,
                        latitude:
                            destinationPosition.lat,
                        longitude:
                            destinationPosition.lng,
                    },
                    instructions: [],
                };

                drawNavigationRoute(
                    route
                );

                startNavigationProgress(
                    route,
                    null
                );

                updateNavigationProgress(
                    position
                );

                map.closePopup();
            } catch (error) {
                const message =
                    error?.code === 1
                        ? 'Location permission is required to navigate to this Meetup.'
                        : error instanceof Error
                            ? error.message
                            : 'Meetup guidance could not be started.';

                showSharingStatus(
                    message,
                    'error'
                );
            } finally {
                button.disabled = false;
                button.textContent =
                    originalText;
            }
        },
        {
            signal:
                eventController.signal,
        }
    );

    mapElement.addEventListener(
        'click',
        async (event) => {
            const sendButton = event.target.closest(
                '[data-member-quick-message-send]'
            );

            if (! sendButton || sendButton.disabled) {
                return;
            }

            const quickMessage = sendButton.closest(
                '[data-member-quick-message]'
            );

            const select = quickMessage?.querySelector(
                '[data-member-quick-message-select]'
            );

            const status = quickMessage?.querySelector(
                '[data-member-quick-message-status]'
            );

            const url = quickMessage?.dataset.quickMessageUrl;
            const message = select?.value;

            if (! url || ! message) {
                return;
            }

            const csrfToken = document
                .querySelector('meta[name="csrf-token"]')
                ?.getAttribute('content');

            const originalText = sendButton.textContent;

            sendButton.disabled = true;
            sendButton.textContent = 'Sending…';

            if (status) {
                status.style.display = 'none';
                status.textContent = '';
            }

            try {
                const response = await fetch(
                    url,
                    {
                        method: 'POST',
                        credentials: 'same-origin',
                        headers: {
                            Accept: 'application/json',
                            'Content-Type': 'application/json',
                            'X-Requested-With': 'XMLHttpRequest',
                            ...(csrfToken
                                ? {
                                    'X-CSRF-TOKEN': csrfToken,
                                }
                                : {}),
                        },
                        body: JSON.stringify({
                            message,
                        }),
                    }
                );

                const payload = await response.json();

                if (! response.ok) {
                    throw new Error(
                        payload?.message
                        || 'Unable to send your message.'
                    );
                }

                if (status) {
                    status.style.display = 'block';
                    status.style.color = '#047857';
                    status.textContent = 'Sent! ✓';
                }

                sendButton.textContent = 'Sent';

                if (
                    payload?.compact_url
                    && document.querySelector(
                        '[data-live-map-message-drawer]'
                    )
                ) {
                    window.dispatchEvent(
                        new CustomEvent(
                            'festfriends:open-conversation',
                            {
                                detail: {
                                    conversation_id:
                                        payload
                                            .conversation_id,

                                    conversation_url:
                                        payload
                                            .conversation_url,

                                    compact_url:
                                        payload
                                            .compact_url,

                                    send_url:
                                        payload
                                            .send_url,
                                },
                            }
                        )
                    );

                    map.closePopup();
                }

                window.setTimeout(() => {
                    if (sendButton.isConnected) {
                        sendButton.textContent = originalText;
                        sendButton.disabled = false;
                    }
                }, 1600);
            } catch (error) {
                if (status) {
                    status.style.display = 'block';
                    status.style.color = '#be123c';
                    status.textContent =
                        error?.message
                        || 'Unable to send your message.';
                }

                sendButton.textContent = originalText;
                sendButton.disabled = false;
            }
        },
        {
            signal:
                eventController.signal,
        }
    );

    const startMemberDirectGuidance =
        async (
            memberId,
            memberName = 'Friend',
            button = null
        ) => {
            const normalizedMemberId =
                Number.parseInt(
                    memberId,
                    10
                );

            if (
                !Number.isFinite(
                    normalizedMemberId
                )
                || !viewerCheckedIn
                || !navigableMemberIds.has(
                    normalizedMemberId
                )
                    && !navigableMemberIds.has(
                        String(
                            normalizedMemberId
                        )
                    )
            ) {
                showSharingStatus(
                    'Friend guidance is not currently available for this member.',
                    'error'
                );

                return;
            }

            const marker =
                memberMarkers.get(
                    normalizedMemberId
                )
                ?? memberMarkers.get(
                    String(
                        normalizedMemberId
                    )
                );

            const friendPosition =
                marker?.getLatLng();

            if (
                !friendPosition
                || !Number.isFinite(
                    friendPosition.lat
                )
                || !Number.isFinite(
                    friendPosition.lng
                )
            ) {
                showSharingStatus(
                    'Your friend’s latest shared location is no longer available.',
                    'error'
                );

                return;
            }

            if (!navigator.geolocation) {
                showSharingStatus(
                    'Your browser does not support location services.',
                    'error'
                );

                return;
            }

            const originalText =
                button?.textContent
                ?? null;

            if (button) {
                button.disabled = true;
                button.textContent =
                    'Finding your location…';
            }

            try {
                const position =
                    await new Promise(
                        (
                            resolve,
                            reject
                        ) => {
                            navigator.geolocation
                                .getCurrentPosition(
                                    resolve,
                                    reject,
                                    {
                                        enableHighAccuracy:
                                            true,

                                        timeout:
                                            15000,

                                        maximumAge:
                                            5000,
                                    }
                                );
                        }
                    );

                const distance =
                    navigationDistanceMeters(
                        position.coords.latitude,
                        position.coords.longitude,
                        friendPosition.lat,
                        friendPosition.lng
                    );

                const route = {
                    navigation_mode:
                        'direct',

                    target_type:
                        'member',

                    target_id:
                        normalizedMemberId,

                    distance_meters:
                        distance,

                    geometry: [
                        {
                            latitude:
                                position.coords.latitude,

                            longitude:
                                position.coords.longitude,
                        },

                        {
                            latitude:
                                friendPosition.lat,

                            longitude:
                                friendPosition.lng,
                        },
                    ],

                    from_endpoint: {
                        type:
                            'current_location',

                        name:
                            'Your location',

                        latitude:
                            position.coords.latitude,

                        longitude:
                            position.coords.longitude,
                    },

                    to_endpoint: {
                        type:
                            'member',

                        id:
                            normalizedMemberId,

                        name:
                            memberName
                            || 'Friend',

                        latitude:
                            friendPosition.lat,

                        longitude:
                            friendPosition.lng,
                    },

                    instructions:
                        [],
                };

                drawNavigationRoute(
                    route
                );

                startNavigationProgress(
                    route,
                    null
                );

                updateNavigationProgress(
                    position
                );

                map.closePopup();
            } catch (error) {
                const message =
                    error?.code === 1
                        ? 'Location permission is required to find your friend.'
                        : error instanceof Error
                            ? error.message
                            : 'Friend guidance could not be started.';

                showSharingStatus(
                    message,
                    'error'
                );
            } finally {
                if (button) {
                    button.disabled =
                        false;

                    button.textContent =
                        originalText
                        || '🧭 Find Friend';
                }
            }
        };

    mapElement.addEventListener(
        'click',
        async (event) => {
            const button = event.target.closest(
                '[data-member-direct-guide]'
            );

            if (
                !button
                || button.disabled
            ) {
                return;
            }

            const popupMemberName =
                button
                    .closest('div')
                    ?.querySelector('strong')
                    ?.textContent
                    ?.trim()
                || 'Friend';

            await startMemberDirectGuidance(
                button.dataset.memberId,
                popupMemberName,
                button
            );
        },
        {
            signal:
                eventController.signal,
        }
    );

    addWindowListener(
        'festival:navigate-member',
        (event) => {
            startMemberDirectGuidance(
                event.detail?.memberId,
                event.detail?.memberName
                || 'Friend'
            );
        }
    );

    mapElement.addEventListener(
        'click',
        async (event) => {
            const button = event.target.closest(
                '[data-landmark-navigate]'
            );

            if (
                !button
                || button.disabled
            ) {
                return;
            }

            const landmarkId =
                Number.parseInt(
                    button.dataset.landmarkId,
                    10
                );

            const landmark =
                landmarks.find(
                    (candidate) =>
                        Number(candidate.id)
                        === landmarkId
                );

            if (!landmark) {
                return;
            }

            const destinationLatitude =
                Number.parseFloat(
                    landmark.latitude
                );

            const destinationLongitude =
                Number.parseFloat(
                    landmark.longitude
                );

            if (
                !Number.isFinite(
                    destinationLatitude
                )
                || !Number.isFinite(
                    destinationLongitude
                )
            ) {
                return;
            }

            if (!navigator.geolocation) {
                showSharingStatus(
                    'Your browser does not support location services.',
                    'error'
                );
                return;
            }

            button.disabled = true;
            const originalText =
                button.textContent;

            button.textContent =
                'Finding your location…';

            try {
                const position =
                    await new Promise(
                        (
                            resolve,
                            reject
                        ) => {
                            navigator.geolocation
                                .getCurrentPosition(
                                    resolve,
                                    reject,
                                    {
                                        enableHighAccuracy:
                                            true,
                                        timeout: 15000,
                                        maximumAge: 5000,
                                    }
                                );
                        }
                    );

                const distance =
                    navigationDistanceMeters(
                        position.coords.latitude,
                        position.coords.longitude,
                        destinationLatitude,
                        destinationLongitude
                    );

                const route = {
                    navigation_mode:
                        'direct',
                    target_type:
                        'landmark',
                    target_id:
                        landmark.id,
                    distance_meters:
                        distance,
                    geometry: [
                        {
                            latitude:
                                position.coords.latitude,
                            longitude:
                                position.coords.longitude,
                        },
                        {
                            latitude:
                                destinationLatitude,
                            longitude:
                                destinationLongitude,
                        },
                    ],
                    from_endpoint: {
                        type: 'current_location',
                        name: 'Your location',
                        latitude:
                            position.coords.latitude,
                        longitude:
                            position.coords.longitude,
                    },
                    to_endpoint: {
                        type: 'landmark',
                        id: landmark.id,
                        name:
                            landmark.name
                            || 'Destination',
                        latitude:
                            destinationLatitude,
                        longitude:
                            destinationLongitude,
                    },
                    instructions: [],
                };

                drawNavigationRoute(
                    route
                );

                startNavigationProgress(
                    route,
                    landmark
                );

                updateNavigationProgress(
                    position
                );

                map.closePopup();
            } catch (error) {
                const message =
                    error?.code === 1
                        ? 'Location permission is required for direct guidance.'
                        : error instanceof Error
                            ? error.message
                            : 'Direct guidance could not be started.';

                showSharingStatus(
                    message,
                    'error'
                );
            } finally {
                button.disabled = false;
                button.textContent =
                    originalText;
            }
        },
        {
            signal:
                eventController.signal,
        }
    );

    mapElement.addEventListener(
        'click',
        async (event) => {
            const button = event.target.closest(
                '[data-landmark-mapped-route]'
            );

            if (
                !button
                || button.disabled
            ) {
                return;
            }

            const landmarkId =
                Number.parseInt(
                    button.dataset.landmarkId,
                    10
                );

            const landmark =
                landmarks.find(
                    (candidate) =>
                        Number(candidate.id)
                        === landmarkId
                );

            if (
                !landmark
                || !landmark.navigation_url
            ) {
                return;
            }

            if (!navigator.geolocation) {
                showSharingStatus(
                    'Your browser does not support location services.',
                    'error'
                );
                return;
            }

            button.disabled = true;
            const originalText =
                button.textContent;

            button.textContent =
                'Finding your location…';

            try {
                const position =
                    await new Promise(
                        (
                            resolve,
                            reject
                        ) => {
                            navigator.geolocation
                                .getCurrentPosition(
                                    resolve,
                                    reject,
                                    {
                                        enableHighAccuracy:
                                            true,
                                        timeout: 15000,
                                        maximumAge: 5000,
                                    }
                                );
                        }
                    );

                button.textContent =
                    'Calculating mapped route…';

                const route =
                    await requestNavigationRoute(
                        landmark.navigation_url,
                        position.coords.latitude,
                        position.coords.longitude
                    );

                drawNavigationRoute(route);

                startNavigationProgress(
                    route,
                    landmark
                );

                updateNavigationProgress(
                    position
                );

                map.closePopup();
            } catch (error) {
                const message =
                    error?.code === 1
                        ? 'Location permission is required to calculate a mapped route.'
                        : error instanceof Error
                            ? error.message
                            : 'A mapped route could not be calculated.';

                showSharingStatus(
                    message,
                    'error'
                );
            } finally {
                button.disabled = false;
                button.textContent =
                    originalText;
            }
        },
        {
            signal:
                eventController.signal,
        }
    );

    mapElement.addEventListener(
        'click',
        async (event) => {
            const button = event.target.closest(
                '[data-venue-map-confirm]'
            );

            if (
                !button
                || !venueMapConfirmUrl
                || button.disabled
            ) {
                return;
            }

            const targetType =
                button.dataset.targetType;
            const targetId =
                Number.parseInt(
                    button.dataset.targetId,
                    10
                );

            if (
                ![
                    'landmark',
                    'zone',
                    'path',
                    'junction',
                ].includes(
                    targetType
                )
                || !Number.isInteger(targetId)
            ) {
                return;
            }

            button.disabled = true;
            const originalText =
                button.textContent;
            button.textContent =
                'Confirming…';

            try {
                const csrfToken =
                    document.querySelector(
                        'meta[name="csrf-token"]'
                    )?.content;

                const response = await fetch(
                    venueMapConfirmUrl,
                    {
                        method: 'POST',
                        credentials: 'same-origin',
                        headers: {
                            Accept: 'application/json',
                            'Content-Type':
                                'application/json',
                            'X-Requested-With':
                                'XMLHttpRequest',

                            ...(csrfToken
                                ? {
                                    'X-CSRF-TOKEN':
                                        csrfToken,
                                }
                                : {}),
                        },
                        body: JSON.stringify({
                            target_type:
                                targetType,
                            target_id:
                                targetId,
                        }),
                    }
                );

                const payload =
                    await response.json();

                if (!response.ok) {
                    throw new Error(
                        payload.message
                        || 'This map item could not be confirmed.'
                    );
                }

                const collection =
                    targetType === 'landmark'
                        ? landmarks
                        : targetType === 'zone'
                            ? zones
                            : targetType === 'path'
                                ? paths
                                : pathJunctions;

                const item = collection.find(
                    (candidate) =>
                        Number(candidate.id)
                        === targetId
                );

                if (!item) {
                    return;
                }

                item.viewer_confirmed = true;
                item.confirmation_count =
                    Number.parseInt(
                        payload.count,
                        10
                    ) || 1;

                if (targetType === 'landmark') {
                    landmarkMarkers
                        .get(String(targetId))
                        ?.setPopupContent(
                            createLandmarkPopup(
                                item
                            )
                        );
                } else if (targetType === 'zone') {
                    zonePolygons
                        .get(String(targetId))
                        ?.setPopupContent(
                            createZonePopup(
                                item
                            )
                        );
                } else if (targetType === 'path') {
                    pathPolylines
                        .get(String(targetId))
                        ?.setPopupContent(
                            createPathPopup(
                                item
                            )
                        );
                } else if (targetType === 'junction') {
                    junctionMarkers
                        .get(String(targetId))
                        ?.setPopupContent(
                            createJunctionPopup(
                                item
                            )
                        );
                }

                if (
                    navigationArrived
                    && activeNavigationRoute
                        ?.verification
                ) {
                    const routeItem =
                        activeNavigationRoute
                            .verification
                            .items
                            ?.find(
                                (candidate) =>
                                    candidate.type
                                        === targetType
                                    && Number(
                                        candidate.id
                                    ) === targetId
                            );

                    if (routeItem) {
                        routeItem.confirmation_count =
                            item.confirmation_count;
                    }

                    renderNavigationConfidenceDetails(
                        activeNavigationRoute
                            .verification
                    );
                }
            } catch (error) {
                button.disabled = false;
                button.textContent =
                    originalText;

                showSharingStatus(
                    error instanceof Error
                        ? error.message
                        : 'This map item could not be confirmed.',
                    'error'
                );
            }
        },
        {
            signal: eventController.signal,
        }
    );

    const illustratedTerrainStyles = {
        grassland: {
            background:
                'radial-gradient(ellipse at center,rgba(70,108,57,.20) 0 1.2px,transparent 1.7px), radial-gradient(ellipse at center,rgba(255,255,255,.18) 0 1px,transparent 1.5px), #a8c985',
            backgroundSize:
                '26px 22px, 31px 27px',
            backgroundPosition:
                '3px 5px, 15px 14px',
        },
        wooded: {
            background:
                'radial-gradient(circle at 15% 20%,#27442f 0 8%,transparent 9%), radial-gradient(circle at 65% 32%,#294b33 0 10%,transparent 11%), radial-gradient(circle at 82% 72%,#24452e 0 9%,transparent 10%), linear-gradient(145deg,#557b52,#739765)',
        },
        fairground: {
            background:
                'linear-gradient(30deg,rgba(114,99,75,.13) 12%,transparent 12.5%,transparent 87%,rgba(114,99,75,.13) 87.5%), linear-gradient(150deg,rgba(114,99,75,.13) 12%,transparent 12.5%,transparent 87%,rgba(114,99,75,.13) 87.5%), #d9cfaa',
            backgroundSize: '48px 84px',
        },
        parking: {
            background:
                'repeating-linear-gradient(90deg,transparent 0 58px,rgba(255,255,255,.5) 59px 62px), linear-gradient(#999fa4,#b3b7ba)',
        },
        neutral: {
            background:
                'linear-gradient(rgba(100,116,139,.12) 1px,transparent 1px), linear-gradient(90deg,rgba(100,116,139,.12) 1px,transparent 1px), #e5e7eb',
            backgroundSize: '40px 40px',
        },
    };

    const illustratedAreaColors = {
        main_stage: {
            fill: 'rgba(217,70,239,.18)',
            stroke: '#c026d3',
        },
        secondary_stage: {
            fill: 'rgba(139,92,246,.18)',
            stroke: '#7c3aed',
        },
        food_vendors: {
            fill: 'rgba(249,115,22,.18)',
            stroke: '#ea580c',
        },
        camping: {
            fill: 'rgba(37,99,235,.15)',
            stroke: '#2563eb',
        },
        parking: {
            fill: 'rgba(100,116,139,.22)',
            stroke: '#475569',
        },
        forest: {
            fill: 'rgba(22,101,52,.20)',
            stroke: '#166534',
        },
        water: {
            fill: 'rgba(14,165,233,.22)',
            stroke: '#0284c7',
        },
    };

    const createSvgNode = (
        name,
        attributes = {}
    ) => {
        const node = document.createElementNS(
            'http://www.w3.org/2000/svg',
            name
        );

        Object.entries(attributes)
            .forEach(([key, value]) => {
                node.setAttribute(
                    key,
                    String(value)
                );
            });

        return node;
    };

    const illustratedAreaTypes =
        new Set([
            'forest',
            'water',
            'camping',
            'parking',
            'food_vendors',
        ]);

    const buildIllustratedAreaDefs = () => {
        const defs =
            createSvgNode('defs');

        const addPattern = (
            id,
            width,
            height,
            children
        ) => {
            const pattern =
                createSvgNode(
                    'pattern',
                    {
                        id,
                        width,
                        height,
                        patternUnits:
                            'userSpaceOnUse',
                    }
                );

            children(pattern);
            defs.append(pattern);
        };

        addPattern(
            'ff-live-area-forest',
            72,
            62,
            (pattern) => {
                pattern.append(
                    createSvgNode('circle', {
                        cx: 18, cy: 33, r: 14,
                        fill: '#397a43',
                    }),
                    createSvgNode('circle', {
                        cx: 31, cy: 24, r: 15,
                        fill: '#4a8c4f',
                    }),
                    createSvgNode('circle', {
                        cx: 45, cy: 35, r: 13,
                        fill: '#2f6b3a',
                    }),
                    createSvgNode('rect', {
                        x: 28, y: 39,
                        width: 7, height: 19,
                        rx: 3,
                        fill: '#76502d',
                    })
                );
            }
        );

        addPattern(
            'ff-live-area-camping',
            86,
            68,
            (pattern) => {
                pattern.append(
                    createSvgNode('path', {
                        d: 'M10 56 L28 26 L46 56 Z',
                        fill: '#e97932',
                    }),
                    createSvgNode('path', {
                        d: 'M28 26 L28 56',
                        stroke: '#fff2d7',
                        'stroke-width': 2.5,
                    }),
                    createSvgNode('path', {
                        d: 'M49 54 L65 30 L80 54 Z',
                        fill: '#4779b9',
                    }),
                    createSvgNode('path', {
                        d: 'M65 30 L65 54',
                        stroke: '#e9f2ff',
                        'stroke-width': 2.3,
                    })
                );
            }
        );

        addPattern(
            'ff-live-area-parking',
            104,
            62,
            (pattern) => {
                pattern.append(
                    createSvgNode('path', {
                        d: 'M0 14 H104 M0 48 H104',
                        stroke: '#f4efe3',
                        'stroke-width': 2.5,
                        opacity: .7,
                        'stroke-dasharray': '16 10',
                    }),
                    createSvgNode('rect', {
                        x: 12, y: 20,
                        width: 28, height: 13,
                        rx: 5,
                        fill: '#b94d45',
                    }),
                    createSvgNode('rect', {
                        x: 57, y: 33,
                        width: 31, height: 13,
                        rx: 5,
                        fill: '#4a75a8',
                    })
                );
            }
        );

        addPattern(
            'ff-live-area-food-vendors',
            105,
            76,
            (pattern) => {
                pattern.append(
                    createSvgNode('rect', {
                        x: 9, y: 37,
                        width: 37, height: 25,
                        rx: 4,
                        fill: '#fff1d7',
                    }),
                    createSvgNode('path', {
                        d: 'M5 38 L13 20 H43 L50 38 Z',
                        fill: '#ef8a2f',
                    }),
                    createSvgNode('path', {
                        d: 'M19 20 V38 M32 20 V38',
                        stroke: '#fff5dc',
                        'stroke-width':
                                    Math.max(
                                        6,
                                        borderWidth
                                    ),
                    }),
                    createSvgNode('rect', {
                        x: 61, y: 29,
                        width: 34, height: 25,
                        rx: 5,
                        fill: '#e9e7dd',
                    })
                );
            }
        );

        addPattern(
            'ff-live-area-water',
            90,
            48,
            (pattern) => {
                pattern.append(
                    createSvgNode('path', {
                        d: 'M-8 14 C10 4 24 24 43 14 S75 4 98 14',
                        fill: 'none',
                        stroke: '#b8e8f5',
                        'stroke-width': 3.5,
                        opacity: .72,
                    }),
                    createSvgNode('path', {
                        d: 'M-15 36 C8 25 24 46 47 35 S77 26 105 36',
                        fill: 'none',
                        stroke: '#76cce5',
                        'stroke-width': 3,
                        opacity: .7,
                    })
                );
            }
        );

        return defs;
    };

    const illustratedAreaPresentation = (
        type,
        element = null
    ) => {
        const presentation = {
            forest: {
                base: '#4f7f48',
                fill:
                    'url(#ff-live-area-forest)',
                stroke: '#315f39',
            },
            water: {
                base: '#51b9dc',
                fill:
                    'url(#ff-live-area-water)',
                stroke: '#2a8bab',
            },
            camping: {
                base: '#86a965',
                fill:
                    'url(#ff-live-area-camping)',
                stroke: '#547d45',
            },
            parking: {
                base: '#878b87',
                fill:
                    'url(#ff-live-area-parking)',
                stroke: '#60645f',
            },
            food_vendors: {
                base: '#c8aa72',
                fill:
                    'url(#ff-live-area-food-vendors)',
                stroke: '#9b753e',
            },
            custom_area: {
                base: '#8b5cf6',
                fill: 'none',
                stroke: '#5b21b6',
            },
        }[type] ?? null;

        if (!presentation) {
            return null;
        }

        return {
            ...presentation,
            base:
                element?.area_color
                || presentation.base,
            opacity:
                Number(
                    element?.area_opacity
                    ?? 0.88
                ),
        };
    };

    const roundedIllustratedAreaPath = (
        polygon,
        cornerRatio = 0.18
    ) => {
        if (
            !Array.isArray(polygon)
            || polygon.length < 3
        ) {
            return '';
        }

        const scaled =
            polygon.map(
                (point) => ({
                    x: Number(point.x) * 1000,
                    y: Number(point.y) * 625,
                })
            );

        const corners =
            scaled.map(
                (current, index) => {
                    const previous =
                        scaled[
                            (
                                index
                                - 1
                                + scaled.length
                            )
                            % scaled.length
                        ];

                    const next =
                        scaled[
                            (index + 1)
                            % scaled.length
                        ];

                    return {
                        vertex: current,
                        entry: {
                            x:
                                current.x
                                + (
                                    previous.x
                                    - current.x
                                )
                                * cornerRatio,
                            y:
                                current.y
                                + (
                                    previous.y
                                    - current.y
                                )
                                * cornerRatio,
                        },
                        exit: {
                            x:
                                current.x
                                + (
                                    next.x
                                    - current.x
                                )
                                * cornerRatio,
                            y:
                                current.y
                                + (
                                    next.y
                                    - current.y
                                )
                                * cornerRatio,
                        },
                    };
                }
            );

        let d =
            `M ${corners[0].exit.x} ${corners[0].exit.y}`;

        for (
            let index = 0;
            index < corners.length;
            index++
        ) {
            const next =
                corners[
                    (index + 1)
                    % corners.length
                ];

            d +=
                ` L ${next.entry.x} ${next.entry.y}`;

            d +=
                ` Q ${next.vertex.x} ${next.vertex.y} ${next.exit.x} ${next.exit.y}`;
        }

        return `${d} Z`;
    };

    const createIllustratedAreaShape = (
        polygon,
        points,
        edgeStyle,
        attributes = {}
    ) => {
        if (
            edgeStyle === 'rounded'
            && polygon.length >= 3
        ) {
            return createSvgNode(
                'path',
                {
                    d:
                        roundedIllustratedAreaPath(
                            polygon
                        ),
                    ...attributes,
                }
            );
        }

        return createSvgNode(
            'polygon',
            {
                points,
                ...attributes,
            }
        );
    };

    const illustratedAreaBorderWidth = (
        style
    ) => ({
        thin: 2,
        normal: 4,
        thick: 7,
    }[style] || 4);

    const illustratedAreaBorderDashArray = (
        style,
        width = 4
    ) => {
        if (style === 'dashed') {
            return `${
                Math.max(
                    8,
                    width * 3
                )
            } ${
                Math.max(
                    5,
                    width * 1.8
                )
            }`;
        }

        if (style === 'dotted') {
            return `1 ${
                Math.max(
                    6,
                    width * 2.2
                )
            }`;
        }

        return null;
    };

    const renderIllustratedAreas = (
        elements
    ) => {
        if (! illustratedAreas) {
            return;
        }

        illustratedAreas.replaceChildren(
            buildIllustratedAreaDefs()
        );

        elements.forEach((element) => {
            const polygon =
                Array.isArray(element.polygon)
                    ? element.polygon
                    : [];

            if (polygon.length < 3) {
                return;
            }

            const points = polygon
                .map((point) =>
                    `${Number(point.x) * 1000},${Number(point.y) * 625}`
                )
                .join(' ');

            const presentation =
                illustratedAreaPresentation(
                    element.type,
                    element
                );

            const edgeStyle =
                element.area_edge_style
                || 'straight';

            const borderStyle =
                element.area_border_style
                || 'solid';

            const borderWidth =
                illustratedAreaBorderWidth(
                    element.area_border_width
                    || 'normal'
                );

            const borderDashArray =
                illustratedAreaBorderDashArray(
                    borderStyle,
                    borderWidth
                );

            if (presentation) {
                const fillStyle =
                    element.area_fill_style
                    || 'illustrated';

                if (fillStyle === 'outline') {
                    illustratedAreas.append(
                        createIllustratedAreaShape(
                            polygon,
                            points,
                            edgeStyle,
                            {
                                fill: 'none',
                                stroke:
                                    presentation.stroke,
                                'stroke-width': 6,
                                'stroke-linejoin':
                                    'round',
                                'stroke-dasharray':
                                    borderDashArray
                                    ?? '',
                                'stroke-linecap':
                                    borderStyle
                                    === 'dotted'
                                        ? 'round'
                                        : 'butt',
                            }
                        )
                    );

                    return;
                }

                if (fillStyle === 'solid') {
                    illustratedAreas.append(
                        createIllustratedAreaShape(
                            polygon,
                            points,
                            edgeStyle,
                            {
                                fill:
                                    presentation.base,
                                'fill-opacity':
                                    presentation.opacity,
                                stroke:
                                    presentation.stroke,
                                'stroke-width':
                                    borderWidth,
                                'stroke-linejoin':
                                    'round',
                                'stroke-dasharray':
                                    borderDashArray
                                    ?? '',
                                'stroke-linecap':
                                    borderStyle
                                    === 'dotted'
                                        ? 'round'
                                        : 'butt',
                            }
                        )
                    );

                    return;
                }

                illustratedAreas.append(
                    createIllustratedAreaShape(
                        polygon,
                        points,
                        edgeStyle,
                        {
                            fill:
                                'rgba(44,53,36,.17)',
                            transform:
                                'translate(5 7)',
                        }
                    ),
                    createIllustratedAreaShape(
                        polygon,
                        points,
                        edgeStyle,
                        {
                            fill:
                                presentation.base,
                            'fill-opacity':
                                presentation.opacity,
                            stroke:
                                presentation.stroke,
                            'stroke-width':
                                    borderWidth,
                            'stroke-linejoin':
                                'round',
                            'stroke-dasharray':
                                borderDashArray
                                ?? '',
                            'stroke-linecap':
                                borderStyle
                                === 'dotted'
                                    ? 'round'
                                    : 'butt',
                        }
                    ),
                    createIllustratedAreaShape(
                        polygon,
                        points,
                        edgeStyle,
                        {
                            fill:
                                presentation.fill,
                            opacity:
                                Math.min(
                                    1,
                                    presentation.opacity
                                    + .08
                                ),
                            stroke: 'none',
                        }
                    )
                );

                return;
            }

            const colors =
                illustratedAreaColors[
                    element.type
                ]
                ?? {
                    fill:
                        'rgba(15,118,110,.16)',
                    stroke: '#0f766e',
                };

            illustratedAreas.append(
                createIllustratedAreaShape(
                    polygon,
                    points,
                    edgeStyle,
                    {
                        fill: colors.fill,
                        stroke: colors.stroke,
                        'stroke-width':
                                    borderWidth,
                        'stroke-linejoin':
                            'round',
                    }
                )
            );
        });
    };

    const illustratedPathStyle = (
        type,
        element = null
    ) => {
        const style = {
            road: {
                stroke: '#e6d6a8',
                edge: '#765f3f',
                edgeOpacity: .78,
                edgeExtra: 8,
                highlightOpacity: .34,
                highlightWidth: .16,
                width: 22,
                dash: '',
            },
            walkway: {
                stroke: '#efe2bc',
                edge: '#9b8762',
                edgeOpacity: .34,
                edgeExtra: 3,
                highlightOpacity: .22,
                highlightWidth: .13,
                width: 14,
                dash: '',
            },
            shuttle: {
                stroke: '#60a5fa',
                edge: '#416f86',
                edgeOpacity: .46,
                edgeExtra: 4,
                highlightOpacity: 0,
                highlightWidth: .12,
                width: 11,
                dash: '18 12',
            },
            service: {
                stroke: '#94a3b8',
                edge: '#475569',
                edgeOpacity: .70,
                edgeExtra: 6,
                highlightOpacity: 0,
                highlightWidth: .12,
                width: 10,
                dash: '12 9',
            },
            restricted: {
                stroke: '#ef4444',
                edge: '#991b1b',
                edgeOpacity: .55,
                edgeExtra: 5,
                highlightOpacity: 0,
                highlightWidth: .12,
                width: 10,
                dash: '12 8',
            },
            other: {
                stroke: '#c4b5fd',
                edge: '#7c6fa8',
                edgeOpacity: .38,
                edgeExtra: 3,
                highlightOpacity: .12,
                highlightWidth: .12,
                width: 10,
                dash: '',
            },
            trail: {
                stroke: '#ead9aa',
                edge: '#927b55',
                edgeOpacity: .22,
                edgeExtra: 2,
                highlightOpacity: 0,
                highlightWidth: .12,
                width: 13,
                dash: '8 8',
            },
        }[type] ?? {
            stroke: '#ead8a8',
            edge: '#927b55',
            edgeOpacity: .22,
            edgeExtra: 2,
            highlightOpacity: 0,
            highlightWidth: .12,
            width: 12,
            dash: '7 8',
        };

        const width =
            element?.path_width
                ? Number(element.path_width)
                : style.width;

        const lineStyle =
            element?.path_line_style
            || 'default';

        let dash = style.dash;

        if (lineStyle === 'solid') {
            dash = '';
        } else if (lineStyle === 'dashed') {
            dash =
                `${Math.max(10, width * 1.4)} ${Math.max(7, width * .85)}`;
        } else if (lineStyle === 'dotted') {
            dash =
                `${Math.max(2, width * .18)} ${Math.max(6, width * .72)}`;
        }

        return {
            ...style,
            stroke:
                element?.path_color
                || style.stroke,
            width,
            dash,
        };
    };

    const smoothSvgPath = (
        points
    ) => {
        if (points.length < 2) {
            return '';
        }

        const scaled = points.map(
            (point) => ({
                x: Number(point.x) * 1000,
                y: Number(point.y) * 625,
            })
        );

        if (scaled.length === 2) {
            return `M ${scaled[0].x} ${scaled[0].y} L ${scaled[1].x} ${scaled[1].y}`;
        }

        let path =
            `M ${scaled[0].x} ${scaled[0].y}`;

        for (
            let index = 1;
            index < scaled.length - 1;
            index++
        ) {
            const current =
                scaled[index];

            const next =
                scaled[index + 1];

            const midpoint = {
                x:
                    (current.x + next.x)
                    / 2,
                y:
                    (current.y + next.y)
                    / 2,
            };

            path +=
                ` Q ${current.x} ${current.y} ${midpoint.x} ${midpoint.y}`;
        }

        const last =
            scaled[
                scaled.length - 1
            ];

        path +=
            ` T ${last.x} ${last.y}`;

        return path;
    };

    const normalizeIllustratedPathLabelAngle = (
        angle
    ) => {
        let normalized = angle;

        while (normalized > 180) {
            normalized -= 360;
        }

        while (normalized <= -180) {
            normalized += 360;
        }

        if (normalized > 90) {
            normalized -= 180;
        } else if (normalized < -90) {
            normalized += 180;
        }

        return normalized;
    };

    const illustratedPathLabelPlacement = (
        points
    ) => {
        if (points.length === 0) {
            return null;
        }

        const index =
            Math.floor(
                (points.length - 1)
                / 2
            );

        const point =
            points[index];

        const previous =
            points[
                Math.max(
                    0,
                    index - 1
                )
            ];

        const next =
            points[
                Math.min(
                    points.length - 1,
                    index + 1
                )
            ];

        const dx =
            (
                Number(next.x)
                - Number(previous.x)
            ) * 1000;

        const dy =
            (
                Number(next.y)
                - Number(previous.y)
            ) * 625;

        const angle =
            normalizeIllustratedPathLabelAngle(
                Math.atan2(
                    dy,
                    dx
                )
                * 180
                / Math.PI
            );

        return {
            x:
                Number(point.x),
            y:
                Number(point.y),
            angle,
        };
    };

    const illustratedPathDirectionMarkers = (
        points,
        size = 12
    ) => {
        if (points.length < 2) {
            return [];
        }

        const segments = [];
        let totalLength = 0;

        for (
            let index = 1;
            index < points.length;
            index++
        ) {
            const start =
                points[index - 1];

            const end =
                points[index];

            const dx =
                (
                    Number(end.x)
                    - Number(start.x)
                ) * 1000;

            const dy =
                (
                    Number(end.y)
                    - Number(start.y)
                ) * 625;

            const length =
                Math.hypot(
                    dx,
                    dy
                );

            if (length <= 0) {
                continue;
            }

            segments.push({
                start,
                end,
                dx,
                dy,
                length,
                from:
                    totalLength,
                to:
                    totalLength
                    + length,
            });

            totalLength += length;
        }

        if (
            segments.length === 0
            || totalLength <= 0
        ) {
            return [];
        }

        const fractions =
            totalLength < 260
                ? [0.72]
                : (
                    totalLength < 520
                        ? [
                            0.32,
                            0.72,
                        ]
                        : [
                            0.20,
                            0.42,
                            0.78,
                        ]
                );

        return fractions
            .map(
                (fraction) => {
                    const target =
                        totalLength
                        * fraction;

                    const segment =
                        segments.find(
                            (candidate) =>
                                target
                                >= candidate.from
                                && target
                                <= candidate.to
                        )
                        ?? segments.at(-1);

                    if (!segment) {
                        return '';
                    }

                    const local =
                        (
                            target
                            - segment.from
                        )
                        / segment.length;

                    const x =
                        Number(
                            segment.start.x
                        ) * 1000
                        + segment.dx
                        * local;

                    const y =
                        Number(
                            segment.start.y
                        ) * 625
                        + segment.dy
                        * local;

                    const ux =
                        segment.dx
                        / segment.length;

                    const uy =
                        segment.dy
                        / segment.length;

                    const px = -uy;
                    const py = ux;

                    const tipX =
                        x
                        + ux
                        * size
                        * .55;

                    const tipY =
                        y
                        + uy
                        * size
                        * .55;

                    const baseX =
                        x
                        - ux
                        * size
                        * .45;

                    const baseY =
                        y
                        - uy
                        * size
                        * .45;

                    return [
                        `${tipX},${tipY}`,
                        `${
                            baseX
                            + px
                            * size
                            * .48
                        },${
                            baseY
                            + py
                            * size
                            * .48
                        }`,
                        `${
                            baseX
                            - px
                            * size
                            * .48
                        },${
                            baseY
                            - py
                            * size
                            * .48
                        }`,
                    ].join(' ');
                }
            )
            .filter(Boolean);
    };

    const renderIllustratedPaths = (
        elements
    ) => {
        if (!illustratedPaths) {
            return;
        }

        illustratedPaths.replaceChildren();

        elements.forEach((element) => {
            const path =
                Array.isArray(element.path)
                    ? element.path
                    : [];

            if (path.length < 2) {
                return;
            }

            const style =
                illustratedPathStyle(
                    element.path_type
                    || 'trail',
                    element
                );

            const d =
                smoothSvgPath(path);

            illustratedPaths.append(
                createSvgNode(
                    'path',
                    {
                        d,
                        fill: 'none',
                        stroke:
                            style.edge
                            || '#6b5a44',
                        'stroke-opacity':
                            String(
                                style.edgeOpacity
                                ?? .34
                            ),
                        'stroke-width':
                            style.width
                            + (
                                style.edgeExtra
                                ?? 3
                            ),
                        'stroke-linejoin':
                            'round',
                        'stroke-linecap':
                            'round',
                    }
                ),
                createSvgNode(
                    'path',
                    {
                        d,
                        fill: 'none',
                        stroke:
                            style.stroke,
                        'stroke-width':
                            style.width,
                        'stroke-linejoin':
                            'round',
                        'stroke-linecap':
                            'round',
                        ...(style.dash
                            ? {
                                'stroke-dasharray':
                                    style.dash,
                            }
                            : {}),
                    }
                ),
                createSvgNode(
                    'path',
                    {
                        d,
                        fill: 'none',
                        stroke:
                            '#fff8dc',
                        'stroke-opacity':
                            style.dash
                                ? '0'
                                : String(
                                    style.highlightOpacity
                                    ?? 0
                                ),
                        'stroke-width':
                            Math.max(
                                2,
                                style.width
                                * (
                                    style.highlightWidth
                                    ?? .12
                                )
                            ),
                        'stroke-linejoin':
                            'round',
                        'stroke-linecap':
                            'round',
                    }
                )
            );

            if (
                element.path_show_arrow
            ) {
                const directionMarkers =
                    illustratedPathDirectionMarkers(
                        path,
                        Math.max(
                            11,
                            style.width * .9
                        )
                    );

                directionMarkers.forEach(
                    (arrowPoints) => {
                        illustratedPaths.append(
                            createSvgNode(
                                'polygon',
                                {
                                    points:
                                        arrowPoints,
                                    fill:
                                        style.stroke,
                                    stroke:
                                        '#ffffff',
                                    'stroke-width':
                                        Math.max(
                                            1.5,
                                            style.width * .12
                                        ),
                                    'stroke-linejoin':
                                        'round',
                                }
                            )
                        );
                    }
                );
            }

            if (
                element.path_show_label
                && element.label
            ) {
                const placement =
                    illustratedPathLabelPlacement(
                        path
                    );

                if (placement) {
                    const text =
                        createSvgNode(
                            'text',
                            {
                                x:
                                    Number(
                                        placement.x
                                    ) * 1000,
                                y:
                                    Number(
                                        placement.y
                                    ) * 625,
                                transform:
                                    `rotate(${
                                        placement.angle
                                    } ${
                                        Number(
                                            placement.x
                                        ) * 1000
                                    } ${
                                        Number(
                                            placement.y
                                        ) * 625
                                    })`,
                                fill:
                                    '#263238',
                                stroke:
                                    '#ffffff',
                                'stroke-width':
                                    5,
                                'paint-order':
                                    'stroke',
                                'stroke-linejoin':
                                    'round',
                                'text-anchor':
                                    'middle',
                                'dominant-baseline':
                                    'middle',
                                'font-size':
                                    Math.max(
                                        13,
                                        style.width * .8
                                    ),
                                'font-weight':
                                    900,
                            }
                        );

                    text.textContent =
                        element.label;

                    illustratedPaths.append(
                        text
                    );
                }
            }
        });
    };

    const renderIllustratedElements = (
        elements
    ) => {
        if (! illustratedElements) {
            return;
        }

        illustratedElements.replaceChildren();

        elements.forEach((element) => {
            const wrapper =
                document.createElement(
                    'div'
                );

            wrapper.className =
                'absolute flex items-center justify-center';

            wrapper.dataset.illustratedElementId =
                String(element.id);

            wrapper.dataset.illustratedElementType =
                String(element.type || '');

            wrapper.dataset.illustratedElementLabel =
                String(element.label || '');

            wrapper.style.left =
                `${Number(element.x) * 100}%`;

            wrapper.style.top =
                `${Number(element.y) * 100}%`;

            wrapper.style.width =
                `${Number(element.width) * 100}%`;

            wrapper.style.height =
                `${Number(element.height) * 100}%`;

            wrapper.style.zIndex =
                String(element.z_index ?? 1);

            wrapper.style.transform =
                `translate(-50%,-50%) rotate(${Number(element.rotation || 0)}deg)`;

            const hasDecoratedArea =
                illustratedAreaTypes.has(
                    element.type
                )
                && Array.isArray(
                    element.polygon
                )
                && element.polygon.length >= 3;

            const content =
                document.createElement(
                    'div'
                );

            content.className =
                'flex h-full w-full flex-col items-center justify-center';

            if (
                element.type !== 'map_text'
            ) {
                content.style.transform =
                    `scale(${
                        element.flip_x ? -1 : 1
                    }, ${
                        element.flip_y ? -1 : 1
                    })`;
            }

            const svg =
                createSvgNode(
                    'svg',
                    {
                        viewBox:
                            '0 0 180 120',
                        preserveAspectRatio:
                            'xMidYMid meet',
                        class:
                            'min-h-0 w-full flex-1 overflow-visible',
                        'aria-hidden':
                            'true',
                    }
                );

            const use =
                createSvgNode(
                    'use',
                    {
                        href:
                            `#ff-live-map-${String(element.type).replaceAll('_', '-')}`,
                    }
                );

            svg.append(use);

            const label =
                document.createElement(
                    'span'
                );

            const majorStage =
                [
                    'main_stage',
                    'secondary_stage',
                ].includes(
                    element.type
                );

            const smallAmenity =
                [
                    'restroom',
                    'water_station',
                    'bar',
                    'showers',
                    'merch',
                    'lost_found',
                    'atm',
                    'charging',
                    'guest_services',
                    'shuttle',
                    'accessible',
                    'security',
                    'rv_camping',
                ].includes(
                    element.type
                );

            const semanticClass = {
                main_stage:
                    'bg-fuchsia-700 text-white border-2 border-white/70 uppercase tracking-wide',
                secondary_stage:
                    'bg-violet-700 text-white border-2 border-white/70 uppercase tracking-wide',
                entrance:
                    'bg-amber-800 text-white border border-white/60 uppercase',
                medical:
                    'bg-red-700 text-white border border-white/70',
                information:
                    'bg-sky-700 text-white border border-white/70',
                water_station:
                    'bg-sky-700 text-white border border-white/70',
                bar:
                    'bg-amber-700 text-white border border-white/60',
                food_vendors:
                    'bg-amber-700 text-white border border-white/60',
                merch:
                    'bg-amber-700 text-white border border-white/60',
                accessible:
                    'bg-blue-800 text-white border border-white/70',
                security:
                    'bg-slate-700 text-white border border-white/60',
                map_text:
                    'bg-white/85 text-zinc-800 uppercase tracking-wide text-shadow-sm',
            }[element.type]
                ?? 'bg-white/90 text-zinc-900';

            label.className =
                `max-w-full truncate rounded-full px-2 py-0.5 font-black leading-tight shadow-sm ${semanticClass} ${
                    majorStage
                        ? 'text-xs sm:text-sm'
                        : smallAmenity
                            ? 'text-[9px] sm:text-[10px]'
                            : 'text-[10px] sm:text-xs'
                }`;

            label.textContent =
                element.label
                || 'Map feature';

            const labelPosition =
                element.type === 'map_text'
                    ? 'center'
                    : (
                        element.label_position
                        || 'below'
                    );

            const labelSize =
                element.type === 'map_text'
                    ? 'normal'
                    : (
                        element.label_size
                        || 'normal'
                    );

            const labelStyle =
                element.type === 'map_text'
                    ? 'auto'
                    : (
                        element.label_style
                        || 'auto'
                    );

            const labelSpacing =
                element.type === 'map_text'
                    ? 'normal'
                    : (
                        element.label_spacing
                        || 'normal'
                    );

            const labelConnector =
                element.type !== 'map_text'
                && element.label_connector
                && labelPosition !== 'center';

            if (labelConnector) {
                label.style.position =
                    'relative';
                label.style.overflow =
                    'visible';

                const connector =
                    document.createElement(
                        'span'
                    );

                connector.setAttribute(
                    'aria-hidden',
                    'true'
                );

                connector.style.position =
                    'absolute';
                connector.style.borderRadius =
                    '999px';
                connector.style.background =
                    'rgba(51,65,85,.58)';

                if (
                    labelPosition === 'above'
                    || labelPosition === 'below'
                ) {
                    connector.style.left =
                        '50%';
                    connector.style.width =
                        '2px';
                    connector.style.height =
                        '.55rem';
                    connector.style.transform =
                        'translateX(-50%)';

                    if (
                        labelPosition === 'above'
                    ) {
                        connector.style.top =
                            'calc(100% + .08rem)';
                    } else {
                        connector.style.bottom =
                            'calc(100% + .08rem)';
                    }
                } else {
                    connector.style.top =
                        '50%';
                    connector.style.width =
                        '.65rem';
                    connector.style.height =
                        '2px';
                    connector.style.transform =
                        'translateY(-50%)';

                    if (
                        labelPosition === 'left'
                    ) {
                        connector.style.left =
                            'calc(100% + .08rem)';
                    } else {
                        connector.style.right =
                            'calc(100% + .08rem)';
                    }
                }

                label.append(
                    connector
                );
            }

            if (
                element.type !== 'map_text'
            ) {
                content.style.gap =
                    {
                        tight: '.08rem',
                        normal: '.24rem',
                        far: '.58rem',
                    }[labelSpacing]
                    || '.24rem';

                label.style.marginTop =
                    '0';
            }

            if (
                labelStyle === 'light'
            ) {
                label.style.border =
                    '1px solid rgba(255,255,255,.85)';
                label.style.background =
                    'rgba(255,255,255,.94)';
                label.style.color =
                    '#18181b';
                label.style.textShadow =
                    'none';
                label.style.boxShadow =
                    '0 2px 8px rgba(15,23,42,.18)';
            } else if (
                labelStyle === 'dark'
            ) {
                label.style.border =
                    '1px solid rgba(255,255,255,.42)';
                label.style.background =
                    'rgba(30,41,59,.92)';
                label.style.color =
                    '#ffffff';
                label.style.textShadow =
                    'none';
                label.style.boxShadow =
                    '0 2px 8px rgba(15,23,42,.28)';
            } else if (
                labelStyle === 'outline'
            ) {
                label.style.border =
                    '2px solid rgba(255,255,255,.95)';
                label.style.background =
                    'rgba(255,255,255,.10)';
                label.style.color =
                    '#263238';
                label.style.textShadow =
                    '-1px -1px 0 #fff,1px -1px 0 #fff,-1px 1px 0 #fff,1px 1px 0 #fff';
                label.style.boxShadow =
                    'none';
            }

            if (
                labelSize === 'small'
            ) {
                label.style.fontSize =
                    '.56rem';
                label.style.padding =
                    '.16rem .38rem';
            } else if (
                labelSize === 'large'
            ) {
                label.style.fontSize =
                    '.88rem';
                label.style.padding =
                    '.30rem .62rem';
            }

            if (
                element.type !== 'map_text'
            ) {
                if (labelPosition === 'above') {
                    content.style.flexDirection =
                        'column-reverse';
                    label.style.marginBottom =
                        '.25rem';
                    label.style.marginTop =
                        '0';
                } else if (
                    labelPosition === 'left'
                ) {
                    content.style.flexDirection =
                        'row-reverse';
                    content.style.gap =
                        '.3rem';
                    label.style.marginTop =
                        '0';
                    label.style.flex =
                        '0 0 auto';
                } else if (
                    labelPosition === 'right'
                ) {
                    content.style.flexDirection =
                        'row';
                    content.style.gap =
                        '.3rem';
                    label.style.marginTop =
                        '0';
                    label.style.flex =
                        '0 0 auto';
                } else if (
                    labelPosition === 'center'
                ) {
                    content.style.position =
                        'relative';
                    label.style.position =
                        'absolute';
                    label.style.left =
                        '50%';
                    label.style.top =
                        '50%';
                    label.style.zIndex =
                        '3';
                    label.style.marginTop =
                        '0';
                    label.style.transform =
                        'translate(-50%,-50%)';
                    label.style.maxWidth =
                        '130%';
                }
            }

            const showLabel =
                element.show_label
                !== false;

            if (
                element.type === 'map_text'
            ) {
                const textStyle =
                    element.text_style
                    || 'light';

                const textScale =
                    Number(
                        element.text_scale
                        ?? 1
                    );

                const styleClass = {
                    plain:
                        'bg-transparent text-zinc-800 shadow-none',
                    light:
                        'bg-white/85 text-zinc-800 shadow-md',
                    dark:
                        'border-2 border-white/60 bg-slate-800/90 text-white shadow-lg',
                    accent:
                        'border-2 border-white/60 bg-fuchsia-700 text-white shadow-lg',
                    outline:
                        'border-2 border-white bg-white/10 text-zinc-800 shadow-none',
                }[textStyle]
                    ?? 'bg-white/85 text-zinc-800 shadow-md';

                label.className =
                    `max-w-full whitespace-normal rounded-xl px-3 py-1.5 text-center font-black uppercase leading-tight tracking-wide ${styleClass}`;

                label.style.fontSize =
                    `${Math.max(
                        .70,
                        Math.min(
                            2,
                            textScale
                        )
                    )}rem`;

                content.append(label);
            } else if (hasDecoratedArea) {
                if (showLabel) {
                    content.append(label);
                }
            } else {
                content.append(svg);

                if (showLabel) {
                    content.append(label);
                }
            }

            wrapper.append(content);
            illustratedElements.append(
                wrapper
            );
        });
    };

    const createIllustratedMemberPin = (
        member
    ) => {
        const position =
            member.illustrated_map_position;

        if (
            !position
            || !Number.isFinite(
                Number(position.x)
            )
            || !Number.isFinite(
                Number(position.y)
            )
        ) {
            return null;
        }

        const pin =
            document.createElement('a');

        pin.href =
            member.profile_url || '#';

        pin.className =
            'absolute z-[300] flex -translate-x-1/2 -translate-y-1/2 flex-col items-center text-center no-underline';

        pin.style.left =
            `${Number(position.x) * 100}%`;

        pin.style.top =
            `${Number(position.y) * 100}%`;

        if (!member.profile_url) {
            pin.removeAttribute('href');
        }

        const avatar =
            document.createElement('span');

        avatar.className =
            member.is_current_user
                ? 'flex h-12 w-12 items-center justify-center overflow-hidden rounded-full border-4 border-white bg-emerald-600 text-xs font-black text-white shadow-xl'
                : member.is_friend
                    ? 'flex h-12 w-12 items-center justify-center overflow-hidden rounded-full border-4 border-white bg-fuchsia-600 text-xs font-black text-white shadow-xl'
                    : 'flex h-12 w-12 items-center justify-center overflow-hidden rounded-full border-4 border-white bg-blue-600 text-xs font-black text-white shadow-xl';

        if (member.profile_photo_url) {
            const image =
                document.createElement(
                    'img'
                );

            image.src =
                member.profile_photo_url;
            image.alt = '';
            image.className =
                'h-full w-full object-cover';

            avatar.append(image);
        } else {
            avatar.textContent =
                member.is_current_user
                    ? 'YOU'
                    : member.initials
                        || '?';
        }

        const label =
            document.createElement('span');

        label.className =
            'mt-1 max-w-32 rounded-full bg-slate-950/90 px-2.5 py-1 text-[11px] font-black leading-tight text-white shadow-lg';

        const area =
            member.illustrated_map_area
                ?.label
            || member.current_zone?.name;

        label.textContent =
            member.is_current_user
                ? (
                    area
                        ? `You · ${area}`
                        : 'You'
                )
                : (
                    area
                        ? `${member.name} · ${area}`
                        : member.name
                );

        pin.append(
            avatar,
            label
        );

        return pin;
    };

    const renderIllustratedMembers = (
        members
    ) => {
        if (! illustratedMembers) {
            return;
        }

        const visibleIds = new Set();
        members.forEach((member) => {
            const pin =
                createIllustratedMemberPin(
                    member
                );

            if (pin) {
                const id = String(member.id);
                visibleIds.add(id);
                const existing = illustratedMemberPins.get(id);
                if (existing) {
                    const from = [Number.parseFloat(existing.style.left), Number.parseFloat(existing.style.top)];
                    const target = [Number.parseFloat(pin.style.left), Number.parseFloat(pin.style.top)];
                    if (pin.hasAttribute('href')) existing.href = pin.href;
                    else existing.removeAttribute('href');
                    existing.replaceChildren(...pin.childNodes);
                    moveMemberPin(`illustrated:${id}`, from, target, point => {
                        existing.style.left = `${point[0]}%`;
                        existing.style.top = `${point[1]}%`;
                    }, memberMotionEligibility.get(id) === true && from.every(Number.isFinite), 'illustrated');
                } else {
                    illustratedMembers.append(pin);
                    illustratedMemberPins.set(id, pin);
                }
            }
        });
        illustratedMemberPins.forEach((pin, id) => {
            if (visibleIds.has(id)) return;
            cancelMemberMotion(`illustrated:${id}`);
            pin.remove();
            illustratedMemberPins.delete(id);
        });
    };

    const renderIllustratedMap = (
        mapData,
        members
    ) => {
        clearIllustratedGuideHighlights();

        illustratedMapAvailable =
            Boolean(
                mapData?.available
                && mapData?.calibrated
                && illustratedCanvas
            );

        mapViewControls?.classList.toggle(
            'hidden',
            !illustratedMapAvailable
        );

        mapViewControls?.classList.toggle(
            'flex',
            illustratedMapAvailable
        );

        if (! illustratedMapAvailable) {
            clearIllustratedMemberPins();
            setMapView('geographic');
            return;
        }

        const terrain =
            illustratedTerrainStyles[
                mapData.background_style
            ]
            ?? illustratedTerrainStyles
                .grassland;

        if (illustratedTerrain) {
            illustratedTerrain.style.background =
                terrain.background;

            illustratedTerrain.style.backgroundSize =
                terrain.backgroundSize
                || '';

            illustratedTerrain.style.backgroundPosition =
                terrain.backgroundPosition
                || '';
        }

        const elements =
            Array.isArray(mapData.elements)
                ? mapData.elements
                : [];

        renderIllustratedGuide(
            elements
        );

        renderIllustratedAreas(
            elements
        );

        renderIllustratedPaths(
            elements
        );

        renderIllustratedElements(
            elements
        );

        renderIllustratedMembers(
            members
        );

        /*
         * The illustrated map is the attendee-friendly default.
         * Geographic Leaflet remains one tap away.
         */
        if (
            activeMapView === 'geographic'
            && !mapElement.dataset
                .illustratedViewInitialized
        ) {
            mapElement.dataset
                .illustratedViewInitialized =
                    'true';

            setMapView(
                'illustrated'
            );
        }
    };

    const renderMembers = (
        members,
        { fitViewport = false } = {}
    ) => {
        const memberIds = new Set();

        navigableMemberIds.clear();
        memberPresence.clear();
        currentUserMemberId = null;

        members.forEach((member) => {
            if (member.is_current_user) {
                currentUserMemberId = String(member.id);
            }
            const latitude = Number.parseFloat(
                member.latitude
            );

            const longitude = Number.parseFloat(
                member.longitude
            );

            if (
                ! Number.isFinite(latitude)
                || ! Number.isFinite(longitude)
            ) {
                return;
            }

            memberIds.add(member.id);

            const id = String(member.id);
            const previous = memberFixes.get(id);
            const updatedAt = Date.parse(member.location_updated_at);
            const accuracy = Number(member.accuracy_meters);
            const distance = previous ? L.latLng(previous.latitude, previous.longitude).distanceTo([latitude, longitude]) : 0;
            const uncertainty = Math.max(5, Number.isFinite(accuracy) ? accuracy : 0, previous?.accuracy || 0);
            memberMotionEligibility.set(id, Boolean(previous && member.is_friend && !member.is_current_user
                && Number.isFinite(updatedAt) && updatedAt > previous.updatedAt
                && distance > uncertainty && distance <= 2000 && Math.abs(longitude - previous.longitude) <= 180));
            memberFixes.set(id, { latitude, longitude, updatedAt, accuracy: Number.isFinite(accuracy) ? accuracy : 0 });

            memberPresence.set(
                String(member.id),
                {
                    id:
                        String(member.id),

                    name:
                        member.name
                        || 'Member',

                    current_zone:
                        member.current_zone
                            ?.name
                        || null,

                    nearest_landmark:
                        member.nearest_landmark
                            ?.name
                        || null,

                    current_location:
                        member.current_location
                        || null,

                    updated_human:
                        member
                            .location_updated_at_human
                        || null,
                }
            );

            if (
                member.is_friend
                && !member.is_current_user
            ) {
                navigableMemberIds.add(
                    member.id
                );
            }

            const existingMarker =
                memberMarkers.get(member.id);

            if (existingMarker) {
                const displayed = existingMarker.getLatLng();
                moveMemberPin(`geographic:${id}`, [displayed.lat, displayed.lng], [latitude, longitude],
                    point => existingMarker.setLatLng(point), memberMotionEligibility.get(id) === true, 'geographic');

                if (
                    activeNavigationRoute
                        ?.navigation_mode
                        === 'direct'
                    && activeNavigationRoute
                        ?.target_type
                        === 'member'
                    && Number(
                        activeNavigationMemberId
                    ) === Number(
                        member.id
                    )
                ) {
                    activeNavigationRoute
                        .to_endpoint
                        .latitude =
                            latitude;

                    activeNavigationRoute
                        .to_endpoint
                        .longitude =
                            longitude;

                    navigationEndMarker
                        ?.setLatLng([
                            latitude,
                            longitude,
                        ]);
                }

                existingMarker.setIcon(
                    createMemberIcon(member)
                );

                existingMarker.setPopupContent(
                    createMemberPopup(member)
                );

                return;
            }

            const marker = L.marker(
                [
                    latitude,
                    longitude,
                ],
                {
                    icon: createMemberIcon(member),
                }
            )
                .addTo(peopleLayer)
                .bindPopup(
                    createMemberPopup(member)
                );

            memberMarkers.set(
                member.id,
                marker
            );

            if (
                layerVisibility.people
                && member.is_friend
                && !member.is_current_user
            ) {
                highlightMarker(marker);
            }
        });

        removeMissingMarkers(memberIds);
        updateCount(memberIds.size);
        publishMemberMarkerState();

        updateCenterMeControl();

        if (fitViewport) {
            fitMapToVisibleMarkers();
        }
    };

    const loadLocations = async (
        { fitViewport = false } = {}
    ) => {
        hideError();

        locationsLoadController?.abort();

        const requestController =
            new AbortController();

        locationsLoadController =
            requestController;

        const requestSequence =
            ++locationsLoadSequence;

        if (refreshButton) {
            refreshButton.disabled = true;
        }

        try {
            const response = await fetch(
                locationsUrl,
                {
                    method: 'GET',

                    headers: {
                        Accept: 'application/json',
                        'X-Requested-With':
                            'XMLHttpRequest',
                    },

                    credentials: 'same-origin',
                    cache: 'no-store',
                    signal:
                        requestController.signal,
                }
            );

            if (! response.ok) {
                throw new Error(
                    'The live locations could not be loaded.'
                );
            }

            const payload = await response.json();

            if (
                requestController.signal.aborted
                || requestSequence
                    !== locationsLoadSequence
                || ! mapIsUsable()
            ) {
                return;
            }

            const liveMembers =
                Array.isArray(payload.members)
                    ? payload.members
                    : [];

            updateGeofenceMeta(payload.festival);

            const previousViewerLiveLocationState =
                viewerLiveLocationState;

            viewerLiveLocationState =
                payload.viewer_live_location
                ?? null;

            updateViewerLiveLocationStatus(
                previousViewerLiveLocationState,
                viewerLiveLocationState
            );

            updateCenterMeControl();

            renderMembers(
                liveMembers,
                { fitViewport }
            );

            renderIllustratedMap(
                payload.illustrated_map,
                liveMembers
            );

            loadingElement?.remove();
        } catch (error) {
            if (
                error?.name === 'AbortError'
            ) {
                return;
            }

            if (
                requestSequence
                !== locationsLoadSequence
            ) {
                return;
            }

            showError(
                error instanceof Error
                    ? error.message
                    : 'The live locations could not be loaded.'
            );
        } finally {
            if (
                locationsLoadController
                === requestController
            ) {
                locationsLoadController = null;

                if (refreshButton) {
                    refreshButton.disabled = false;
                }
            }
        }
    };

    const isLiveLocationNetworkError = (
        error
    ) => (
        navigator.onLine === false
        || error instanceof TypeError
    );

    const sendLiveLocation = async (
        position
    ) => {
        if (! locationUpdateUrl) {
            return;
        }

        const accuracy =
            Number.parseFloat(
                position?.coords?.accuracy
            );

        if (
            Number.isFinite(accuracy)
            && accuracy > liveLocationMaxAccuracyMeters
        ) {
            showSharingStatus(
                `Your current GPS fix is only accurate to about ${Math.round(
                    accuracy
                )} meters. Location sharing is still on and FestFriends will wait for a better reading before moving your pin.`,
                'info'
            );

            return;
        }

        pendingLiveLocationPosition =
            position;

        if (locationUpdateInProgress) {
            return;
        }

        const now = Date.now();

        if (now - lastLocationSentAt < 10000) {
            return;
        }

        if (navigator.onLine === false) {
            liveLocationNetworkOffline = true;

            showSharingStatus(
                'Your internet connection is offline. Location sharing is still on; FestFriends will send your latest location automatically when the connection returns.',
                'info'
            );

            return;
        }

        locationUpdateInProgress = true;

        try {
            const csrfToken =
                document.querySelector(
                    'meta[name="csrf-token"]'
                )?.content;

            const response = await fetch(
                locationUpdateUrl,
                {
                    method: 'PATCH',
                    credentials: 'same-origin',

                    headers: {
                        Accept: 'application/json',
                        'Content-Type':
                            'application/json',
                        'X-Requested-With':
                            'XMLHttpRequest',

                        ...(csrfToken
                            ? {
                                'X-CSRF-TOKEN':
                                csrfToken,
                            }
                            : {}),
                    },

                    body: JSON.stringify({
                        latitude:
                        position.coords.latitude,

                        longitude:
                        position.coords.longitude,

                        accuracy:
                        position.coords.accuracy,
                    }),
                }
            );

            const payload = await response.json();

            if (! response.ok) {
                const error = new Error(
                    payload.message
                    || 'Your location could not be updated.'
                );

                error.apiCode =
                    payload.error?.code
                    ?? null;

                throw error;
            }

            lastLocationSentAt = Date.now();

            if (
                pendingLiveLocationPosition
                === position
            ) {
                pendingLiveLocationPosition = null;
            }

            liveLocationNetworkOffline = false;

            const liveMapState = payload.live_map ?? {};

            if (! showOutsideFestivalAreaStatus(liveMapState)) {
                showSharingStatus(
                    `Location shared successfully. Accuracy: approximately ${Math.round(
                        position.coords.accuracy
                    )} meters.`
                );
            }

            await loadLocations();
        } catch (error) {
            if (
                isLiveLocationNetworkError(
                    error
                )
            ) {
                liveLocationNetworkOffline = true;

                showSharingStatus(
                    'FestFriends temporarily could not reach the server. Location sharing is still on; your latest location will be retried automatically when the connection returns.',
                    'info'
                );
            } else if (
                error?.apiCode
                === 'festival_live_location_implausible_jump'
            ) {
                showSharingStatus(
                    error.message,
                    'info'
                );
            } else {
                showSharingStatus(
                    error instanceof Error
                        ? error.message
                        : 'Your location could not be updated.',
                    'error'
                );
            }
        } finally {
            locationUpdateInProgress = false;

            if (
                pendingLiveLocationPosition
                && pendingLiveLocationPosition
                    !== position
                && navigator.onLine !== false
            ) {
                lastLocationSentAt = 0;

                void sendLiveLocation(
                    pendingLiveLocationPosition
                );
            }
        }
    };

    const handleGeolocationError = (
        error
    ) => {
        if (
            error.code
            === GeolocationPositionError
                .PERMISSION_DENIED
        ) {
            showSharingStatus(
                'Location permission was denied. Enable location access in your browser settings, then choose Share My Location again.',
                'error'
            );

            setSharingActive(false);

            if (
                geolocationWatchId !== null
            ) {
                navigator.geolocation.clearWatch(
                    geolocationWatchId
                );

                geolocationWatchId = null;
            }

            return;
        }

        const message =
            error.code
            === GeolocationPositionError
                .POSITION_UNAVAILABLE
                ? 'Your device temporarily cannot determine its location. Location sharing is still on and FestFriends will keep trying automatically.'
                : error.code
                    === GeolocationPositionError
                        .TIMEOUT
                    ? 'Your device took too long to get a GPS fix. Location sharing is still on and FestFriends will keep trying automatically.'
                    : 'Your location could not be determined right now. Location sharing is still on and FestFriends will keep trying automatically.';

        showSharingStatus(
            message,
            'info'
        );

        setSharingActive(true);
    };

    const startLocationSharing = () => {
        if (pendingLiveLocationClear) {
            showSharingStatus(
                navigator.onLine === false
                    ? 'FestFriends is still waiting to remove your previous Live Map pin. Reconnect to the internet before starting a new location-sharing session.'
                    : 'FestFriends is finishing your previous Stop Sharing request before starting a new location-sharing session.',
                'info'
            );

            if (navigator.onLine !== false) {
                startSharingButton?.setAttribute(
                    'disabled',
                    'disabled'
                );

                void clearLiveLocationOnServer()
                    .then(async () => {
                        await loadLocations();

                        showSharingStatus(
                            'Your previous Stop Sharing request is complete. Starting a new location-sharing session now…',
                            'info'
                        );

                        startLocationSharing();
                    })
                    .catch(() => {
                        pendingLiveLocationClear = true;
                        persistPendingLocationClear(true);

                        showSharingStatus(
                            'FestFriends could not finish removing your previous Live Map pin yet. Location sharing was not restarted, and FestFriends will retry the cleanup automatically.',
                            'info'
                        );
                    })
                    .finally(() => {
                        startSharingButton?.removeAttribute(
                            'disabled'
                        );
                    });
            }

            return;
        }

        if (! viewerCheckedIn) {
            showSharingStatus(
                'You must be checked in before sharing your live location.',
                'error'
            );

            return;
        }

        if (! window.isSecureContext) {
            showSharingStatus(
                'Live location sharing requires a secure HTTPS connection.',
                'error'
            );

            return;
        }

        if (! navigator.geolocation) {
            showSharingStatus(
                'Your browser does not support location sharing.',
                'error'
            );

            return;
        }

        if (geolocationWatchId !== null) {
            return;
        }

        setSharingActive(true);

        showSharingStatus(
            'Finding your current location…',
            'info'
        );

        geolocationWatchId =
            navigator.geolocation.watchPosition(
                sendLiveLocation,
                handleGeolocationError,
                {
                    enableHighAccuracy: true,
                    maximumAge: 5000,
                    timeout: 15000,
                }
            );
    };

    const clearLiveLocationOnServer = () => {
        if (liveLocationClearPromise) {
            return liveLocationClearPromise;
        }

        liveLocationClearPromise =
            (async () => {
                if (! locationClearUrl) {
                    pendingLiveLocationClear = false;
                    persistPendingLocationClear(false);

                    return true;
                }

                const csrfToken =
                    document.querySelector(
                        'meta[name="csrf-token"]'
                    )?.content;

                const response = await fetch(
                    locationClearUrl,
                    {
                        method: 'DELETE',

                        credentials: 'same-origin',

                        headers: {
                            Accept: 'application/json',
                            'X-Requested-With':
                                'XMLHttpRequest',

                            ...(csrfToken
                                ? {
                                    'X-CSRF-TOKEN':
                                        csrfToken,
                                }
                                : {}),
                        },
                    }
                );

                const payload =
                    await response.json();

                if (! response.ok) {
                    const error = new Error(
                        payload.message
                        || 'Location sharing could not be stopped.'
                    );

                    error.apiCode =
                        payload.error?.code
                        ?? null;

                    throw error;
                }

                pendingLiveLocationClear = false;
                persistPendingLocationClear(false);
                lastLocationSentAt = 0;

                return payload;
            })()
                .finally(() => {
                    liveLocationClearPromise = null;
                });

        return liveLocationClearPromise;
    };

    const stopLocationSharing = async () => {
        if (
            geolocationWatchId !== null
        ) {
            navigator.geolocation.clearWatch(
                geolocationWatchId
            );

            geolocationWatchId = null;
        }

        setSharingActive(false);
        pendingLiveLocationPosition = null;
        liveLocationNetworkOffline = false;
        pendingLiveLocationClear =
            Boolean(locationClearUrl);

        persistPendingLocationClear(
            pendingLiveLocationClear
        );

        if (! locationClearUrl) {
            showSharingStatus(
                'Location sharing stopped in this browser.',
                'info'
            );

            return;
        }

        if (navigator.onLine === false) {
            showSharingStatus(
                'Location sharing has stopped on this device. FestFriends is offline, so your last server-side pin may remain briefly; it will be removed automatically when your connection returns.',
                'info'
            );

            return;
        }

        stopSharingButton?.setAttribute(
            'disabled',
            'disabled'
        );

        showSharingStatus(
            'Stopping location sharing…',
            'info'
        );

        try {
            const payload =
                await clearLiveLocationOnServer();

            showSharingStatus(
                payload.message
                || 'Live location sharing has stopped.',
                'info'
            );

            await loadLocations();
        } catch (error) {
            if (
                isLiveLocationNetworkError(
                    error
                )
            ) {
                pendingLiveLocationClear = true;
                persistPendingLocationClear(true);

                showSharingStatus(
                    'Location sharing has stopped on this device. FestFriends temporarily could not remove your server-side pin, so it will retry automatically when your connection returns.',
                    'info'
                );
            } else {
                showSharingStatus(
                    error instanceof Error
                        ? error.message
                        : 'Location sharing could not be stopped.',
                    'error'
                );
            }
        } finally {
            stopSharingButton?.removeAttribute(
                'disabled'
            );
        }
    };

    window.addEventListener(
        'storage',
        (event) => {
            if (
                ! pendingLocationClearStorageKey
                || event.key
                    !== pendingLocationClearStorageKey
            ) {
                return;
            }

            const pending =
                event.newValue === '1';

            pendingLiveLocationClear =
                pending;

            if (! pending) {
                showSharingStatus(
                    'Another FestFriends tab finished removing your previous Live Map pin. This tab is fully synchronized now.',
                    'info'
                );

                void loadLocations();

                return;
            }

            if (
                geolocationWatchId !== null
            ) {
                navigator.geolocation.clearWatch(
                    geolocationWatchId
                );

                geolocationWatchId = null;
            }

            pendingLiveLocationPosition = null;
            liveLocationNetworkOffline = false;

            setSharingActive(false);

            showSharingStatus(
                'Location sharing was stopped from another FestFriends tab. This tab has stopped using GPS too.',
                'info'
            );

            void loadLocations();
        },
        {
            signal: eventController.signal,
        }
    );

    window.addEventListener(
        'offline',
        () => {
            if (geolocationWatchId === null) {
                return;
            }

            liveLocationNetworkOffline = true;

            showSharingStatus(
                'Your internet connection is offline. Location sharing is still on; FestFriends will send your latest location automatically when the connection returns.',
                'info'
            );
        },
        {
            signal: eventController.signal,
        }
    );

    window.addEventListener(
        'online',
        () => {
            if (pendingLiveLocationClear) {
                showSharingStatus(
                    'Your connection is back. FestFriends is removing your last Live Map pin now…',
                    'info'
                );

                void clearLiveLocationOnServer()
                    .then((payload) => {
                        showSharingStatus(
                            payload.message
                            || 'Live location sharing has stopped.',
                            'info'
                        );

                        return loadLocations();
                    })
                    .catch(() => {
                        pendingLiveLocationClear = true;
                        persistPendingLocationClear(true);

                        showSharingStatus(
                            'FestFriends still could not remove your last server-side pin. It will retry when connectivity changes again, and the old location will also expire automatically.',
                            'info'
                        );
                    });

                return;
            }

            if (
                geolocationWatchId === null
                || ! liveLocationNetworkOffline
            ) {
                return;
            }

            liveLocationNetworkOffline = false;

            showSharingStatus(
                'Your connection is back. FestFriends is updating your Live Map location now…',
                'info'
            );

            if (pendingLiveLocationPosition) {
                lastLocationSentAt = 0;

                void sendLiveLocation(
                    pendingLiveLocationPosition
                );
            } else {
                void loadLocations();
            }
        },
        {
            signal: eventController.signal,
        }
    );

    if (
        pendingLiveLocationClear
        && navigator.onLine !== false
    ) {
        showSharingStatus(
            'FestFriends is finishing a previous Stop Sharing request and removing your old Live Map pin…',
            'info'
        );

        void clearLiveLocationOnServer()
            .then((payload) => {
                showSharingStatus(
                    payload.message
                    || 'Your previous Live Map location has been removed.',
                    'info'
                );

                return loadLocations();
            })
            .catch(() => {
                pendingLiveLocationClear = true;
                persistPendingLocationClear(true);

                showSharingStatus(
                    'FestFriends could not finish removing your previous Live Map pin yet. It will retry automatically when connectivity changes.',
                    'info'
                );
            });
    }

    updateCommandLocationAction(
        viewerCheckedIn,
        geolocationWatchId !== null
    );

    startSharingButton?.addEventListener(
        'click',
        startLocationSharing
    );

    stopSharingButton?.addEventListener(
        'click',
        stopLocationSharing
    );

    commandLocationButtons.forEach(
        (button) => {
            button.addEventListener(
                'click',
                async (event) => {
                    /*
                     * When checked out, retain the
                     * ordinary anchor behavior and
                     * scroll to the live map.
                     */
                    if (! viewerCheckedIn) {
                        return;
                    }

                    event.preventDefault();

                    section?.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start',
                    });

                    if (
                        geolocationWatchId !== null
                    ) {
                        await stopLocationSharing();

                        return;
                    }

                    startLocationSharing();
                }
            );
        }
    );

    addWindowListener(
        'festival:check-in-changed',
        async (event) => {
            const checkedIn =
                event.detail?.checkedIn === true;

            setViewerCheckedIn(
                checkedIn
            );

            if (! checkedIn) {
                await loadLocations();
            }
        }
    );

    const focusMeetupMarker = (
        meetupId
    ) => {
        const marker = meetupMarkers.get(
            String(meetupId)
        );

        if (! marker) {
            return;
        }

        section?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
        });

        window.setTimeout(
            () => {
                map.setView(
                    marker.getLatLng(),
                    Math.max(
                        map.getZoom(),
                        17
                    ),
                    {
                        animate: true,
                    }
                );

                marker.openPopup();

                highlightMarker(marker);
            },
            400
        );
    };

    if (requestedMeetupId) {
        window.setTimeout(
            () => {
                focusMeetupMarker(
                    requestedMeetupId
                );
            },
            500
        );
    }

    meetupMapButtons.forEach(
        (button) => {
            button.addEventListener(
                'click',
                () => {
                    focusMeetupMarker(
                        button.dataset.meetupId
                    );
                }
            );
        }
    );

    addWindowListener(
        'festival:focus-meetup',
        (event) => {
            focusMeetupMarker(
                event.detail?.meetupId
            );
        }
    );

    addWindowListener(
        'festival:focus-meetup-card',
        (event) => {
            const meetupId = String(
                event.detail?.meetupId ?? ''
            );

            if (! meetupId) {
                return;
            }

            const meetupCard = document.querySelector(
                `[data-festival-meetup-card="${CSS.escape(
                    meetupId
                )}"]`
            );

            if (! meetupCard) {
                return;
            }

            map.closePopup();

            meetupCard.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
            });

            window.setTimeout(
                () => {
                    meetupCard.classList.add(
                        'ring-2',
                        'ring-amber-400',
                        'ring-offset-4',
                        'ring-offset-slate-950'
                    );

                    window.setTimeout(
                        () => {
                            meetupCard.classList.remove(
                                'ring-2',
                                'ring-amber-400',
                                'ring-offset-4',
                                'ring-offset-slate-950'
                            );
                        },
                        2200
                    );
                },
                450
            );
        }
    );

    addWindowListener(
        'festival:request-member-markers',
        publishMemberMarkerState
    );

    addWindowListener(
        'festival:focus-member',
        (event) => {
            const memberId =
                String(
                    event.detail?.memberId
                    ?? ''
                );

            const marker =
                memberMarkers.get(memberId);

            if (!marker) {
                return;
            }

            section?.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
            });

            window.setTimeout(
                () => {
                    map.setView(
                        marker.getLatLng(),
                        Math.max(
                            map.getZoom(),
                            17
                        ),
                        {
                            animate: true,
                        }
                    );

                    marker.openPopup();

                    highlightMarker(marker);
                },
                400
            );
        }
    );

    showEveryoneButton?.addEventListener(
        'click',
        () => {
            setMapView('geographic');

            window.setTimeout(
                () => {
                    map.invalidateSize();
                    fitMapToVisibleMarkers();
                },
                75
            );
        },
        {
            signal: eventController.signal,
        }
    );

    centerMeButton?.addEventListener(
        'click',
        () => {
            if (currentUserMemberId === null) {
                return;
            }

            const marker = memberMarkers.get(
                currentUserMemberId
            );

            if (! marker) {
                return;
            }

            setMapView('geographic');

            window.setTimeout(
                () => {
                    map.invalidateSize();
                    map.setView(
                        marker.getLatLng(),
                        Math.max(
                            map.getZoom(),
                            17
                        ),
                        {
                            animate: true,
                        }
                    );

                    marker.openPopup();
                    highlightMarker(marker);
                },
                75
            );
        },
        {
            signal: eventController.signal,
        }
    );

    refreshButton?.addEventListener(
        'click',
        loadLocations,
        { signal: eventController.signal }
    );

    renderZones(zones);
    renderPaths(paths);
    renderPathJunctions(
        pathJunctions
    );
    const refreshVenueMapReadiness = async () => {
        if (!venueMapReadinessUrl) {
            return;
        }

        try {
            const response=await fetch(
                venueMapReadinessUrl,
                {
                    headers:{
                        'Accept':'application/json',
                    },
                }
            );

            if (!response.ok) {
                return;
            }

            const payload=await response.json();
            const readiness=payload?.data;

            if (!readiness) {
                return;
            }

            if (venueMapReadinessStateLabel) {
                venueMapReadinessStateLabel.textContent=
                    readiness.state_label
                    || 'Map starting';
            }

            if (venueMapReadinessHeadline) {
                venueMapReadinessHeadline.textContent=
                    readiness.headline
                    || 'Help build this festival map';
            }

            if (venueMapReadinessDescription) {
                venueMapReadinessDescription.textContent=
                    readiness.description
                    || '';
            }

            if (venueMapReadinessOfficialTotal) {
                venueMapReadinessOfficialTotal.textContent=
                    String(
                        readiness.official_total
                        || 0
                    );
            }

            if (venueMapReadinessCommunityGroups) {
                venueMapReadinessCommunityGroups.textContent=
                    String(
                        readiness.community
                            ?.visible_groups
                        || 0
                    );
            }

            if (venueMapReadinessPrivateCount) {
                venueMapReadinessPrivateCount.textContent=
                    String(
                        readiness.viewer
                            ?.private_pin_count
                        || 0
                    );
            }
        } catch {
            // Readiness is advisory; map use continues if refresh fails.
        }
    };

    const syncPersonalPinCapacity = () => {
        if (personalPinCount) {
            personalPinCount.textContent=
                String(personalPins.length);
        }

        if (personalPinRemaining) {
            personalPinRemaining.textContent=
                String(
                    Math.max(
                        0,
                        20 - personalPins.length
                    )
                );
        }

        personalPinClearAll?.classList.toggle(
            'hidden',
            personalPins.length === 0
        );

        if (personalPinPlacementStart) {
            personalPinPlacementStart.disabled=
                personalPins.length >= 20;
        }

        if (personalPinCurrentLocation) {
            personalPinCurrentLocation.disabled=
                personalPins.length >= 20;
        }

        renderPersonalPinManager();
    };

    let personalPinLabelCustomized=false;

    const applyPersonalPinPreset = (
        pinType,
        defaultLabel
    ) => {
        if (personalPinTypeInput) {
            personalPinTypeInput.value=pinType;
        }

        if (
            personalPinLabelInput
            && (
                !personalPinLabelCustomized
                || personalPinLabelInput.value.trim() === ''
                || personalPinLabelInput.value.trim() === 'Remember this spot'
            )
        ) {
            personalPinLabelInput.value=
                defaultLabel
                || 'Remember this spot';

            personalPinLabelCustomized=false;
        }
    };

    const personalPinDraft = () => ({
        pin_type:
            personalPinTypeInput?.value
            || 'custom',
        label:
            personalPinLabelInput?.value?.trim()
            || 'Remember this spot',
        note:
            personalPinNoteInput?.value?.trim()
            || null,
    });

    const checkNearbyPrivatePins = async (
        latitude,
        longitude,
        draft
    ) => {
        if (!personalPinDuplicateCheckUrl) {
            return null;
        }

        const url=new URL(
            personalPinDuplicateCheckUrl,
            window.location.origin
        );

        url.searchParams.set(
            'latitude',
            String(latitude)
        );
        url.searchParams.set(
            'longitude',
            String(longitude)
        );
        url.searchParams.set(
            'pin_type',
            draft.pin_type
        );
        url.searchParams.set(
            'label',
            draft.label
        );

        const response=await fetch(
            url,
            {
                headers:{
                    'Accept':'application/json',
                },
            }
        );

        const payload=await response.json();

        if (!response.ok) {
            throw new Error(
                payload?.message
                || 'Nearby private pins could not be checked.'
            );
        }

        return payload?.data || null;
    };

    const savePersonalPinAt = async (
        latitude,
        longitude,
        {
            source='map',
        }={}
    ) => {
        if (!personalPinStoreUrl) {
            throw new Error(
                'Private pins are unavailable right now.'
            );
        }

        const draft=personalPinDraft();

        if (!draft.label) {
            throw new Error(
                'Add a label before placing the pin.'
            );
        }

        let acknowledgeNearby=false;
        const duplicate=
            await checkNearbyPrivatePins(
                latitude,
                longitude,
                draft
            );

        if (duplicate?.strong_duplicate) {
            const existing=
                duplicate.items?.[0];

            const createAnyway=window.confirm(
                'You already have a very similar private pin here. Press Cancel to use the existing pin, or OK only if you intentionally want another private pin at this spot.'
            );

            if (!createAnyway) {
                if (existing?.id) {
                    focusPersonalPin(
                        existing.id
                    );
                }

                return null;
            }

            acknowledgeNearby=true;
        }

        const response=await fetch(
            personalPinStoreUrl,
            {
                method:'POST',
                headers:{
                    'Accept':'application/json',
                    'Content-Type':'application/json',
                    'X-CSRF-TOKEN':csrfToken(),
                },
                body:JSON.stringify({
                    ...draft,
                    latitude,
                    longitude,
                    acknowledge_nearby_private_pin:
                        acknowledgeNearby,
                }),
            }
        );

        const payload=await response.json();

        if (!response.ok) {
            const message=
                payload?.message
                || Object.values(
                    payload?.errors
                    || {}
                )?.flat()?.[0]
                || 'The private pin could not be saved.';

            throw new Error(message);
        }

        const pin=payload?.data;

        if (!pin) {
            throw new Error(
                'The private pin response was incomplete.'
            );
        }

        personalPins=[
            pin,
            ...personalPins.filter(
                (item) =>
                    String(item.id)
                    !== String(pin.id)
            ),
        ];

        const marker=renderPersonalPin(pin);

        syncPersonalPinCapacity();
        void refreshVenueMapReadiness();

        if (marker) {
            marker.openPopup();
        }

        if (
            source === 'current_location'
            && personalPinPlacementStatus
        ) {
            personalPinPlacementStatus.textContent=
                'Saved one private pin from your current GPS location. Live-location sharing remains off unless you turn it on separately.';

            personalPinPlacementStatus.classList.remove(
                'hidden'
            );
        }

        return pin;
    };

    let personalPinPlacementActive=false;

    const csrfToken=() =>
        document
            .querySelector('meta[name="csrf-token"]')
            ?.getAttribute('content')
        || '';

    const setPersonalPinError=(message='') => {
        if (!personalPinError) {
            return;
        }

        personalPinError.textContent=message;
        personalPinError.classList.toggle(
            'hidden',
            !message
        );
    };

    const setPersonalPinPlacement=(active) => {
        personalPinPlacementActive=active;

        personalPinPlacementStatus?.classList.toggle(
            'hidden',
            !active
        );

        personalPinPlacementStart?.setAttribute(
            'aria-pressed',
            active ? 'true' : 'false'
        );

        if (personalPinPlacementStart) {
            personalPinPlacementStart.textContent=active
                ? 'Tap the Map Now'
                : 'Tap Map to Place Pin';
        }

        if (canvas) {
            canvas.style.cursor=active
                ? 'crosshair'
                : '';
        }
    };

    personalPinDropToggle?.addEventListener(
        'click',
        () => {
            const opening=
                personalPinComposer?.classList.contains('hidden');

            personalPinComposer?.classList.toggle(
                'hidden',
                !opening
            );

            personalPinDropToggle.setAttribute(
                'aria-expanded',
                opening ? 'true' : 'false'
            );

            if (!opening) {
                setPersonalPinPlacement(false);
            }
        },
        {
            signal:eventController.signal,
        }
    );

    const closePersonalPinPromotionPanel = () => {
        resetPersonalPinPromotionPanel();
    };

    personalPinPromotionClose?.addEventListener(
        'click',
        closePersonalPinPromotionPanel,
        { signal:eventController.signal }
    );

    personalPinPromotionCancel?.addEventListener(
        'click',
        closePersonalPinPromotionPanel,
        { signal:eventController.signal }
    );

    personalPinPromotionSubmit?.addEventListener(
        'click',
        async () => {
            const state = pendingPersonalPinPromotion;

            if (!state?.url || !state?.pin) {
                return;
            }

            const duplicate = state.preview?.duplicate_check || {};
            const matchingSignal = state.preview?.promotion_guide?.matching_signal || null;
            const needsAcknowledgement =
                Boolean(duplicate?.has_similar)
                || Boolean(matchingSignal);

            if (
                needsAcknowledgement
                && !personalPinPromotionDuplicateAck?.checked
            ) {
                showPersonalPinPromotionError(
                    'Review the nearby evidence and confirm that your observation adds something genuinely useful before sharing.'
                );
                return;
            }

            showPersonalPinPromotionError('');
            personalPinPromotionSubmit.disabled = true;
            const originalText = personalPinPromotionSubmit.textContent;
            personalPinPromotionSubmit.textContent = 'Sharing…';

            try {
                const body = new FormData();
                body.append(
                    'acknowledge_duplicate',
                    needsAcknowledgement ? '1' : '0'
                );

                const photo = personalPinPromotionPhoto?.files?.[0];
                if (photo) {
                    body.append('photo', photo);
                }

                const response = await fetch(
                    state.url,
                    {
                        method:'POST',
                        headers:{
                            'Accept':'application/json',
                            'X-CSRF-TOKEN':csrfToken(),
                        },
                        body,
                    }
                );

                const payload = await response.json();

                if (!response.ok) {
                    throw new Error(
                        payload?.message
                        || Object.values(payload?.errors || {})?.flat()?.[0]
                        || 'The private discovery could not be shared.'
                    );
                }

                const id = String(state.pin.id);
                const index=personalPins.findIndex(
                    (pin) => String(pin.id) === id
                );

                if (index >= 0) {
                    personalPins[index]={
                        ...personalPins[index],
                        promoted:true,
                        promoted_suggestion_id:
                            payload?.data?.suggestion_id
                            || personalPins[index].promoted_suggestion_id,
                    };
                }

                renderPersonalPins(personalPins);
                renderPersonalPinManager();

                const journey = payload?.data?.journey || {};
                personalPinPromotionResult?.classList.remove('hidden');
                personalPinPromotionActions?.classList.add('hidden');

                if (personalPinPromotionResultTitle) {
                    personalPinPromotionResultTitle.textContent =
                        journey.headline
                        || 'Submitted for organizer review';
                }

                if (personalPinPromotionResultCopy) {
                    personalPinPromotionResultCopy.textContent =
                        payload?.data?.photo_attached
                            ? 'Your fresh photo is attached to the pending suggestion. Validation and organizer review still come next.'
                            : (journey.description || 'Your private pin stays private while the separate pending suggestion moves through review.');
                }

                if (personalPinPromotionResultLink) {
                    personalPinPromotionResultLink.href =
                        journey.links?.matching_signal_web
                        || journey.links?.community_web
                        || '#';
                }
            } catch (error) {
                showPersonalPinPromotionError(
                    error?.message
                    || 'The private discovery could not be shared.'
                );
            } finally {
                personalPinPromotionSubmit.disabled = false;
                personalPinPromotionSubmit.textContent = originalText;
            }
        },
        { signal:eventController.signal }
    );

    personalPinComposerClose?.addEventListener(
        'click',
        () => {
            personalPinComposer?.classList.add('hidden');
            personalPinDropToggle?.setAttribute(
                'aria-expanded',
                'false'
            );
            setPersonalPinPlacement(false);
        },
        {
            signal:eventController.signal,
        }
    );

    personalPinPresetButtons.forEach(
        (button) => {
            button.addEventListener(
                'click',
                () => {
                    applyPersonalPinPreset(
                        button.dataset.personalPinPreset
                            || 'custom',
                        button.dataset.personalPinDefaultLabel
                            || 'Remember this spot'
                    );
                },
                {
                    signal:eventController.signal,
                }
            );
        }
    );

    personalPinTypeInput?.addEventListener(
        'change',
        () => {
            const selected=
                personalPinTypeInput
                    .selectedOptions?.[0];

            applyPersonalPinPreset(
                personalPinTypeInput.value,
                selected?.dataset.defaultLabel
                    || selected?.textContent?.trim()
                    || 'Remember this spot'
            );
        },
        {
            signal:eventController.signal,
        }
    );

    personalPinLabelInput?.addEventListener(
        'input',
        () => {
            personalPinLabelCustomized=true;
        },
        {
            signal:eventController.signal,
        }
    );

    personalPinClearAll?.addEventListener(
        'click',
        async () => {
            if (
                !personalPinDestroyAllUrl
                || personalPins.length === 0
            ) {
                return;
            }

            const confirmed=window.confirm(
                'Delete all of your private pins for this festival? Promoted community suggestions will stay in the community review system.'
            );

            if (!confirmed) {
                return;
            }

            try {
                const response=await fetch(
                    personalPinDestroyAllUrl,
                    {
                        method:'DELETE',
                        headers:{
                            'Accept':'application/json',
                            'X-CSRF-TOKEN':csrfToken(),
                        },
                    }
                );

                const payload=await response.json();

                if (!response.ok) {
                    throw new Error(
                        payload?.message
                        || 'Your private pins could not be cleared.'
                    );
                }

                personalPins=[];
                personalPinLayer.clearLayers();
                personalPinMarkers.clear();
                syncPersonalPinCapacity();
                void refreshVenueMapReadiness();
            } catch (error) {
                window.alert(
                    error?.message
                    || 'Your private pins could not be cleared.'
                );
            }
        },
        {
            signal:eventController.signal,
        }
    );

    personalPinManagerFilter?.addEventListener(
        'change',
        () => {
            renderPersonalPinManager();
        },
        {
            signal:eventController.signal,
        }
    );

    personalPinPlacementStart?.addEventListener(
        'click',
        () => {
            setPersonalPinError('');

            if (!personalPinStoreUrl) {
                setPersonalPinError(
                    'Private pins are unavailable right now.'
                );
                return;
            }

            const label=
                personalPinLabelInput?.value?.trim()
                || '';

            if (!label) {
                setPersonalPinError(
                    'Add a label before placing the pin.'
                );
                personalPinLabelInput?.focus();
                return;
            }

            setPersonalPinPlacement(true);
        },
        {
            signal:eventController.signal,
        }
    );

    personalPinCurrentLocation?.addEventListener(
        'click',
        () => {
            setPersonalPinError('');

            if (!navigator.geolocation) {
                setPersonalPinError(
                    'This browser does not provide location access.'
                );
                return;
            }

            personalPinCurrentLocation.disabled=true;
            personalPinCurrentLocation.textContent=
                'Finding location…';

            navigator.geolocation.getCurrentPosition(
                async (position) => {
                    try {
                        await savePersonalPinAt(
                            position.coords.latitude,
                            position.coords.longitude,
                            {
                                source:
                                    'current_location',
                            }
                        );
                    } catch (error) {
                        setPersonalPinError(
                            error?.message
                            || 'Your current location could not be saved as a private pin.'
                        );
                    } finally {
                        personalPinCurrentLocation.disabled=
                            personalPins.length >= 20;

                        personalPinCurrentLocation.textContent=
                            '◎ Pin My Current Location';
                    }
                },
                (error) => {
                    setPersonalPinError(
                        error?.message
                        || 'FestFriends could not access your current location.'
                    );

                    personalPinCurrentLocation.disabled=
                        personalPins.length >= 20;

                    personalPinCurrentLocation.textContent=
                        '◎ Pin My Current Location';
                },
                {
                    enableHighAccuracy:true,
                    maximumAge:5000,
                    timeout:15000,
                }
            );
        },
        {
            signal:eventController.signal,
        }
    );

    map.on('click',async (event) => {
        if (!personalPinPlacementActive) {
            return;
        }

        setPersonalPinPlacement(false);
        setPersonalPinError('');

        try {
            await savePersonalPinAt(
                event.latlng.lat,
                event.latlng.lng,
                {
                    source:'map',
                }
            );
        } catch (error) {
            setPersonalPinError(
                error?.message
                || 'The private pin could not be saved.'
            );
        }
    });

    mapElement.addEventListener(
        'click',
        async (event) => {
            const rememberMeetupButton=event.target.closest(
                '[data-meetup-remember-private]'
            );

            if (rememberMeetupButton) {
                event.preventDefault();

                const previewUrl=
                    rememberMeetupButton.dataset
                        .meetupPrivatePinPreviewUrl;
                const rememberUrl=
                    rememberMeetupButton.dataset
                        .meetupRememberPrivateUrl;

                if (!rememberUrl) {
                    return;
                }

                rememberMeetupButton.disabled=true;

                try {
                    let acknowledgeNearby=false;

                    if (previewUrl) {
                        const previewResponse=await fetch(
                            previewUrl,
                            {
                                headers:{
                                    'Accept':'application/json',
                                },
                            }
                        );

                        const previewPayload=
                            await previewResponse.json();

                        if (!previewResponse.ok) {
                            throw new Error(
                                previewPayload?.message
                                || 'This meetup location could not be prepared as a private pin.'
                            );
                        }

                        const preview=previewPayload?.data;

                        if (!preview?.eligible) {
                            throw new Error(
                                'This meetup does not have an exact map location to remember privately.'
                            );
                        }

                        const nearby=
                            preview.nearby_private_pin;

                        if (nearby?.strong_duplicate) {
                            const existing=
                                nearby.items?.[0];

                            const duplicateOverride=
                                window.confirm(
                                    'You already have a very similar private pin at this meetup spot. Press Cancel to use the existing pin, or OK to save another private pin intentionally.'
                                );

                            if (!duplicateOverride) {
                                if (existing?.id) {
                                    focusPersonalPin(
                                        existing.id
                                    );
                                }

                                return;
                            }

                            acknowledgeNearby=true;
                        }
                    }

                    const response=await fetch(
                        rememberUrl,
                        {
                            method:'POST',
                            headers:{
                                'Accept':'application/json',
                                'Content-Type':'application/json',
                                'X-CSRF-TOKEN':csrfToken(),
                            },
                            body:JSON.stringify({
                                acknowledge_nearby_private_pin:
                                    acknowledgeNearby,
                            }),
                        }
                    );

                    const payload=await response.json();

                    if (!response.ok) {
                        throw new Error(
                            payload?.message
                            || Object.values(
                                payload?.errors
                                || {}
                            )?.flat()?.[0]
                            || 'The meetup location could not be remembered privately.'
                        );
                    }

                    const pin=payload?.data;

                    if (!pin) {
                        throw new Error(
                            'The private pin response was incomplete.'
                        );
                    }

                    personalPins=[
                        pin,
                        ...personalPins.filter(
                            (item) =>
                                String(item.id)
                                !== String(pin.id)
                        ),
                    ];

                    const marker=renderPersonalPin(pin);

                    syncPersonalPinCapacity();
                    void refreshVenueMapReadiness();

                    if (marker) {
                        marker.openPopup();
                    }
                } catch (error) {
                    window.alert(
                        error?.message
                        || 'The meetup location could not be remembered privately.'
                    );
                } finally {
                    rememberMeetupButton.disabled=false;
                }

                return;
            }

            const managerFocus=event.target.closest(
                '[data-personal-pin-manager-focus]'
            );

            if (managerFocus) {
                event.preventDefault();

                focusPersonalPin(
                    managerFocus.dataset
                        .personalPinManagerFocus
                );

                return;
            }

            const deleteButton=event.target.closest(
                '[data-personal-pin-delete]'
            );

            if (deleteButton) {
                event.preventDefault();

                const id=String(
                    deleteButton.dataset.personalPinDelete
                    || ''
                );
                const url=
                    deleteButton.dataset.personalPinDeleteUrl;

                if (!id || !url) {
                    return;
                }

                const response=await fetch(
                    url,
                    {
                        method:'DELETE',
                        headers:{
                            'Accept':'application/json',
                            'X-CSRF-TOKEN':csrfToken(),
                        },
                    }
                );

                if (response.ok) {
                    personalPinMarkers.get(id)?.remove();
                    personalPinMarkers.delete(id);
                    personalPins=personalPins.filter(
                        (pin) =>
                            String(pin.id) !== id
                    );

                    syncPersonalPinCapacity();
                    void refreshVenueMapReadiness();
                }

                return;
            }

            const editButton=event.target.closest(
                '[data-personal-pin-edit]'
            );

            if (editButton) {
                event.preventDefault();

                const id=String(
                    editButton.dataset.personalPinEdit
                    || ''
                );
                const url=
                    editButton.dataset.personalPinUpdateUrl;
                const current=personalPins.find(
                    (pin) =>
                        String(pin.id) === id
                );

                if (!id || !url || !current) {
                    return;
                }

                const nextLabel=window.prompt(
                    'Private pin label',
                    current.label || ''
                );

                if (nextLabel === null) {
                    return;
                }

                const cleanLabel=nextLabel.trim();

                if (!cleanLabel) {
                    window.alert(
                        'Private pins need a label.'
                    );
                    return;
                }

                const nextNote=window.prompt(
                    'Private note (optional)',
                    current.note || ''
                );

                if (nextNote === null) {
                    return;
                }

                try {
                    const response=await fetch(
                        url,
                        {
                            method:'PATCH',
                            headers:{
                                'Accept':'application/json',
                                'Content-Type':'application/json',
                                'X-CSRF-TOKEN':csrfToken(),
                            },
                            body:JSON.stringify({
                                label:cleanLabel,
                                note:nextNote.trim() || null,
                            }),
                        }
                    );

                    const payload=await response.json();

                    if (!response.ok) {
                        throw new Error(
                            payload?.message
                            || 'The private pin could not be updated.'
                        );
                    }

                    const index=personalPins.findIndex(
                        (pin) =>
                            String(pin.id) === id
                    );

                    if (index >= 0) {
                        personalPins[index]=payload.data;
                    }

                    syncPersonalPinCapacity();

                    const marker=
                        personalPinMarkers.get(id);

                    marker?.setPopupContent(
                        personalPinPopup(payload.data)
                    );
                    marker?.openPopup();
                } catch (error) {
                    window.alert(
                        error?.message
                        || 'The private pin could not be updated.'
                    );
                }

                return;
            }

            const moveButton=event.target.closest(
                '[data-personal-pin-move]'
            );

            if (moveButton) {
                event.preventDefault();

                const id=String(
                    moveButton.dataset.personalPinMove
                    || ''
                );
                const marker=
                    personalPinMarkers.get(id);

                marker?.closePopup();
                marker?.dragging?.enable();

                window.setTimeout(
                    () => {
                        if (marker?.dragging?.enabled()) {
                            window.alert(
                                'Drag your private pin to its new location, then release it to save.'
                            );
                        }
                    },
                    20
                );

                return;
            }

            const promoteButton=event.target.closest(
                '[data-personal-pin-promote]'
            );

            if (!promoteButton) {
                return;
            }

            event.preventDefault();

            const id=String(
                promoteButton.dataset.personalPinPromote
                || ''
            );
            const url=
                promoteButton.dataset.personalPinPromoteUrl;
            const previewUrl=
                promoteButton.dataset.personalPinPromotionPreviewUrl;

            if (!id || !url) {
                return;
            }

            try {
                const currentPin = personalPins.find(
                    (pin) => String(pin.id) === id
                );

                if (!currentPin) {
                    throw new Error('This private pin is no longer available.');
                }

                let preview = null;

                if (previewUrl) {
                    const previewResponse=await fetch(
                        previewUrl,
                        {
                            headers:{
                                'Accept':'application/json',
                            },
                        }
                    );

                    const previewPayload=
                        await previewResponse.json();

                    if (!previewResponse.ok) {
                        throw new Error(
                            previewPayload?.message
                            || 'FestFriends could not check nearby community evidence.'
                        );
                    }

                    preview = previewPayload?.data || null;
                }

                if (
                    openPersonalPinPromotionPanel(
                        currentPin,
                        url,
                        preview
                    )
                ) {
                    return;
                }

                // Safe fallback for older markup: keep the explicit location-share confirmation.
                const sharing = currentPin.community_sharing || {};
                const exactShareConfirmed = window.confirm(
                    sharing.recommended
                        ? 'Share this reusable venue place for community review? A new pending suggestion will copy this pin’s exact location. Your private pin itself stays private.'
                        : 'This pin is private-first. Share only if it represents a reusable venue place rather than a personal location. A new pending suggestion will copy this pin’s exact location. Continue?'
                );

                if (!exactShareConfirmed) {
                    return;
                }

                let acknowledgeDuplicate=false;
                const duplicate=preview?.duplicate_check;

                if (duplicate?.has_similar) {
                    const samePlaceLikely=Boolean(
                        duplicate.same_place_likely
                    );
                    const firstCandidate=duplicate.items?.[0];
                    const shareAnyway=window.confirm(
                        samePlaceLikely
                            ? 'A pending community suggestion probably represents the same place. Press OK only if this is genuinely different. Press Cancel to review the existing community suggestion instead.'
                            : 'Nearby pending community suggestions already exist. Press OK to confirm this pin is genuinely different, or Cancel to review them first.'
                    );

                    if (!shareAnyway) {
                        if (firstCandidate?.community_link) {
                            window.location.assign(
                                firstCandidate.community_link
                            );
                        }

                        return;
                    }

                    acknowledgeDuplicate=true;
                }

                const response=await fetch(
                    url,
                    {
                        method:'POST',
                        headers:{
                            'Accept':'application/json',
                            'Content-Type':'application/json',
                            'X-CSRF-TOKEN':csrfToken(),
                        },
                        body:JSON.stringify({
                            acknowledge_duplicate:
                                acknowledgeDuplicate,
                        }),
                    }
                );

                const payload=await response.json();

                if (!response.ok) {
                    throw new Error(
                        payload?.message
                        || Object.values(payload?.errors || {})?.flat()?.[0]
                        || 'The pin could not be submitted for community review.'
                    );
                }

                const index=personalPins.findIndex(
                    (pin) => String(pin.id) === id
                );

                if (index >= 0) {
                    personalPins[index]={
                        ...personalPins[index],
                        promoted:true,
                        promoted_suggestion_id:
                            payload?.data?.suggestion_id
                            || personalPins[index].promoted_suggestion_id,
                    };
                }

                renderPersonalPins(personalPins);
                renderPersonalPinManager();
            } catch (error) {
                window.alert(
                    error?.message
                    || 'The private pin could not be submitted for community review.'
                );
            }

            return;

        },
        {
            signal:eventController.signal,
        }
    );

    const walkthroughLocateButton = mapElement.querySelector(
        '[data-venue-map-walkthrough-locate]'
    );
    // The manual scout uses navigator.geolocation.getCurrentPosition only; no walk-through watchPosition is started.
    const walkthroughStatus = mapElement.querySelector(
        '[data-venue-map-walkthrough-status]'
    );
    const walkthroughResults = mapElement.querySelector(
        '[data-venue-map-walkthrough-results]'
    );
    const needsCompassPanel = mapElement.querySelector('[data-venue-map-needs-compass]');
    const needsCompassSectors = mapElement.querySelector('[data-venue-map-needs-compass-sectors]');
    const needsCompassContract = venueMapWalkthrough?.needs_compass || null;
    const needsCompassRefresh = mapElement.querySelector('[data-venue-map-needs-compass-refresh]');
    const quickCapture = venueMapWalkthrough?.quick_capture || null;
    const postCaptureRebalanceContract = venueMapWalkthrough?.post_capture_rebalance || quickCapture?.post_capture_rebalance || null;
    const areaHelpContract = venueMapWalkthrough?.area_help || quickCapture?.area_help || null;
    const areaHelpPanel = mapElement.querySelector('[data-venue-map-area-help]');
    const areaHelpResult = mapElement.querySelector('[data-venue-map-area-help-result]');
    const areaHelpRefresh = mapElement.querySelector('[data-venue-map-area-help-refresh]');

    const walkthroughDistanceLabel = (meters) => {
        if (! Number.isFinite(meters)) {
            return '';
        }

        if (meters < 1000) {
            return `${Math.max(1, Math.round(meters))} m away`;
        }

        return `${(meters / 1000).toFixed(1)} km away`;
    };

    const blankAreaDiscovery = venueMapWalkthrough?.blank_area_discovery || null;
    const walkthroughOpportunityQueue = venueMapWalkthrough?.opportunity_queue || null;

    const walkthroughProximityBoost = (distanceMeters, nearbyRadius) => {
        if (!Number.isFinite(distanceMeters)) {
            return 0;
        }

        if (distanceMeters <= 100) {
            return 40;
        }

        if (distanceMeters <= 250) {
            return 30;
        }

        if (distanceMeters <= nearbyRadius) {
            return 20;
        }

        return 0;
    };

    const nearestBlankAreaAnchorDistance = (latitude, longitude) => {
        if (!blankAreaDiscovery) {
            return null;
        }

        const anchors = [
            ...(blankAreaDiscovery.official_anchors || []),
            ...(blankAreaDiscovery.crowd_anchors || []),
        ];

        const distances = anchors
            .map((anchor) => navigationDistanceMeters(
                latitude,
                longitude,
                Number(anchor.latitude),
                Number(anchor.longitude)
            ))
            .filter((distance) => Number.isFinite(distance));

        return distances.length > 0
            ? Math.min(...distances)
            : Number.POSITIVE_INFINITY;
    };

    const needsCompassBearingDegrees = (fromLatitude, fromLongitude, toLatitude, toLongitude) => {
        const toRadians = (value) => value * Math.PI / 180;
        const toDegrees = (value) => value * 180 / Math.PI;
        const fromLat = toRadians(fromLatitude);
        const toLat = toRadians(toLatitude);
        const longitudeDelta = toRadians(toLongitude - fromLongitude);
        const y = Math.sin(longitudeDelta) * Math.cos(toLat);
        const x = Math.cos(fromLat) * Math.sin(toLat) - Math.sin(fromLat) * Math.cos(toLat) * Math.cos(longitudeDelta);
        return (toDegrees(Math.atan2(y, x)) + 360) % 360;
    };

    const needsCompassDirection = (bearing, distanceMeters) => {
        const atSpotThreshold = Number(needsCompassContract?.direction_model?.at_current_spot_threshold_meters || 35);
        if (Number.isFinite(distanceMeters) && distanceMeters <= atSpotThreshold) {
            return { key: 'here', label: 'At your spot', emoji: '◎', sort: -1 };
        }
        if (bearing >= 315 || bearing < 45) return { key: 'north', label: 'North of you', emoji: '↑', sort: 0 };
        if (bearing < 135) return { key: 'east', label: 'East of you', emoji: '→', sort: 1 };
        if (bearing < 225) return { key: 'south', label: 'South of you', emoji: '↓', sort: 2 };
        return { key: 'west', label: 'West of you', emoji: '←', sort: 3 };
    };

    const needsCompassDistanceBand = (meters) => {
        const veryClose = Number(needsCompassContract?.direction_model?.distance_bands?.very_close_max_meters || 100);
        const nearby = Number(needsCompassContract?.direction_model?.distance_bands?.nearby_max_meters || 250);
        if (meters <= Number(needsCompassContract?.direction_model?.at_current_spot_threshold_meters || 35)) return 'at your spot';
        if (meters <= veryClose) return 'very close';
        if (meters <= nearby) return 'nearby';
        return 'within the scout area';
    };

    const areaHelpActionLabel = (item) => {
        const key = String(item?.area_help_action || 'document_missing_category');
        return areaHelpContract?.actions?.[key] || item?.action?.label || 'Document this useful place';
    };

    const renderAreaHelp = (latitude, longitude, rankedOpportunities = []) => {
        if (!areaHelpPanel || !areaHelpResult || !areaHelpContract) return;
        const radius = Math.max(1, Number(areaHelpContract.nearby_radius_meters || 450));
        const spatial = rankedOpportunities
            .filter((item) => item?.distance_rankable === true && Number.isFinite(Number(item.latitude)) && Number.isFinite(Number(item.longitude)))
            .map((item) => ({
                ...item,
                areaHelpDistanceMeters: navigationDistanceMeters(latitude, longitude, Number(item.latitude), Number(item.longitude)),
            }))
            .filter((item) => Number.isFinite(item.areaHelpDistanceMeters) && item.areaHelpDistanceMeters <= radius)
            .sort((a, b) => Number(b.device_priority || b.base_priority || 0) - Number(a.device_priority || a.base_priority || 0));
        const fallback = rankedOpportunities
            .filter((item) => filledForAreaHelp(item?.landmark_type))
            .sort((a, b) => Number(b.device_priority || b.base_priority || 0) - Number(a.device_priority || a.base_priority || 0));
        const candidate = spatial[0] || fallback[0] || null;

        areaHelpRefresh?.classList.add('hidden');
        areaHelpResult.classList.remove('hidden');
        if (!candidate) {
            areaHelpResult.innerHTML = '<p class="text-[11px] font-bold text-zinc-500">No contextual action is available from the currently loaded bounded queue. That does not mean this area is complete.</p>';
            areaHelpPanel.classList.remove('hidden');
            return;
        }

        const distanceText = Number.isFinite(candidate.areaHelpDistanceMeters)
            ? needsCompassDistanceBand(candidate.areaHelpDistanceMeters)
            : 'useful across the venue';
        const actionLabel = areaHelpActionLabel(candidate);
        const canCapture = Boolean(walkthroughLastPosition && candidate.landmark_type);
        areaHelpResult.innerHTML = `
            <div class="rounded-xl border border-emerald-200 bg-white/90 p-3 dark:border-emerald-900 dark:bg-zinc-950/70">
                <div class="flex items-start gap-3">
                    <span class="text-xl" aria-hidden="true">${escapeHtml(candidate.emoji || '📍')}</span>
                    <div class="min-w-0 flex-1">
                        <div class="flex flex-wrap items-center gap-2"><strong class="text-sm text-zinc-950 dark:text-white">${escapeHtml(actionLabel)}</strong><span class="rounded-full bg-emerald-100 px-2 py-0.5 text-[9px] font-black text-emerald-900 dark:bg-emerald-950 dark:text-emerald-100">${escapeHtml(distanceText)}</span></div>
                        <p class="mt-1 text-xs font-black text-zinc-800 dark:text-zinc-100">${escapeHtml(candidate.title || 'Useful map need')}</p>
                        <p class="mt-1 text-[11px] leading-5 text-zinc-600 dark:text-zinc-300">${escapeHtml(candidate.area_help_reason || candidate.guidance || 'Add only what you personally observe.')}</p>
                        <div class="mt-2 flex flex-wrap gap-2">
                            ${candidate.signal_key ? `<button type="button" data-walkthrough-focus-signal="${escapeHtml(String(candidate.signal_key))}" class="rounded-lg border border-emerald-200 px-2.5 py-1.5 text-[10px] font-black text-emerald-800 dark:border-emerald-800 dark:text-emerald-200">Review crowd area</button>` : ''}
                            ${canCapture ? `<button type="button" data-walkthrough-quick-capture data-landmark-type="${escapeHtml(String(candidate.landmark_type || 'other'))}" data-crowd-signal="${escapeHtml(String(candidate.signal_key || ''))}" data-opportunity-title="${escapeHtml(String(candidate.title || ''))}" class="rounded-lg bg-violet-700 px-2.5 py-1.5 text-[10px] font-black text-white">📸 Capture this</button>` : ''}
                        </div>
                    </div>
                </div>
            </div>`;
        areaHelpPanel.classList.remove('hidden');
    };

// That nearby recommendation is stale after your capture
    const invalidateAreaHelpAfterCapture = () => {
        if (!areaHelpPanel || !areaHelpResult || !areaHelpContract) return;
        areaHelpResult.classList.add('hidden');
        areaHelpRefresh?.classList.remove('hidden');
        areaHelpPanel.classList.remove('hidden');
    };

    const filledForAreaHelp = (value) => value !== null && value !== undefined && String(value).trim() !== '';

    const renderNeedsCompass = (latitude, longitude, rankedOpportunities = []) => {
        if (!needsCompassPanel || !needsCompassSectors || !needsCompassContract) return;
        needsCompassRefresh?.classList.add('hidden');
        needsCompassSectors.classList.remove('hidden');
        const radius = Math.max(1, Number(needsCompassContract.nearby_radius_meters || 450));
        const maxSectors = Math.max(1, Number(needsCompassContract.max_sectors || 4));
        const maxPerSector = Math.max(1, Number(needsCompassContract.max_items_per_sector || 2));

        const grouped = new Map();
        rankedOpportunities
            .filter((item) => item?.distance_rankable === true && Number.isFinite(Number(item.latitude)) && Number.isFinite(Number(item.longitude)))
            .map((item) => {
                const distanceMeters = navigationDistanceMeters(latitude, longitude, Number(item.latitude), Number(item.longitude));
                if (!Number.isFinite(distanceMeters) || distanceMeters > radius) return null;
                const bearing = needsCompassBearingDegrees(latitude, longitude, Number(item.latitude), Number(item.longitude));
                const direction = needsCompassDirection(bearing, distanceMeters);
                return { ...item, compassDistanceMeters: distanceMeters, compassDirection: direction };
            })
            .filter(Boolean)
            .sort((a, b) => Number(b.device_priority || b.base_priority || 0) - Number(a.device_priority || a.base_priority || 0))
            .forEach((item) => {
                const key = item.compassDirection.key;
                if (!grouped.has(key)) grouped.set(key, { direction: item.compassDirection, items: [] });
                const bucket = grouped.get(key).items;
                if (bucket.length < maxPerSector) bucket.push(item);
            });

        const sectors = Array.from(grouped.values())
            .sort((a, b) => a.direction.sort - b.direction.sort)
            .slice(0, maxSectors);

        if (!sectors.length) {
            needsCompassSectors.innerHTML = '<p class="text-[11px] font-bold text-zinc-500 sm:col-span-2 xl:col-span-4">No bounded aggregate target falls inside the current scout radius. That does not mean nearby map needs are complete; non-spatial category needs remain in the regular queue.</p>';
            needsCompassPanel.classList.remove('hidden');
            return;
        }

        needsCompassSectors.innerHTML = sectors.map((sector) => `
            <article class="rounded-xl border border-sky-200 bg-white/90 p-3 dark:border-sky-900 dark:bg-zinc-950/70" data-needs-compass-sector="${escapeHtml(sector.direction.key)}">
                <div class="flex items-center gap-2">
                    <span class="flex h-7 w-7 items-center justify-center rounded-full bg-sky-100 text-sm font-black text-sky-800 dark:bg-sky-950 dark:text-sky-200" aria-hidden="true">${escapeHtml(sector.direction.emoji)}</span>
                    <strong class="text-xs text-zinc-900 dark:text-white">${escapeHtml(sector.direction.label)}</strong>
                </div>
                <div class="mt-2 space-y-2">
                    ${sector.items.map((item) => `
                        <div class="rounded-lg bg-sky-50/80 p-2 dark:bg-sky-950/30">
                            <div class="flex items-start gap-2"><span aria-hidden="true">${escapeHtml(item.emoji || '📍')}</span><div class="min-w-0"><strong class="text-[11px] text-zinc-900 dark:text-white">${escapeHtml(item.title || 'Useful map need')}</strong><p class="mt-0.5 text-[10px] font-bold text-sky-700 dark:text-sky-300">${escapeHtml(needsCompassDistanceBand(item.compassDistanceMeters))}</p></div></div>
                            <div class="mt-2 flex flex-wrap gap-1.5">
                                ${item.signal_key ? `<button type="button" data-walkthrough-focus-signal="${escapeHtml(String(item.signal_key))}" class="rounded-md border border-sky-200 px-2 py-1 text-[10px] font-black text-sky-800 dark:border-sky-800 dark:text-sky-200">See marker</button>` : ''}
                                <button type="button" data-walkthrough-quick-capture data-landmark-type="${escapeHtml(String(item.landmark_type || 'other'))}" data-crowd-signal="${escapeHtml(String(item.signal_key || ''))}" data-opportunity-title="${escapeHtml(String(item.title || ''))}" class="rounded-md bg-violet-700 px-2 py-1 text-[10px] font-black text-white">📸 Capture this</button>
                            </div>
                        </div>`).join('')}
                </div>
            </article>`).join('');
        needsCompassPanel.classList.remove('hidden');
    };

    const invalidateNeedsCompassAfterCapture = () => {
        if (!needsCompassPanel || !needsCompassSectors || !needsCompassContract) return;
        needsCompassSectors.classList.add('hidden');
        needsCompassRefresh?.classList.remove('hidden');
        needsCompassPanel.classList.remove('hidden');
    };

    const blankAreaDiscoveryCard = (latitude, longitude) => {
        if (!blankAreaDiscovery) {
            return '';
        }

        const threshold = Math.max(
            1,
            Number(blankAreaDiscovery.blank_area_radius_meters || 90)
        );
        const nearestDistance = nearestBlankAreaAnchorDistance(
            latitude,
            longitude
        );

        if (
            Number.isFinite(nearestDistance)
            && nearestDistance <= threshold
        ) {
            return '';
        }

        const nearestCopy = Number.isFinite(nearestDistance)
            ? `The nearest public map anchor is about ${Math.round(nearestDistance)} m away.`
            : 'No public map anchor is available in this scout payload yet.';

        return `
            <article class="rounded-xl border-2 border-fuchsia-300 bg-fuchsia-50 p-3 dark:border-fuchsia-800 dark:bg-fuchsia-950/30 md:col-span-3" data-walkthrough-blank-area>
                <div class="flex flex-wrap items-start justify-between gap-2">
                    <div>
                        <p class="text-[10px] font-black uppercase tracking-wide text-fuchsia-700 dark:text-fuchsia-300">Map looks thin here</p>
                        <p class="mt-1 text-sm font-black text-zinc-950 dark:text-white">Save what you notice privately first</p>
                    </div>
                    <span class="rounded-full bg-white px-2 py-1 text-[10px] font-black text-fuchsia-800 dark:bg-zinc-900 dark:text-fuchsia-200">${escapeHtml(`${threshold} m public-anchor check`)}</span>
                </div>
                <p class="mt-2 text-[11px] leading-5 text-zinc-600 dark:text-zinc-300">${escapeHtml(nearestCopy)} This does not mean the area is empty—only that no official landmark or privacy-threshold crowd signal is nearby in the public map data.</p>
                <div class="mt-3 flex flex-wrap gap-2">
                    <button type="button" data-walkthrough-remember-private class="rounded-lg bg-fuchsia-700 px-3 py-2 text-[11px] font-black text-white">📌 Remember this spot privately</button>
                    <span class="self-center text-[10px] font-bold text-zinc-500 dark:text-zinc-400">Your scout coordinate stays in memory only; saving still requires an explicit pin action.</span>
                </div>
            </article>
        `;
    };

    let walkthroughLastPosition = null;
    let walkthroughContinuationAvoidSignalKey = null;

    const renderWalkthroughTargets = (latitude, longitude, options = {}) => {
        if (! venueMapWalkthrough || ! walkthroughResults) {
            return;
        }

        walkthroughLastPosition = { latitude, longitude };
        renderSmartNextCategory();
        const queue = walkthroughOpportunityQueue || {};
        const avoidSignalKey = options?.avoidSignalKey || null;
        const maxTargets = Math.max(
            1,
            Number(queue?.ranking?.max_results || venueMapWalkthrough?.summary?.max_nearby_device_targets || 3)
        );
        const nearbyRadius = Math.max(
            1,
            Number(queue?.ranking?.nearby_radius_meters || venueMapWalkthrough?.summary?.nearby_radius_meters || 450)
        );
        const blankAreaThreshold = Math.max(
            1,
            Number(blankAreaDiscovery?.blank_area_radius_meters || 90)
        );
        const nearestAnchorDistance = nearestBlankAreaAnchorDistance(latitude, longitude);
        const blankAreaDetected = Boolean(blankAreaDiscovery)
            && (!Number.isFinite(nearestAnchorDistance) || nearestAnchorDistance > blankAreaThreshold);

        const staticOpportunities = Array.isArray(queue?.static_opportunities)
            ? queue.static_opportunities
            : [];

        const rankedOpportunities = staticOpportunities.map((opportunity) => {
            const distanceRankable = opportunity?.distance_rankable === true;
            const targetLatitude = Number(opportunity?.latitude);
            const targetLongitude = Number(opportunity?.longitude);
            const distanceMeters = distanceRankable
                ? navigationDistanceMeters(latitude, longitude, targetLatitude, targetLongitude)
                : null;
            const basePriority = Number(opportunity?.base_priority || 0);
            const proximityBoost = walkthroughProximityBoost(distanceMeters, nearbyRadius);
            const continuationPenalty = avoidSignalKey
                && String(opportunity?.signal_key || '') === String(avoidSignalKey)
                ? 35
                : 0;
            const postCaptureTypePenalty = quickCaptureSession?.recentCapturedLandmarkType
                && String(opportunity?.landmark_type || '') === String(quickCaptureSession.recentCapturedLandmarkType)
                ? Number(postCaptureRebalanceContract?.penalties?.same_landmark_type || 900)
                : 0;
            const postCaptureSignalPenalty = quickCaptureSession?.recentCapturedSignalKey
                && String(opportunity?.signal_key || '') === String(quickCaptureSession.recentCapturedSignalKey)
                ? Number(postCaptureRebalanceContract?.penalties?.same_aggregate_signal || 1400)
                : 0;

            return {
                ...opportunity,
                distance_meters: Number.isFinite(distanceMeters) ? distanceMeters : null,
                device_priority: basePriority + proximityBoost - continuationPenalty - postCaptureTypePenalty - postCaptureSignalPenalty,
                continuation_deemphasized: continuationPenalty > 0,
                post_capture_deemphasized: postCaptureTypePenalty > 0 || postCaptureSignalPenalty > 0,
            };
        });

        if (blankAreaDetected && queue?.device_candidate) {
            rankedOpportunities.push({
                ...queue.device_candidate,
                distance_meters: 0,
                device_priority: Number(queue.device_candidate.base_priority || 120),
                device_generated: true,
            });
        }

        renderNeedsCompass(latitude, longitude, rankedOpportunities);
        renderAreaHelp(latitude, longitude, rankedOpportunities);

        const selected = rankedOpportunities
            .sort((a, b) => {
                const priorityDifference = Number(b.device_priority || 0) - Number(a.device_priority || 0);
                if (priorityDifference !== 0) {
                    return priorityDifference;
                }

                const aDistance = Number.isFinite(a.distance_meters) ? a.distance_meters : Number.POSITIVE_INFINITY;
                const bDistance = Number.isFinite(b.distance_meters) ? b.distance_meters : Number.POSITIVE_INFINITY;
                return aDistance - bDistance;
            })
            .slice(0, maxTargets);

        const cards = selected.map((opportunity, index) => {
            const kind = String(opportunity.kind || 'mapping_need');
            const distance = Number.isFinite(opportunity.distance_meters) && kind === 'crowd_evidence'
                ? walkthroughDistanceLabel(opportunity.distance_meters)
                : '';
            const rankLabel = index === 0 ? 'Best next step' : `Priority ${index + 1}`;
            const evidence = Number.isFinite(Number(opportunity.evidence_score))
                ? `<span class="rounded-full bg-violet-50 px-2 py-1 text-[10px] font-black text-violet-800 dark:bg-violet-950/40 dark:text-violet-200">${escapeHtml(String(opportunity.evidence_score))}/100 evidence</span>`
                : '';
            const distanceBadge = distance
                ? `<span class="rounded-full bg-white px-2 py-1 text-[10px] font-black text-cyan-800 ring-1 ring-cyan-100 dark:bg-zinc-950 dark:text-cyan-200 dark:ring-cyan-900">${escapeHtml(distance)}</span>`
                : '';
            const focusButton = opportunity.signal_key
                ? `<button type="button" data-walkthrough-focus-signal="${escapeHtml(String(opportunity.signal_key))}" class="rounded-lg border border-cyan-200 px-2.5 py-1.5 text-[11px] font-black text-cyan-800 dark:border-cyan-800 dark:text-cyan-200">See marker</button>`
                : '';
            const captureButton = kind !== 'blank_area_private_first'
                ? `<button type="button" data-walkthrough-quick-capture data-landmark-type="${escapeHtml(String(opportunity.landmark_type || 'other'))}" data-crowd-signal="${escapeHtml(String(opportunity.signal_key || ''))}" data-opportunity-title="${escapeHtml(String(opportunity.title || ''))}" class="rounded-lg bg-violet-700 px-2.5 py-1.5 text-[11px] font-black text-white">📸 Capture this</button>`
                : '';
            const action = kind === 'blank_area_private_first'
                ? '<button type="button" data-walkthrough-remember-private class="rounded-lg bg-fuchsia-700 px-2.5 py-1.5 text-[11px] font-black text-white">📌 Remember privately</button>'
                : `${captureButton}${opportunity?.action?.url
                    ? `<a href="${escapeHtml(opportunity.action.url)}" class="rounded-lg border border-cyan-200 px-2.5 py-1.5 text-[11px] font-black text-cyan-800 dark:border-cyan-800 dark:text-cyan-200">${escapeHtml(opportunity.action.label || 'Detailed contribution')}</a>`
                    : ''}`;
            const kindCopy = kind === 'blank_area_private_first'
                ? 'Private-first discovery'
                : (kind === 'crowd_evidence'
                    ? 'Strengthen crowd evidence'
                    : (kind === 'pending_category_review' ? 'Review before duplicating' : 'Useful category gap'));

            return `
                <article class="rounded-2xl border ${index === 0 ? 'border-cyan-300 bg-cyan-50/80 dark:border-cyan-800 dark:bg-cyan-950/25' : 'border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-900'} p-3" data-walkthrough-opportunity="${escapeHtml(String(opportunity.key || ''))}" data-walkthrough-opportunity-kind="${escapeHtml(kind)}">
                    <div class="flex flex-wrap items-start justify-between gap-2">
                        <div>
                            <p class="text-[10px] font-black uppercase tracking-wide text-cyan-700 dark:text-cyan-300">${escapeHtml(rankLabel)} · ${escapeHtml(kindCopy)}</p>
                            <p class="mt-1 text-sm font-black text-zinc-950 dark:text-white">${escapeHtml(`${opportunity.emoji || '📍'} ${opportunity.title || 'Help improve this map'}`)}</p>
                        </div>
                        <div class="flex flex-wrap gap-1">${distanceBadge}${evidence}</div>
                    </div>
                    <p class="mt-2 text-[11px] leading-5 text-zinc-600 dark:text-zinc-300">${escapeHtml(opportunity.guidance || 'Contribute only what you personally observe at the venue.')}</p>
                    <div class="mt-3 flex flex-wrap gap-2">${focusButton}${action}</div>
                </article>
            `;
        }).join('');

        walkthroughResults.innerHTML = `
            <div class="rounded-2xl border border-cyan-200 bg-white/80 p-3 dark:border-cyan-900/70 dark:bg-zinc-950/70" data-walkthrough-opportunity-queue>
                <div class="flex flex-wrap items-start justify-between gap-2">
                    <div>
                        <p class="text-[10px] font-black uppercase tracking-[0.14em] text-cyan-700 dark:text-cyan-300">Prioritized map-improvement queue</p>
                        <p class="mt-1 text-sm font-black text-zinc-950 dark:text-white">${options?.continuation ? 'Fresh options for your next capture' : 'Best ways to help from where you are right now'}</p>
                    </div>
                    <span class="rounded-full bg-emerald-50 px-2.5 py-1 text-[10px] font-black text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200">Ranked on this device</span>
                </div>
                <div class="mt-3 grid gap-3 md:grid-cols-2 xl:grid-cols-3">${cards || '<div class="rounded-xl border border-dashed border-cyan-200 p-4 text-xs font-bold text-zinc-600 dark:border-cyan-900 dark:text-zinc-300">No mapping opportunity is available in the current bounded queue.</div>'}</div>
                <p class="mt-3 text-[10px] leading-4 text-zinc-500 dark:text-zinc-400">Distance is calculated from this one-time GPS fix on your device. Thin-map detection is not proof that a place is empty, and queue priority never approves a map item or changes recognition weight.</p>
            </div>
        `;
        walkthroughResults.classList.remove('hidden');

        if (walkthroughStatus) {
            walkthroughStatus.textContent = selected.length > 0
                ? `${options?.continuation ? 'Fresh location check complete. ' : ''}Showing ${selected.length} prioritized mapping opportunit${selected.length === 1 ? 'y' : 'ies'}. Your GPS fix stayed on this device.`
                : 'No mapping opportunity is currently available. Your GPS fix stayed on this device.';
        }
    };

    const quickCaptureOpen = mapElement.querySelector('[data-venue-map-quick-capture-open]');
    const quickCapturePanel = mapElement.querySelector('[data-venue-map-quick-capture-panel]');
    const quickCaptureClose = mapElement.querySelector('[data-venue-map-quick-capture-close]');
    const quickCaptureForm = mapElement.querySelector('[data-venue-map-quick-capture-form]');
    const quickCaptureStatus = mapElement.querySelector('[data-venue-map-quick-capture-status]');
    const quickCaptureCommunityAck = mapElement.querySelector('[data-venue-map-quick-capture-community-ack]');
    const quickCapturePhotoWrap = mapElement.querySelector('[data-venue-map-quick-capture-photo-wrap]');
    const quickCaptureResult = mapElement.querySelector('[data-venue-map-quick-capture-result]');
    const quickCaptureSessionCounts = mapElement.querySelector('[data-venue-map-quick-capture-session-counts]');
    const quickCaptureSignalInput = mapElement.querySelector('[data-venue-map-quick-capture-signal]');
    let quickCapturePosition = null;
    const sessionCoverageContract = venueMapWalkthrough?.session_coverage || quickCapture?.session_coverage || null;
    const sessionCoverageHelped = mapElement.querySelector('[data-venue-map-session-coverage-helped]');
    const sessionCoverageNeeds = mapElement.querySelector('[data-venue-map-session-coverage-needs]');
    const nextCategoryGuideResult = mapElement.querySelector('[data-venue-map-next-category-result]');
    const quickCaptureLandmarkTypes = quickCapture?.landmark_types || {};
    const quickCaptureSession = {
        captures: 0,
        privateMemories: 0,
        communitySubmissions: 0,
        strengthenedAreas: 0,
        photoEvidence: 0,
        helpedLandmarkTypes: new Set(),
        communityLandmarkTypes: new Set(),
        strengthenedSignalKeys: new Set(),
        recentCapturedLandmarkType: null,
        recentCapturedSignalKey: null,
        rebalanceRevision: 0,
    };

    const landmarkTypeLabel = (key) => {
        const value = quickCaptureLandmarkTypes?.[key];
        if (typeof value === 'string') return value;
        if (value && typeof value === 'object') return value.label || value.name || key;
        return String(key || 'Venue place').replaceAll('_', ' ');
    };

    const smartNextCategoryCandidate = () => {
        const guide = sessionCoverageContract?.smart_next_category || null;
        const seed = Array.isArray(guide?.candidate_seed) ? guide.candidate_seed : [];
        if (!seed.length) return null;

        return seed
            .map((candidate) => {
                const type = String(candidate.landmark_type || '');
                const alreadyHelped = type && quickCaptureSession.helpedLandmarkTypes.has(type);
                const justCapturedType = type && quickCaptureSession.recentCapturedLandmarkType === type;
                const justCapturedSignal = candidate.signal_key && quickCaptureSession.recentCapturedSignalKey === String(candidate.signal_key);
                const diversityPenalty = alreadyHelped ? Number(guide?.ranking?.already_helped_category_penalty || 1000) : 0;
                const postCaptureTypePenalty = justCapturedType ? Number(postCaptureRebalanceContract?.penalties?.same_landmark_type || 900) : 0;
                const postCaptureSignalPenalty = justCapturedSignal ? Number(postCaptureRebalanceContract?.penalties?.same_aggregate_signal || 1400) : 0;
                return {
                    ...candidate,
                    alreadyHelped,
                    clientDiversityScore: Number(candidate.diversity_priority || 0) - diversityPenalty - postCaptureTypePenalty - postCaptureSignalPenalty,
                };
            })
            .sort((a, b) => (b.clientDiversityScore - a.clientDiversityScore) || String(a.key || '').localeCompare(String(b.key || '')))[0] || null;
    };

    const renderSmartNextCategory = () => {
        if (!nextCategoryGuideResult || !sessionCoverageContract?.smart_next_category) return;
        const candidate = smartNextCategoryCandidate();
        if (!candidate) {
            nextCategoryGuideResult.innerHTML = '<p class="text-[11px] font-bold text-zinc-500">No different category is available in the currently loaded bounded queue. That does not mean the venue is complete.</p>';
            return;
        }

        const different = !candidate.alreadyHelped;
        const captureButton = walkthroughLastPosition
            ? `<button type="button" class="mt-2 rounded-lg bg-amber-700 px-3 py-2 text-[10px] font-black text-white" data-walkthrough-quick-capture data-landmark-type="${escapeHtml(candidate.landmark_type || 'other')}" data-crowd-signal="${escapeHtml(candidate.signal_key || '')}" data-opportunity-title="${escapeHtml(candidate.title || 'Useful map need')}">📸 Capture this</button>`
            : '<p class="mt-2 text-[10px] font-bold text-zinc-500">Use Walk & Map for a fresh one-time location check before capturing this suggestion.</p>';

        nextCategoryGuideResult.innerHTML = `
            <div class="flex items-start gap-2">
                <span class="text-lg" aria-hidden="true">${escapeHtml(candidate.emoji || '📍')}</span>
                <div class="min-w-0 flex-1">
                    <div class="flex flex-wrap items-center gap-2">
                        <strong class="text-xs text-zinc-900 dark:text-white">${escapeHtml(candidate.title || 'Useful map need')}</strong>
                        <span class="rounded-full bg-white px-2 py-0.5 text-[9px] font-black text-amber-800 ring-1 ring-amber-200 dark:bg-zinc-900 dark:text-amber-200 dark:ring-amber-900">${different ? 'Different category' : 'Best loaded need'}</span>
                    </div>
                    <p class="mt-1 text-[11px] leading-5 text-zinc-600 dark:text-zinc-300">${escapeHtml(candidate.why_useful || 'This remains a useful map need.')}</p>
                    ${captureButton}
                </div>
            </div>`;
    };

    const renderSessionCoverage = () => {
        if (!sessionCoverageContract) return;
        if (sessionCoverageHelped) {
            const helped = Array.from(quickCaptureSession.helpedLandmarkTypes);
            sessionCoverageHelped.innerHTML = helped.length
                ? helped.map((key) => {
                    const shared = quickCaptureSession.communityLandmarkTypes.has(key);
                    return `<span class="rounded-full bg-violet-100 px-2 py-1 text-[10px] font-black text-violet-900 dark:bg-violet-950 dark:text-violet-100">${shared ? '🌎' : '📌'} ${escapeHtml(landmarkTypeLabel(key))}</span>`;
                }).join('')
                : '<span class="text-[11px] font-bold text-zinc-500">Your fresh captures will appear here.</span>';
        }

        if (sessionCoverageNeeds) {
            const seed = Array.isArray(sessionCoverageContract.current_need_seed) ? sessionCoverageContract.current_need_seed : [];
            const remaining = seed
                .filter((need) => !need.landmark_type || !quickCaptureSession.helpedLandmarkTypes.has(String(need.landmark_type)))
                .slice(0, Number(sessionCoverageContract.max_visible_needs || 4));
            sessionCoverageNeeds.innerHTML = remaining.length
                ? remaining.map((need) => `<div class="flex items-start gap-2 text-[11px]"><span aria-hidden="true">${escapeHtml(need.emoji || '📍')}</span><div><strong class="text-zinc-800 dark:text-zinc-100">${escapeHtml(need.title || 'Useful map need')}</strong><span class="ml-1 text-zinc-500">${escapeHtml(need.short_label || '')}</span></div></div>`).join('')
                : '<p class="text-[11px] font-bold text-zinc-500">You have touched the currently loaded category needs. That does not mean the venue map is complete.</p>';
        }
        renderSmartNextCategory();
    };

    const renderQuickCaptureSession = () => {
        if (!quickCaptureSessionCounts) return;
        const pills = [
            `${quickCaptureSession.captures} capture${quickCaptureSession.captures === 1 ? '' : 's'}`,
            quickCaptureSession.privateMemories > 0 ? `${quickCaptureSession.privateMemories} private` : null,
            quickCaptureSession.communitySubmissions > 0 ? `${quickCaptureSession.communitySubmissions} shared` : null,
            quickCaptureSession.strengthenedAreas > 0 ? `${quickCaptureSession.strengthenedAreas} area${quickCaptureSession.strengthenedAreas === 1 ? '' : 's'} strengthened` : null,
            quickCaptureSession.photoEvidence > 0 ? `${quickCaptureSession.photoEvidence} photo${quickCaptureSession.photoEvidence === 1 ? '' : 's'} added` : null,
        ].filter(Boolean);
        quickCaptureSessionCounts.innerHTML = pills.map((label) => `<span class="rounded-full bg-white px-2.5 py-1 text-[10px] font-black text-zinc-700 ring-1 ring-zinc-200 dark:bg-zinc-900 dark:text-zinc-200 dark:ring-zinc-800">${escapeHtml(label)}</span>`).join('');
    };

    const applyQuickCaptureSessionEvent = (event = {}) => {
        if (!event?.client_memory_only) return;
        quickCaptureSession.captures += Number(event.capture_increment || 0);
        quickCaptureSession.privateMemories += Number(event.private_memory_increment || 0);
        quickCaptureSession.communitySubmissions += Number(event.community_submission_increment || 0);
        quickCaptureSession.strengthenedAreas += Number(event.strengthened_area_increment || 0);
        quickCaptureSession.photoEvidence += Number(event.photo_evidence_increment || 0);
        if (event.landmark_type) {
            const landmarkType = String(event.landmark_type);
            quickCaptureSession.helpedLandmarkTypes.add(landmarkType);
            if (event.community_capture) quickCaptureSession.communityLandmarkTypes.add(landmarkType);
        }
        if (event.strengthened_signal_key) quickCaptureSession.strengthenedSignalKeys.add(String(event.strengthened_signal_key));
        if (event.post_capture_rebalance) {
            quickCaptureSession.recentCapturedLandmarkType = event.deemphasize_landmark_type ? String(event.deemphasize_landmark_type) : null;
            quickCaptureSession.recentCapturedSignalKey = event.deemphasize_signal_key ? String(event.deemphasize_signal_key) : null;
            quickCaptureSession.rebalanceRevision += 1;
            if (event.clear_directional_compass_until_fresh_gps) {
                invalidateNeedsCompassAfterCapture();
                invalidateAreaHelpAfterCapture();
            }
        }
        renderQuickCaptureSession();
        renderSessionCoverage();
    };

    renderQuickCaptureSession();

    const prepareQuickCaptureFromOpportunity = (button) => {
        if (!quickCaptureForm || !walkthroughLastPosition) return;
        quickCapturePosition = { ...walkthroughLastPosition };
        quickCapturePanel?.classList.remove('hidden');
        quickCaptureResult?.classList.add('hidden');
        const type = String(button?.dataset?.landmarkType || 'other');
        const signalKey = String(button?.dataset?.crowdSignal || '');
        const title = String(button?.dataset?.opportunityTitle || '');
        const typeInput = quickCaptureForm.querySelector('[name="landmark_type"]');
        if (typeInput && Array.from(typeInput.options || []).some((option) => option.value === type)) {
            typeInput.value = type;
        }
        if (quickCaptureSignalInput) quickCaptureSignalInput.value = signalKey;
        const labelInput = quickCaptureForm.querySelector('[name="label"]');
        if (labelInput && !labelInput.value && title) labelInput.placeholder = `Name what you see · ${title}`;
        if (quickCaptureStatus) quickCaptureStatus.textContent = 'Fresh one-time scout position ready for this capture. It is sent only if you press Save capture.';
        updateQuickCaptureVisibility();
        quickCapturePanel?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    };

    const updateQuickCaptureVisibility = () => {
        const community = quickCaptureForm?.querySelector('input[name="visibility"]:checked')?.value === 'community';
        quickCaptureCommunityAck?.classList.toggle('hidden', !community);
        quickCapturePhotoWrap?.classList.toggle('hidden', !community);
        const description = quickCaptureForm?.querySelector('[name="description"]');
        if (description) description.required = community;
        const ack = quickCaptureForm?.querySelector('[name="acknowledge_community_location"]');
        if (ack) {
            ack.required = community;
            if (!community) ack.checked = false;
        }
        const photo = quickCaptureForm?.querySelector('[name="photo"]');
        if (photo && !community) photo.value = '';
    };

    quickCaptureForm?.addEventListener('change', (event) => {
        if (event.target?.name === 'visibility') updateQuickCaptureVisibility();
    });

    quickCaptureOpen?.addEventListener('click', () => {
        quickCapturePanel?.classList.remove('hidden');
        quickCaptureResult?.classList.add('hidden');
        quickCapturePosition = null;
        if (quickCaptureSignalInput) quickCaptureSignalInput.value = '';
        if (!navigator.geolocation) {
            if (quickCaptureStatus) quickCaptureStatus.textContent = 'This browser cannot provide a one-time GPS position.';
            return;
        }
        if (quickCaptureStatus) quickCaptureStatus.textContent = 'Capturing one fresh GPS fix on this device…';
        navigator.geolocation.getCurrentPosition(
            (position) => {
                quickCapturePosition = {
                    latitude: Number(position.coords.latitude),
                    longitude: Number(position.coords.longitude),
                };
                if (quickCaptureStatus) quickCaptureStatus.textContent = 'Fresh GPS fix ready. It is sent only if you press Save capture.';
            },
            () => {
                if (quickCaptureStatus) quickCaptureStatus.textContent = 'GPS was not available. Nothing was submitted or recorded.';
            },
            { enableHighAccuracy: true, maximumAge: 0, timeout: 12000 }
        );
        updateQuickCaptureVisibility();
    });

    quickCaptureClose?.addEventListener('click', () => {
        quickCapturePanel?.classList.add('hidden');
        quickCapturePosition = null;
    });

    renderSessionCoverage();

    quickCaptureForm?.addEventListener('submit', async (event) => {
        event.preventDefault();
        if (!quickCapturePosition || !quickCapture?.web_store_url) {
            if (quickCaptureStatus) quickCaptureStatus.textContent = 'Capture a fresh GPS fix before saving.';
            return;
        }
        const data = new FormData(quickCaptureForm);
        data.set('latitude', String(quickCapturePosition.latitude));
        data.set('longitude', String(quickCapturePosition.longitude));
        if (quickCaptureStatus) quickCaptureStatus.textContent = 'Saving this explicit capture…';
        try {
            const response = await fetch(quickCapture.web_store_url, {
                method: 'POST',
                headers: { 'Accept': 'application/json', 'X-CSRF-TOKEN': csrfToken() },
                body: data,
            });
            const payload = await response.json();
            if (!response.ok) {
                const errors = payload?.errors ? Object.values(payload.errors).flat().join(' ') : null;
                throw new Error(errors || payload?.message || 'The capture could not be saved.');
            }
            const result = payload?.data || {};
            if (result.visibility === 'private' && result.personal_pin) {
                personalPins = [result.personal_pin, ...personalPins.filter((pin) => String(pin.id) !== String(result.personal_pin.id))];
                renderPersonalPins(personalPins);
                renderPersonalPinManager();
            }
            const followThrough = result?.follow_through || null;
            applyQuickCaptureSessionEvent(followThrough?.session_event || {});
            if (quickCaptureResult) {
                const headline = followThrough?.headline || (result.visibility === 'private' ? 'Saved privately.' : 'Submitted as community evidence.');
                const message = followThrough?.message || result.next_step || '';
                const impact = followThrough?.impact || {};
                const signal = followThrough?.community_signal || null;
                const nextAction = followThrough?.next_action || null;
                const impactBits = [
                    impact.crowd_signal_became_visible ? 'Crowd signal became visible' : null,
                    impact.independent_contributor_added ? 'Independent evidence added' : null,
                    impact.photo_evidence_added ? 'Fresh photo added' : null,
                    Number(impact.evidence_score_change || 0) > 0 ? `Evidence +${Number(impact.evidence_score_change)} points` : null,
                ].filter(Boolean);
                const signalHtml = signal
                    ? `<div class="mt-2 rounded-lg bg-white/70 p-2 dark:bg-zinc-950/40"><strong>${escapeHtml(signal.evidence_quality?.label || 'Crowd evidence')}</strong> · ${escapeHtml(String(signal.contributor_count || 0))} independent contributor${Number(signal.contributor_count || 0) === 1 ? '' : 's'}${signal.photo_count ? ` · ${escapeHtml(String(signal.photo_count))} photo${Number(signal.photo_count) === 1 ? '' : 's'}` : ''}</div>`
                    : '';
                const impactHtml = impactBits.length
                    ? `<div class="mt-2 flex flex-wrap gap-1.5">${impactBits.map((bit) => `<span class="rounded-full bg-emerald-100 px-2 py-1 text-[10px] font-black text-emerald-900 dark:bg-emerald-950 dark:text-emerald-100">${escapeHtml(bit)}</span>`).join('')}</div>`
                    : '';
                const actionHtml = nextAction?.web_url
                    ? `<a href="${escapeHtml(nextAction.web_url)}" class="mt-2 inline-flex rounded-lg bg-emerald-700 px-3 py-2 text-[11px] font-black text-white hover:bg-emerald-600">${escapeHtml(nextAction.label || 'See what happens next')}</a>`
                    : nextAction?.guidance
                        ? `<p class="mt-2 text-[11px] font-bold">${escapeHtml(nextAction.guidance)}</p>`
                        : '';
                const recognition = followThrough?.recognition?.message
                    ? `<p class="mt-2 text-[10px] text-emerald-800/80 dark:text-emerald-200/80">${escapeHtml(followThrough.recognition.message)}</p>`
                    : '';
                const continuation = followThrough?.continuation || null;
                const continuationHtml = continuation?.available
                    ? `<div class="mt-3 rounded-lg border border-cyan-200 bg-cyan-50/80 p-2 dark:border-cyan-900 dark:bg-cyan-950/30"><strong>${escapeHtml(continuation.headline || 'Keep mapping while you walk')}</strong><p class="mt-1 text-[11px]">${escapeHtml(continuation.guidance || '')}</p><button type="button" data-quick-capture-continue data-avoid-signal="${escapeHtml(String(continuation.avoid_signal_key || ''))}" class="mt-2 rounded-lg bg-cyan-700 px-3 py-2 text-[11px] font-black text-white">🧭 ${escapeHtml(continuation.label || 'Capture another nearby need')}</button><p class="mt-1 text-[10px] text-cyan-800/80 dark:text-cyan-200/80">One new GPS fix only · no background tracking · you still choose what to capture.</p></div>`
                    : '';
                quickCaptureResult.innerHTML = `<strong>${escapeHtml(headline)}</strong> ${escapeHtml(message)}${signalHtml}${impactHtml}${actionHtml}${recognition}${continuationHtml}`;
                quickCaptureResult.classList.remove('hidden');
            }
            if (quickCaptureStatus) quickCaptureStatus.textContent = result.visibility === 'private'
                ? 'Saved as an owner-only private pin.'
                : 'Submitted as a pending community suggestion. Organizer review is still required.';
            quickCapturePosition = null;
            walkthroughLastPosition = null;
            quickCaptureForm.reset();
            if (quickCaptureSignalInput) quickCaptureSignalInput.value = '';
            updateQuickCaptureVisibility();
        } catch (error) {
            if (quickCaptureStatus) quickCaptureStatus.textContent = error?.message || 'The capture could not be saved.';
        }
    });

    walkthroughLocateButton?.addEventListener('click', () => {
        if (! navigator.geolocation) {
            if (walkthroughStatus) {
                walkthroughStatus.textContent = 'This browser cannot provide a one-time current position.';
            }
            return;
        }

        walkthroughLocateButton.disabled = true;
        if (walkthroughStatus) {
            walkthroughStatus.textContent = 'Checking your current spot once on this device…';
        }

        navigator.geolocation.getCurrentPosition(
            (position) => {
                walkthroughLocateButton.disabled = false;
                renderWalkthroughTargets(
                    Number(position.coords.latitude),
                    Number(position.coords.longitude)
                );
            },
            () => {
                walkthroughLocateButton.disabled = false;
                if (walkthroughStatus) {
                    walkthroughStatus.textContent = 'FestFriends could not access your current spot. Nothing was recorded; you can still use the category scouts.';
                }
            },
            {
                enableHighAccuracy: true,
                maximumAge: 0,
                timeout: 10000,
            }
        );
    });

    mapElement.addEventListener('click', (event) => {
        const continuationButton = event.target.closest('[data-quick-capture-continue]');
        if (continuationButton) {
            event.preventDefault();
            if (!navigator.geolocation) {
                if (quickCaptureStatus) quickCaptureStatus.textContent = 'This browser cannot provide a fresh one-time position.';
                return;
            }
            continuationButton.disabled = true;
            const avoidSignalKey = String(continuationButton.dataset.avoidSignal || '');
            if (quickCaptureStatus) quickCaptureStatus.textContent = 'Requesting one brand-new GPS fix for your next mapping choice…';
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    continuationButton.disabled = false;
                    quickCapturePosition = {
                        latitude: Number(position.coords.latitude),
                        longitude: Number(position.coords.longitude),
                    };
                    walkthroughContinuationAvoidSignalKey = avoidSignalKey || null;
                    renderWalkthroughTargets(quickCapturePosition.latitude, quickCapturePosition.longitude, {
                        continuation: true,
                        avoidSignalKey: walkthroughContinuationAvoidSignalKey,
                    });
                    if (quickCaptureStatus) quickCaptureStatus.textContent = 'Fresh location check complete. Choose a mapping opportunity below; nothing has been submitted.';
                    walkthroughResults?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                },
                () => {
                    continuationButton.disabled = false;
                    if (quickCaptureStatus) quickCaptureStatus.textContent = 'GPS was not available. Nothing was recorded or submitted.';
                },
                { enableHighAccuracy: true, maximumAge: 0, timeout: 12000 }
            );
            return;
        }

        const opportunityCapture = event.target.closest('[data-walkthrough-quick-capture]');
        if (opportunityCapture) {
            event.preventDefault();
            prepareQuickCaptureFromOpportunity(opportunityCapture);
            return;
        }

        const rememberPrivate = event.target.closest(
            '[data-walkthrough-remember-private]'
        );

        if (rememberPrivate) {
            event.preventDefault();
            personalPinComposer?.classList.remove('hidden');
            if (personalPinTypeInput) {
                personalPinTypeInput.value = 'custom';
                personalPinTypeInput.dispatchEvent(new Event('change'));
            }
            if (personalPinLabelInput) {
                personalPinLabelInput.value = 'Map discovery';
            }
            if (personalPinNoteInput && !personalPinNoteInput.value) {
                personalPinNoteInput.value = 'Private-first walk-through discovery';
            }
            if (personalPinPlacementStatus) {
                personalPinPlacementStatus.textContent = 'Private-first discovery ready. Use Pin My Current Location for a fresh one-time GPS fix, or tap the map. Nothing has been published.';
                personalPinPlacementStatus.classList.remove('hidden');
            }
            personalPinComposer?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            return;
        }

        const focus = event.target.closest('[data-walkthrough-focus-signal]');
        if (! focus) {
            return;
        }

        const marker = crowdSignalMarkers.get(
            String(focus.dataset.walkthroughFocusSignal || '')
        );
        if (! marker || ! mapIsUsable()) {
            return;
        }

        setMapView('geographic');
        map.setView(marker.getLatLng(), Math.max(map.getZoom(), 17), { animate: true });
        marker.openPopup();
    });

    renderLandmarks(landmarks);
    if (crowdSignals.length > 0) {
        renderCrowdSignals(crowdSignals);
    } else {
        renderCommunitySuggestions(communitySuggestions);
    }
    renderPersonalPins(personalPins);
    syncPersonalPinCapacity();
    renderMeetups(meetups);

    if (requestedCrowdSignalKey) {
        const requestedCrowdSignalMarker = crowdSignalMarkers.get(
            String(requestedCrowdSignalKey)
        );

        if (requestedCrowdSignalMarker) {
            setMapView('geographic');
            window.setTimeout(() => {
                if (! mapIsUsable()) {
                    return;
                }

                map.setView(requestedCrowdSignalMarker.getLatLng(), Math.max(map.getZoom(), 17));
                requestedCrowdSignalMarker.openPopup();
            }, 80);
        }
    }

    if (requestedLandmarkId) {
        const requestedLandmarkMarker = landmarkMarkers.get(
            String(requestedLandmarkId)
        );

        if (requestedLandmarkMarker) {
            setMapView('geographic');
            window.setTimeout(() => {
                if (! mapIsUsable()) {
                    return;
                }

                map.invalidateSize();
                map.setView(
                    requestedLandmarkMarker.getLatLng(),
                    Math.max(map.getZoom(), 17),
                    { animate: true }
                );
                requestedLandmarkMarker.openPopup();
                highlightMarker(requestedLandmarkMarker);

                if (requestedLandmarkNavigation) {
                    window.setTimeout(() => {
                        if (! mapIsUsable()) {
                            return;
                        }

                        const navigateButton = mapElement.querySelector(
                            `[data-landmark-navigate][data-landmark-id="${requestedLandmarkId}"]`
                        );

                        navigateButton?.click();
                    }, 150);
                }
            }, 100);
        }
    }

    loadLocations({
        fitViewport: true,
    });

    const pollingInterval = window.setInterval(
        () => {
            if (document.hidden) {
                return;
            }

            loadLocations();
        },
        30000
    );

    document.addEventListener(
        'visibilitychange',
        () => {
            if (document.hidden) {
                stopMemberMotions();
                return;
            }

            loadLocations();

            window.requestAnimationFrame(() => {
                if (! mapIsUsable()) {
                    return;
                }

                map.invalidateSize({
                    animate: false,
                });
            });
        },
        {
            signal: eventController.signal,
        }
    );

    motionPreference.addEventListener?.('change', () => {
        if (motionPreference.matches) stopMemberMotions();
    }, { signal: eventController.signal });

    cleanupFestivalLiveMap = () => {
        if (generation === festivalLiveMapGeneration) {
            festivalLiveMapGeneration++;
        }

        eventController.abort();

        locationsLoadController?.abort();
        locationsLoadController = null;

        if (geolocationWatchId !== null) {
            navigator.geolocation.clearWatch(
                geolocationWatchId
            );
            geolocationWatchId = null;
        }

        window.clearInterval(pollingInterval);

        stopMemberMotions(false);
        memberFixes.clear();
        memberMotionEligibility.clear();
        clearIllustratedMemberPins();
        memberMarkers.clear();
        memberPresence.clear();
        navigableMemberIds.clear();
        publishMemberMarkerState();

        if (highlightedCircle && map.hasLayer(highlightedCircle)) {
            map.removeLayer(highlightedCircle);
            highlightedCircle = null;
        }

        clearNavigation();

        if (map.getContainer()?._leaflet_id) {
            map.remove();
        }
        cleanupFestivalLiveMap = null;
    };

    addWindowListener(
        'pagehide',
        (event) => {
            pageWasPersistedInHistory =
                event.persisted === true;

            if (event.persisted === true) {
                sharingWasActiveBeforeHistoryFreeze =
                    geolocationWatchId !== null;
            }
        }
    );

    addWindowListener(
        'pageshow',
        (event) => {
            if (
                event.persisted !== true
                && ! pageWasPersistedInHistory
            ) {
                return;
            }

            pageWasPersistedInHistory = false;

            if (
                sharingWasActiveBeforeHistoryFreeze
                && geolocationWatchId === null
                && viewerCheckedIn
            ) {
                startLocationSharing();
            }

            sharingWasActiveBeforeHistoryFreeze =
                false;

            loadLocations();

            window.requestAnimationFrame(() => {
                if (! mapIsUsable()) {
                    return;
                }

                map.invalidateSize({
                    animate: false,
                });
            });
        }
    );

    addWindowListener(
        'beforeunload',
        () => cleanupFestivalLiveMap?.(),
        { once: true }
    );

    window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
            if (! mapIsUsable()) {
                return;
            }

            map.invalidateSize({ animate: false });
        });
    });
};

const formatLabel = (value) => {
    return String(value)
        .replaceAll('_', ' ')
        .replace(
            /\b\w/g,
            (character) =>
                character.toUpperCase()
        );
};

const escapeHtml = (value) => {
    const element =
        document.createElement('div');

    element.textContent = String(value);

    return element.innerHTML;
};

const escapeAttribute = (value) => {
    return escapeHtml(value)
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#039;');
};

if (document.readyState === 'loading') {
    document.addEventListener(
        'DOMContentLoaded',
        initializeFestivalLiveMap,
        {
            once: true,
        }
    );
} else {
    initializeFestivalLiveMap();
}

document.addEventListener(
    'livewire:navigating',
    () => cleanupFestivalLiveMap?.()
);

document.addEventListener(
    'livewire:navigated',
    initializeFestivalLiveMap
);
